import React, { useState, useEffect, useRef } from 'react';
import { Download, FileText, Sparkles, Copy, Check, History, LogOut, Eye, Target, CheckCircle2, Edit2, Save, X, Upload, MessageSquare, Plus, Trash2, ChevronDown, ChevronUp, ChevronLeft, ChevronRight, Loader2, Settings, Briefcase, ArrowRight, CheckCircle, TrendingUp, ArrowLeftRight, Calendar, Building2, Users, Star, Globe, Shield, Zap } from 'lucide-react';
import Logo from './components/Logo';
import { Document, Packer, Paragraph, TextRun, AlignmentType, HeadingLevel, LevelFormat } from 'docx';
import { usePostHog } from '@posthog/react';
import { captureError, captureApiError, identifyUser, trackEvent } from './utils/errorTracking';
import Auth from './Auth';
import DashboardView from './components/DashboardView';
import Navigation from './components/Navigation';
import LibraryView from './components/LibraryView';
import { PrivacyPolicy } from './components';
import PaywallModal from './components/PaywallModal';
import CookieConsent from './components/CookieConsent';
import OfferComparisonView from './components/OfferComparisonView';
import JobSearch from './components/JobSearch';
import LinkedInProfileView from './components/LinkedInProfileView';

// ============================================================================
// API CONFIGURATION
// ============================================================================
// The API key is now stored securely in the backend .env file
// Using relative path so it works regardless of port/domain

const API_URL = '';

// ============================================================================
// WIZARD PROGRESS BAR COMPONENT
// ============================================================================

function WizardProgressBar({ currentStep, progress }) {
  const steps = [
    { number: 1, label: 'Add Job', shortLabel: 'Job', icon: FileText, encouragement: "Let's see what they're looking for" },
    { number: 2, label: 'Fine-tune', shortLabel: 'Tune', icon: Sparkles, encouragement: 'Adding your secret sauce' },
    { number: 3, label: 'Ready!', shortLabel: 'Go!', icon: Download, encouragement: 'Time to apply' }
  ];

  const getStepStatus = (step) => {
    if (step.number < currentStep) return 'completed';
    if (step.number === currentStep) return 'current';
    return 'upcoming';
  };

  const currentStepData = steps.find(s => s.number === currentStep);

  return (
    <div className="mb-6 sticky top-16 sm:top-20 z-[30]">
      {/* Encouragement message */}
      <div className="text-center mb-3">
        <p className="text-sm text-slate-500 italic">{currentStepData?.encouragement}</p>
      </div>

      {/* Progress bar - simplified, modern */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-center justify-between max-w-lg mx-auto">
          {steps.map((step, index) => {
            const status = getStepStatus(step);
            const Icon = step.icon;
            return (
              <React.Fragment key={step.number}>
                <div className="flex flex-col items-center relative">
                  {/* Glow effect for current step */}
                  {status === 'current' && (
                    <div className="absolute -inset-2 bg-primary/10 rounded-2xl animate-pulse" />
                  )}
                  <div
                    className={`
                      relative w-10 h-10 rounded-xl flex items-center justify-center
                      mb-1.5 transition-all duration-300
                      ${status === 'completed' ? 'bg-primary text-white shadow-md shadow-primary/30' : ''}
                      ${status === 'current' ? 'bg-primary text-white shadow-lg shadow-primary/40 scale-110' : ''}
                      ${status === 'upcoming' ? 'bg-slate-100 text-slate-400' : ''}
                    `}
                  >
                    {status === 'completed' ? (
                      <Check size={18} strokeWidth={3} />
                    ) : (
                      <Icon size={18} />
                    )}
                  </div>
                  <span className={`text-xs font-semibold transition-colors ${
                    status === 'current' ? 'text-primary' :
                    status === 'completed' ? 'text-slate-700' : 'text-slate-400'
                  }`}>
                    <span className="sm:hidden">{step.shortLabel}</span>
                    <span className="hidden sm:inline">{step.label}</span>
                  </span>
                </div>
                {index < steps.length - 1 && (
                  <div className="flex-1 h-1 mx-4 mb-6 bg-slate-100 overflow-hidden rounded-full">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ease-out ${
                        step.number < currentStep ? 'bg-primary w-full' : 'w-0'
                      }`}
                    />
                  </div>
                )}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// RESUME DIFF COMPONENT - Before/After Comparison
// ============================================================================

function ResumeDiff({ originalResume, tailoredResume }) {
  // Simple line-by-line comparison with highlighting
  const getLines = (text) => text?.split('\n') || [];

  const originalLines = getLines(originalResume);
  const tailoredLines = getLines(tailoredResume);

  // Simple diff: mark lines as added (in tailored but different from original position)
  const findChanges = () => {
    const changes = [];
    const originalSet = new Set(originalLines.map(l => l.trim().toLowerCase()));

    tailoredLines.forEach((line, idx) => {
      const trimmedLine = line.trim();
      if (!trimmedLine) {
        changes.push({ line, type: 'unchanged', idx });
      } else if (!originalSet.has(trimmedLine.toLowerCase())) {
        changes.push({ line, type: 'added', idx });
      } else {
        changes.push({ line, type: 'unchanged', idx });
      }
    });

    return changes;
  };

  const changes = findChanges();
  const addedCount = changes.filter(c => c.type === 'added').length;

  return (
    <div className="space-y-4">
      {/* Summary Stats */}
      <div className="flex items-center gap-4 p-3 bg-slate-50 rounded-lg">
        <div className="flex items-center gap-2">
          <div className="w-3 h-3 rounded-full bg-green-500" />
          <span className="text-sm text-slate-600">
            <span className="font-semibold text-green-600">{addedCount}</span> lines enhanced
          </span>
        </div>
        <div className="text-xs text-slate-400">
          Green highlights show AI improvements
        </div>
      </div>

      {/* Diff View */}
      <div className="border border-slate-200 rounded-xl overflow-hidden">
        <div className="grid grid-cols-2 bg-slate-100 border-b border-slate-200">
          <div className="px-4 py-2 text-xs font-semibold text-slate-500 border-r border-slate-200">
            ORIGINAL
          </div>
          <div className="px-4 py-2 text-xs font-semibold text-slate-500">
            TAILORED
          </div>
        </div>

        <div className="max-h-[500px] overflow-y-auto">
          <div className="grid grid-cols-2 divide-x divide-slate-200">
            {/* Original Side */}
            <div className="p-4 bg-slate-50/50">
              <pre className="whitespace-pre-wrap font-sans text-xs text-slate-500 leading-relaxed">
                {originalResume || 'Original resume not available'}
              </pre>
            </div>

            {/* Tailored Side with Highlights */}
            <div className="p-4">
              <div className="space-y-0">
                {changes.map((change, idx) => (
                  <div
                    key={idx}
                    className={`font-sans text-xs leading-relaxed py-0.5 px-1 -mx-1 rounded ${
                      change.type === 'added'
                        ? 'bg-green-100 text-green-800'
                        : 'text-slate-700'
                    }`}
                  >
                    {change.line || '\u00A0'}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// ANIMATED SCORE COUNTER COMPONENT
// ============================================================================

function AnimatedScore({ value, duration = 1000 }) {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    if (typeof value !== 'number') return;

    const startTime = Date.now();
    const startValue = 0;

    const animate = () => {
      const elapsed = Date.now() - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Easing function for smooth deceleration
      const easeOut = 1 - Math.pow(1 - progress, 3);
      const current = Math.round(startValue + (value - startValue) * easeOut);

      setDisplayValue(current);

      if (progress < 1) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }, [value, duration]);

  return <span>{displayValue}</span>;
}

// ============================================================================
// APPLICATION TIMELINE COMPONENT
// ============================================================================

function ApplicationTimeline({ application }) {
  const events = [];

  // Build timeline events
  if (application.createdAt) {
    events.push({
      date: new Date(application.createdAt),
      label: 'Resume tailored',
      icon: FileText,
      color: 'bg-slate-400'
    });
  }

  if (application.appliedDate) {
    events.push({
      date: new Date(application.appliedDate),
      label: 'Applied',
      icon: CheckCircle,
      color: 'bg-secondary'
    });
  }

  if (application.interviewDate) {
    events.push({
      date: new Date(application.interviewDate),
      label: 'Interview scheduled',
      icon: Calendar,
      color: 'bg-primary'
    });
  }

  if (application.status === 'offer') {
    events.push({
      date: application.updatedAt ? new Date(application.updatedAt) : new Date(),
      label: 'Offer received!',
      icon: Star,
      color: 'bg-green-500'
    });
  }

  // Sort by date
  events.sort((a, b) => a.date - b.date);

  if (events.length === 0) {
    return null;
  }

  const formatDate = (date) => {
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <div className="relative">
      {/* Timeline line */}
      <div className="absolute left-4 top-6 bottom-6 w-0.5 bg-slate-200" />

      <div className="space-y-4">
        {events.map((event, idx) => {
          const Icon = event.icon;
          return (
            <div key={idx} className="flex items-center gap-4 relative">
              <div className={`w-8 h-8 rounded-full ${event.color} flex items-center justify-center z-10`}>
                <Icon size={14} className="text-white" />
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-slate-700">{event.label}</p>
                <p className="text-xs text-slate-400">{formatDate(event.date)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ============================================================================
// COMPANY INTEL CARD COMPONENT
// ============================================================================

function CompanyIntelCard({ company, jobDescription }) {
  const [intel, setIntel] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Extract company signals from job description
  useEffect(() => {
    if (!company || !jobDescription) return;

    // Parse common signals from JD text
    const jdLower = jobDescription.toLowerCase();

    const signals = {
      remote: jdLower.includes('remote') || jdLower.includes('work from home'),
      hybrid: jdLower.includes('hybrid'),
      onsite: jdLower.includes('on-site') || jdLower.includes('in-office'),
      startup: jdLower.includes('startup') || jdLower.includes('early-stage') || jdLower.includes('series a') || jdLower.includes('series b'),
      enterprise: jdLower.includes('fortune 500') || jdLower.includes('enterprise') || jdLower.includes('global company'),
      fastPaced: jdLower.includes('fast-paced') || jdLower.includes('fast paced') || jdLower.includes('move quickly'),
      equity: jdLower.includes('equity') || jdLower.includes('stock options') || jdLower.includes('rsu'),
      benefits: jdLower.includes('401k') || jdLower.includes('health insurance') || jdLower.includes('unlimited pto'),
    };

    // Count team size mentions
    const teamMatch = jobDescription.match(/team of (\d+)/i) || jobDescription.match(/(\d+)\+? employees/i);
    const teamSize = teamMatch ? teamMatch[1] : null;

    setIntel({ signals, teamSize, company });
  }, [company, jobDescription]);

  if (!intel) return null;

  const activeSignals = Object.entries(intel.signals).filter(([_, v]) => v);

  if (activeSignals.length === 0 && !intel.teamSize) return null;

  return (
    <div className="bg-gradient-to-br from-slate-50 to-slate-100 rounded-xl border border-slate-200 p-4">
      <div className="flex items-center gap-2 mb-3">
        <Building2 size={16} className="text-slate-500" />
        <span className="text-sm font-semibold text-slate-700">Company Signals</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {intel.signals.remote && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">
            <Globe size={12} /> Remote
          </span>
        )}
        {intel.signals.hybrid && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
            Hybrid
          </span>
        )}
        {intel.signals.startup && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-purple-100 text-purple-700 text-xs rounded-full">
            🚀 Startup
          </span>
        )}
        {intel.signals.enterprise && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-200 text-slate-700 text-xs rounded-full">
            🏢 Enterprise
          </span>
        )}
        {intel.signals.fastPaced && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
            ⚡ Fast-paced
          </span>
        )}
        {intel.signals.equity && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-700 text-xs rounded-full">
            📈 Equity
          </span>
        )}
        {intel.teamSize && (
          <span className="inline-flex items-center gap-1 px-2 py-1 bg-slate-200 text-slate-600 text-xs rounded-full">
            <Users size={12} /> {intel.teamSize}+ team
          </span>
        )}
      </div>
    </div>
  );
}

// ============================================================================
// FAQ PAGE COMPONENT
// ============================================================================

function FAQPage() {
  const [isScrolled, setIsScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const faqs = [
    {
      question: "Can I customize my resume myself for each job?",
      answer: "You can, but most applicants do not know what ATS systems prioritize. Resmaker performs that analysis automatically."
    },
    {
      question: "Is Resmaker just another resume builder?",
      answer: "No. Resmaker is a job specific resume optimization tool. It does not create generic resumes."
    },
    {
      question: "Do I need a different resume for every job application?",
      answer: "Yes. Different roles emphasize different skills, keywords, and experience. ATS systems evaluate each application independently."
    },
    {
      question: "Does Resmaker change my experience or make anything up?",
      answer: "No. Your experience stays the same. Only the emphasis and structure change."
    },
    {
      question: "How does Resmaker optimize my resume?",
      answer: "Resmaker analyzes the job description to identify required skills and keywords, then restructures your resume to highlight the most relevant experience for that specific role."
    },
    {
      question: "What is an ATS?",
      answer: "ATS stands for Applicant Tracking System. It's software that companies use to filter and rank resumes before a human recruiter sees them. Most large companies use ATS systems."
    },
    {
      question: "Will my resume still look professional?",
      answer: "Yes. Resmaker maintains clean, professional formatting while optimizing content for ATS systems and recruiter review."
    },
    {
      question: "Can I export my optimized resume?",
      answer: "Yes. You can download your optimized resume in both PDF and DOCX formats, ready to submit with your application."
    }
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 px-4 py-4 transition-all duration-200 ${
        isScrolled ? 'bg-white/95 backdrop-blur-lg border-b border-slate-200 shadow-sm' : ''
      }`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <a href="/" className="flex items-center gap-3">
            <Logo size={40} className="drop-shadow-lg" />
            <span className="font-heading text-xl font-bold text-slate-900">resmaker.ai</span>
          </a>
          <a
            href="/"
            className="text-slate-600 hover:text-slate-900 font-medium text-sm transition-colors"
          >
            Back to Home
          </a>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-16 px-4">
        <div className="max-w-3xl mx-auto">
          <h1 className="font-heading text-3xl sm:text-4xl font-bold text-slate-900 mb-4">
            Frequently Asked Questions
          </h1>
          <p className="text-lg text-slate-600 mb-12">
            Everything you need to know about resume optimization with Resmaker.
          </p>

          <div className="space-y-8">
            {faqs.map((faq, index) => (
              <div key={index} className="pb-8 border-b border-slate-200 last:border-b-0">
                <h2 className="text-lg font-semibold text-deep mb-3">
                  {faq.question}
                </h2>
                <p className="text-base text-slate-600 leading-relaxed">
                  {faq.answer}
                </p>
              </div>
            ))}
          </div>

          {/* CTA */}
          <div className="mt-16 text-center">
            <h2 className="text-xl font-semibold text-deep mb-4">
              Ready to optimize your resume?
            </h2>
            <a
              href="/"
              className="inline-flex items-center gap-2 px-8 py-4 bg-brand text-white font-semibold rounded-xl hover:bg-[#5A4CF5] transition-colors"
            >
              Get Started
              <ArrowRight size={18} />
            </a>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 text-center text-sm text-slate-500 border-t border-slate-200">
        <p>&copy; {new Date().getFullYear()} resmaker.ai. All rights reserved.</p>
      </footer>
    </div>
  );
}

// ============================================================================
// LANDING PAGE COMPONENT - Value Proposition First
// ============================================================================

function LandingPage({ onAuthSuccess }) {
  const verifiedMessage = 'Email verified successfully. Sign in to continue.';
  const [showAuth, setShowAuth] = useState(false);
  const [authPreset, setAuthPreset] = useState({
    mode: 'login',
    info: '',
    verificationUrl: '',
    resetToken: ''
  });
  const [verificationNotice, setVerificationNotice] = useState('');
  const [verificationMessage, setVerificationMessage] = useState('');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);

  // Scroll-aware header background
  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const sessionKey = 'emailVerifiedSuccess';
    const resetTokenKey = 'passwordResetToken';
    const params = new URLSearchParams(window.location.search);
    const verified = params.get('verified');
    const resetFlag = params.get('reset');
    const resetTokenFromUrl = params.get('token');
    const sessionVerified = sessionStorage.getItem(sessionKey);

    if (resetTokenFromUrl) {
      sessionStorage.setItem(resetTokenKey, resetTokenFromUrl);
    }

    if (verified === '1') {
      setVerificationNotice('Email verified! You can sign in now.');
      setVerificationMessage(verifiedMessage);
      if (window.posthog) {
        posthog.capture('email_verified');
      }
      setAuthPreset({
        mode: 'login',
        info: verifiedMessage,
        verificationUrl: '',
        resetToken: ''
      });
      sessionStorage.setItem(sessionKey, '1');
    } else if (sessionVerified) {
      setVerificationNotice('Email verified! You can sign in now.');
      setVerificationMessage(verifiedMessage);
    }

    if (resetFlag === '1') {
      const storedResetToken = sessionStorage.getItem(resetTokenKey);
      if (storedResetToken) {
        setAuthPreset({
          mode: 'reset',
          info: '',
          verificationUrl: '',
          resetToken: storedResetToken
        });
        setShowAuth(true);
      } else {
        setAuthPreset({
          mode: 'forgot',
          info: 'Reset link is missing or expired. Request a new one.',
          verificationUrl: '',
          resetToken: ''
        });
        setShowAuth(true);
      }
    }

    if (verified || resetTokenFromUrl) {
      const url = new URL(window.location.href);
      if (verified) url.searchParams.delete('verified');
      if (resetTokenFromUrl) url.searchParams.delete('token');
      window.history.replaceState({}, '', url.toString());
    }
  }, []);

  // Mock data for the preview cards
  const mockApplications = [
    { company: 'Stripe', role: 'Senior Engineer', status: 'interview', color: '#635bff' },
    { company: 'Linear', role: 'Product Designer', status: 'applied', color: '#5E6AD2' },
    { company: 'Notion', role: 'Full Stack Dev', status: 'offer', color: '#000000' },
  ];

  const getStatusStyle = (status) => {
    const styles = {
      draft: { bg: 'bg-slate-100', text: 'text-slate-600' },
      applied: { bg: 'bg-slate-100', text: 'text-slate-600' },
      interview: { bg: 'bg-slate-100', text: 'text-slate-900' },
      offer: { bg: 'bg-slate-100', text: 'text-slate-900' },
      rejected: { bg: 'bg-slate-100', text: 'text-slate-500' },
    };
    return styles[status] || styles.draft;
  };

  if (showAuth) {
    return (
      <div className="min-h-screen bg-white">
        <div className="absolute top-4 left-4 z-10">
          <button
            onClick={() => setShowAuth(false)}
            className="flex items-center gap-2 px-4 py-2 text-slate-600 hover:text-slate-900 font-medium transition-colors cursor-pointer"
          >
            <ChevronUp className="rotate-[-90deg]" size={18} />
            Back
          </button>
        </div>
        <Auth
          onAuthSuccess={onAuthSuccess}
          initialMode={authPreset.mode}
          initialInfo={authPreset.info}
          initialVerificationUrl={authPreset.verificationUrl}
          initialResetToken={authPreset.resetToken}
        />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white overflow-hidden">
      {/* Header */}
      <header className={`fixed top-0 left-0 right-0 z-50 px-4 py-4 transition-all duration-200 ${
        isScrolled ? 'bg-white/95 backdrop-blur-lg border-b border-slate-200 shadow-sm' : ''
      }`}>
        <div className="max-w-6xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size={40} className="drop-shadow-lg animate-pulse-glow" />
            <span className="font-heading text-xl font-bold text-slate-900">resmaker.ai</span>
          </div>

          {/* Desktop Nav */}
          <nav className="hidden sm:flex items-center gap-6">
            <a
              href="/faq"
              className="text-slate-600 hover:text-slate-900 font-medium text-sm transition-colors"
            >
              FAQ
            </a>
            <button
              onClick={() => {
                setAuthPreset({
                  mode: 'login',
                  info: verificationMessage || '',
                  verificationUrl: '',
                  resetToken: ''
                });
                setShowAuth(true);
              }}
              className="px-4 py-2 text-slate-700 font-semibold hover:text-primary transition-colors cursor-pointer"
            >
              Sign in
            </button>
          </nav>

          {/* Mobile: Just Sign in */}
          <button
            onClick={() => {
              setAuthPreset({
                mode: 'login',
                info: verificationMessage || '',
                verificationUrl: '',
                resetToken: ''
              });
              setShowAuth(true);
            }}
            className="sm:hidden px-4 py-2 text-slate-700 font-semibold hover:text-primary transition-colors cursor-pointer"
          >
            Sign in
          </button>
        </div>
      </header>

      {/* Hero Section */}
      <main className="pt-24 pb-12 px-4">
        <div className="max-w-6xl mx-auto">
          {verificationNotice && (
            <div className="mb-6 rounded-xl border border-green-200 bg-green-50/80 px-5 py-4 shadow-sm">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <div className="flex items-center gap-2 text-sm font-semibold text-green-900">
                  <CheckCircle2 size={18} />
                  {verificationNotice}
                </div>
                <button
                  onClick={() => {
                    setAuthPreset({
                      mode: 'login',
                      info: verifiedMessage,
                      verificationUrl: '',
                      resetToken: ''
                    });
                    setShowAuth(true);
                  }}
                  className="inline-flex items-center justify-center rounded-xl bg-green-600 px-4 py-2 text-sm font-semibold text-white shadow-sm transition hover:bg-green-700"
                >
                  Continue to sign in
                </button>
              </div>
            </div>
          )}
          <div className="grid lg:grid-cols-2 gap-12 items-center min-h-[70vh]">
            {/* Left: Value Prop */}
            <div className="space-y-8">
              <div className="space-y-4">
                <p className="text-lg md:text-xl text-slate-600 max-w-lg">
                  Your resume isn't bad. It's getting filtered out.
                </p>
                <h1 className="font-heading text-4xl md:text-5xl lg:text-6xl font-extrabold text-deep leading-tight tracking-tight">
                  Optimize Your Resume for{' '}
                  <span className="text-brand">
                    Every Job Posting
                  </span>
                </h1>
                <p className="text-lg md:text-xl text-slate-600 max-w-lg">
                  Resmaker optimizes your resume for each job posting so it can pass ATS screening and reach a recruiter.
                </p>
                <p className="text-base text-slate-500 max-w-lg">
                  Most applicants submit the same resume to every role. Hiring systems evaluate every job separately.
                </p>
              </div>

              {/* Value Props */}
              <div className="space-y-4">
                {[
                  { icon: Target, text: 'Pass ATS filters that reject 75% of applicants' },
                  { icon: Sparkles, text: 'One tailored resume for each job you apply to' },
                  { icon: TrendingUp, text: 'Track every application from click to offer' },
                ].map((item, idx) => (
                  <div
                    key={idx}
                    className={`flex items-center gap-3 animate-slideInLeft stagger-${idx + 1}`}
                    style={{ opacity: 0, animationFillMode: 'forwards' }}
                  >
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon size={20} className="text-primary" />
                    </div>
                    <span className="text-slate-700 font-medium">{item.text}</span>
                  </div>
                ))}
              </div>

              {/* CTA */}
              <div className="flex flex-col sm:flex-row gap-4 pt-4">
                <button
                  onClick={() => {
                    if (window.posthog) {
                      posthog.capture('landing_cta_clicked', { cta: 'get_started' });
                    }
                    setAuthPreset({ mode: 'signup', info: '', verificationUrl: '', resetToken: '' });
                    setShowAuth(true);
                  }}
                  className="px-8 py-4 bg-brand text-white font-semibold text-lg rounded-xl hover:bg-[#5A4CF5] transition-colors cursor-pointer flex items-center justify-center gap-2"
                >
                  Optimize My Resume
                  <ArrowRight size={20} />
                </button>
                <button
                  onClick={() => {
                    if (window.posthog) posthog.capture('landing_cta_clicked', { cta: 'sign_in' });
                    setAuthPreset({
                      mode: 'login',
                      info: verificationMessage || '',
                      verificationUrl: '',
                      resetToken: ''
                    });
                    setShowAuth(true);
                  }}
                  className="px-8 py-4 bg-white/80 backdrop-blur text-slate-700 font-semibold text-lg rounded-xl border-2 border-slate-200 hover:border-primary/30 transition-all duration-300 cursor-pointer"
                >
                  Sign In
                </button>
              </div>
            </div>

            {/* Right: Preview Cards */}
            <div className="relative hidden lg:block">
              {/* Floating Application Cards */}
              <div className="relative h-[500px]">
                {/* Main Preview Card */}
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-80 animate-float">
                  <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-lg border border-white/50 overflow-hidden">
                    <div className="p-4 border-b border-slate-100">
                      <div className="flex items-center justify-between">
                        <span className="font-heading font-bold text-slate-900">My Applications</span>
                        <span className="text-xs text-slate-500">3 active</span>
                      </div>
                    </div>
                    <div className="p-4 space-y-3">
                      {mockApplications.map((app, idx) => {
                        const statusStyle = getStatusStyle(app.status);
                        return (
                          <div
                            key={idx}
                            className="flex items-center gap-3 p-3 bg-slate-50 rounded-xl hover:bg-slate-100 transition-colors"
                          >
                            <div
                              className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold text-sm"
                              style={{ backgroundColor: app.color }}
                            >
                              {app.company[0]}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="font-semibold text-slate-900 text-sm truncate">{app.company}</div>
                              <div className="text-xs text-slate-500 truncate">{app.role}</div>
                            </div>
                            <span className={`px-2 py-1 rounded-full text-xs font-medium ${statusStyle.bg} ${statusStyle.text}`}>
                              {app.status}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>

                {/* Floating Stats Card */}
                <div className="absolute top-8 right-0 animate-float" style={{ animationDelay: '0.5s' }}>
                  <div className="bg-white/90 backdrop-blur-xl rounded-xl shadow-xl border border-white/50 p-4">
                    <div className="text-2xl font-bold text-primary">12</div>
                    <div className="text-xs text-slate-500">Applications</div>
                  </div>
                </div>

                {/* Floating Success Card */}
                <div className="absolute bottom-16 left-0 animate-float" style={{ animationDelay: '1s' }}>
                  <div className="bg-white border border-slate-200 rounded-xl shadow-lg p-4 flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center">
                      <Check size={16} className="text-white" />
                    </div>
                    <div>
                      <div className="font-semibold text-sm text-slate-900">Offer Received!</div>
                      <div className="text-xs text-slate-500">Stripe - Senior Engineer</div>
                    </div>
                  </div>
                </div>

                {/* Background Decoration */}
                <div className="absolute inset-0 -z-10">
                  <div className="absolute top-1/4 left-1/4 w-64 h-64 bg-slate-100 rounded-full blur-3xl"></div>
                  <div className="absolute bottom-1/4 right-1/4 w-64 h-64 bg-slate-100 rounded-full blur-3xl"></div>
                </div>
              </div>
            </div>
          </div>

          {/* Mobile Preview - Simplified */}
          <div className="lg:hidden mt-8">
            <div className="bg-white/90 backdrop-blur-xl rounded-2xl shadow-xl border border-white/50 p-6">
              <div className="text-center mb-4">
                <span className="font-heading font-bold text-slate-900">Your Application Library</span>
              </div>
              <div className="grid grid-cols-3 gap-4 text-center">
                <div>
                  <div className="text-2xl font-bold text-primary">3</div>
                  <div className="text-xs text-slate-500">Applied</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-slate-700">1</div>
                  <div className="text-xs text-slate-500">Interview</div>
                </div>
                <div>
                  <div className="text-2xl font-bold text-green-500">1</div>
                  <div className="text-xs text-slate-500">Offer</div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Value Bar */}
        <div className="mt-16 sm:mt-24 py-16 sm:py-20 border-t border-slate-200">
          <div className="max-w-5xl mx-auto">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 md:gap-12">
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-brand/10 flex items-center justify-center">
                  <Shield size={20} className="text-brand" />
                </div>
                <p className="text-sm font-medium text-slate-900">Built for ATS screening</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-brand/10 flex items-center justify-center">
                  <Target size={20} className="text-brand" />
                </div>
                <p className="text-sm font-medium text-slate-900">Optimized per job posting</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-brand/10 flex items-center justify-center">
                  <Zap size={20} className="text-brand" />
                </div>
                <p className="text-sm font-medium text-slate-900">Done in seconds</p>
              </div>
              <div className="text-center">
                <div className="w-10 h-10 mx-auto mb-3 rounded-lg bg-brand/10 flex items-center justify-center">
                  <Download size={20} className="text-brand" />
                </div>
                <p className="text-sm font-medium text-slate-900">Export ready in PDF and DOCX</p>
              </div>
            </div>
          </div>
        </div>

        {/* Section: Why One Resume No Longer Works */}
        <div className="py-16 sm:py-24">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-deep mb-8">
              Why One Resume No Longer Works
            </h2>
            <div className="space-y-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              <p>
                Recruiters do not review resumes first.<br />
                Applicant Tracking Systems do.
              </p>
              <p>
                ATS software does not ask whether your resume is good.<br />
                It asks how closely your resume matches the job description.
              </p>
              <p className="text-slate-900 font-medium">
                That difference determines whether your application is seen.
              </p>
            </div>

            <div className="mt-12 pt-12 border-t border-slate-200">
              <h3 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900 mb-6">
                The Problem With General Resume Builders
              </h3>
              <div className="space-y-6 text-base sm:text-lg text-slate-600 leading-relaxed">
                <p>
                  Most resume tools help you write stronger bullet points, improve formatting and clarity, and create a single polished resume.
                </p>
                <p>
                  But ATS systems compare resumes directly against job postings.
                </p>
                <p>
                  If the keywords, skills, and experience emphasis do not align with that role, your resume is filtered out even if you are qualified.
                </p>
                <p className="text-slate-900 font-medium">
                  A strong resume can still lose to a more relevant one.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section: How Resmaker Optimizes Your Resume */}
        <div className="py-16 sm:py-24 bg-lavender">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-deep mb-8">
              How Resmaker Optimizes Your Resume for Each Job
            </h2>
            <div className="space-y-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              <p>
                Resmaker does not help you write one resume.<br />
                It helps you submit the right resume for every job you apply to.
              </p>
              <div>
                <p className="mb-4">For each job posting, Resmaker:</p>
                <ul className="space-y-3 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2.5 flex-shrink-0"></span>
                    <span>Analyzes the job description and role requirements</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2.5 flex-shrink-0"></span>
                    <span>Identifies job specific keywords and skills</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2.5 flex-shrink-0"></span>
                    <span>Rewrites and reorders your resume to match ATS screening logic</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2.5 flex-shrink-0"></span>
                    <span>Aligns your experience with what that role prioritizes</span>
                  </li>
                </ul>
              </div>
              <p className="text-slate-900 font-medium">
                Same candidate. Optimized resume. Every application.
              </p>
            </div>

            <div className="mt-12 pt-12 border-t border-slate-200">
              <h3 className="font-heading text-xl sm:text-2xl font-semibold text-slate-900 mb-6">
                Why This Works
              </h3>
              <div className="space-y-6 text-base sm:text-lg text-slate-600 leading-relaxed">
                <p>
                  ATS systems rank resumes based on relevance to the job posting, not writing quality alone.
                </p>
                <p>
                  Resmaker ensures your resume is written using recruiter and job description language, structured in a format ATS systems can parse, and tailored to the skills and experience each role screens for.
                </p>
                <p className="text-slate-900 font-medium">
                  This is how resumes pass automated filters and reach human reviewers.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Section: Comparison Table */}
        <div className="py-16 sm:py-24 bg-lightBlue">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-deep mb-8">
              Resume Builders vs Job Specific Resume Optimization
            </h2>

            {/* Comparison Table */}
            <div className="overflow-x-auto -mx-4 px-4 sm:mx-0 sm:px-0">
              <div className="overflow-hidden rounded-xl border border-slate-200 min-w-[500px] sm:min-w-0">
              <table className="w-full">
                <thead>
                  <tr className="bg-slate-50">
                    <th className="text-left py-4 px-5 text-sm font-semibold text-slate-900 border-b border-slate-200">Feature</th>
                    <th className="text-center py-4 px-5 text-sm font-semibold text-slate-500 border-b border-slate-200">General Resume Builders</th>
                    <th className="text-center py-4 px-5 text-sm font-semibold text-slate-900 border-b border-slate-200">Resmaker</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  <tr>
                    <td className="py-4 px-5 text-sm text-slate-700">One resume for all jobs</td>
                    <td className="py-4 px-5 text-center text-sm text-slate-500">Yes</td>
                    <td className="py-4 px-5 text-center text-sm font-medium text-slate-900">No</td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="py-4 px-5 text-sm text-slate-700">Job description analysis</td>
                    <td className="py-4 px-5 text-center text-sm text-slate-500">No</td>
                    <td className="py-4 px-5 text-center text-sm font-medium text-slate-900">Yes</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-5 text-sm text-slate-700">Keyword alignment per role</td>
                    <td className="py-4 px-5 text-center text-sm text-slate-500">Limited</td>
                    <td className="py-4 px-5 text-center text-sm font-medium text-slate-900">Precise</td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="py-4 px-5 text-sm text-slate-700">Skill prioritization</td>
                    <td className="py-4 px-5 text-center text-sm text-slate-500">Static</td>
                    <td className="py-4 px-5 text-center text-sm font-medium text-slate-900">Dynamic</td>
                  </tr>
                  <tr>
                    <td className="py-4 px-5 text-sm text-slate-700">Built for ATS screening</td>
                    <td className="py-4 px-5 text-center text-sm text-slate-500">Not primarily</td>
                    <td className="py-4 px-5 text-center text-sm font-medium text-slate-900">Yes</td>
                  </tr>
                  <tr className="bg-slate-50/50">
                    <td className="py-4 px-5 text-sm text-slate-700">Optimized per application</td>
                    <td className="py-4 px-5 text-center text-sm text-slate-500">No</td>
                    <td className="py-4 px-5 text-center text-sm font-medium text-slate-900">Yes</td>
                  </tr>
                </tbody>
              </table>
              </div>
            </div>

            {/* Closing Line */}
            <div className="mt-10 text-center">
              <p className="text-base sm:text-lg text-slate-600">
                The difference is not design.<br />
                <span className="text-slate-900 font-medium">The difference is relevance.</span>
              </p>
            </div>
          </div>
        </div>

        {/* Section: How Resmaker Works */}
        <div className="py-16 sm:py-24 bg-slate-50">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-deep mb-8">
              How Resmaker Works
            </h2>

            <div className="space-y-12">
              {/* Step 1 */}
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">1</span>
                </div>
                <div>
                  <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mb-2">
                    Paste the Job Posting
                  </h3>
                  <p className="text-base text-slate-600">
                    Resmaker analyzes required skills, keywords, and role priorities.
                  </p>
                </div>
              </div>

              {/* Step 2 */}
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">2</span>
                </div>
                <div>
                  <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mb-2">
                    Optimize Your Resume
                  </h3>
                  <p className="text-base text-slate-600">
                    Your resume is rewritten and reordered to match that specific job.
                  </p>
                </div>
              </div>

              {/* Step 3 */}
              <div className="flex gap-6">
                <div className="flex-shrink-0 w-10 h-10 rounded-full bg-brand flex items-center justify-center">
                  <span className="text-sm font-semibold text-white">3</span>
                </div>
                <div>
                  <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900 mb-2">
                    Download and Apply
                  </h3>
                  <p className="text-base text-slate-600">
                    Submit a resume designed for ATS screening and recruiter review.
                  </p>
                </div>
              </div>
            </div>

            {/* CTA */}
            <div className="mt-12 text-center">
              <button
                onClick={() => {
                  if (window.posthog) {
                    posthog.capture('landing_cta_clicked', { cta: 'how_it_works' });
                  }
                  setAuthPreset({ mode: 'signup', info: '', verificationUrl: '', resetToken: '' });
                  setShowAuth(true);
                }}
                className="px-8 py-4 bg-brand text-white font-semibold text-lg rounded-xl hover:bg-[#5A4CF5] transition-colors cursor-pointer inline-flex items-center gap-2"
              >
                Optimize My Resume for a Job
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Section: Why Resmaker Is Not ChatGPT */}
        <div className="py-16 sm:py-24">
          <div className="max-w-3xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-deep mb-8">
              Why Resmaker Is Not ChatGPT
            </h2>
            <div className="space-y-6 text-base sm:text-lg text-slate-600 leading-relaxed">
              <p>
                ChatGPT can help you write a resume.<br />
                It does not optimize your resume against a specific job posting.
              </p>
              <p className="text-slate-900 font-medium">
                Resmaker is built specifically for resume screening systems.
              </p>
              <div>
                <p className="mb-4">Unlike general AI tools, Resmaker:</p>
                <ul className="space-y-3 ml-1">
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2.5 flex-shrink-0"></span>
                    <span>Compares your resume directly to the job description</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2.5 flex-shrink-0"></span>
                    <span>Identifies missing and misweighted keywords</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2.5 flex-shrink-0"></span>
                    <span>Adjusts structure, emphasis, and phrasing per role</span>
                  </li>
                  <li className="flex items-start gap-3">
                    <span className="w-1.5 h-1.5 rounded-full bg-slate-400 mt-2.5 flex-shrink-0"></span>
                    <span>Optimizes for how ATS software scores resumes</span>
                  </li>
                </ul>
              </div>
              <p className="pt-4">
                ChatGPT generates text.<br />
                <span className="text-slate-900 font-medium">Resmaker optimizes for hiring systems.</span>
              </p>
            </div>
          </div>
        </div>

        {/* Final CTA Section */}
        <div className="py-20 sm:py-28 bg-gradient-to-r from-deep to-brand">
          <div className="max-w-2xl mx-auto text-center">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-8">
              Optimize Your Resume for a Job Posting Today
            </h2>
            <p className="text-base sm:text-lg text-white/80 mb-10 leading-relaxed">
              Stop sending the same resume to every job.<br />
              One job.<br />
              One optimized resume.
            </p>
            <button
              onClick={() => {
                if (window.posthog) {
                  posthog.capture('landing_cta_clicked', { cta: 'final_cta' });
                }
                setAuthPreset({ mode: 'signup', info: '', verificationUrl: '', resetToken: '' });
                setShowAuth(true);
              }}
              className="px-10 py-5 bg-green-500 text-white font-semibold text-lg rounded-xl hover:bg-green-600 transition-colors cursor-pointer inline-flex items-center gap-2"
            >
              Optimize My Resume
              <ArrowRight size={20} />
            </button>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 text-center text-sm text-slate-500">
        <p>
          Your data is stored securely. See our{' '}
          <button
            onClick={() => setShowPrivacyModal(true)}
            className="text-primary hover:text-primary/80 underline cursor-pointer font-medium"
          >
            Privacy Policy
          </button>
          {' '}for details.
        </p>
      </footer>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-4xl w-full max-h-[90vh] flex flex-col rounded-2xl shadow-lg overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h3 className="font-heading text-xl font-semibold text-slate-900">Privacy Policy</h3>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              <PrivacyPolicy />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="px-6 py-3 bg-primary hover:bg-dark text-white font-semibold rounded-xl transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Consent Banner */}
      <CookieConsent onOpenPrivacyPolicy={() => setShowPrivacyModal(true)} />
    </div>
  );
}

// ============================================================================
// BILLING RETURN PAGES
// ============================================================================

function BillingReturnPage({ status }) {
  const params = new URLSearchParams(window.location.search);
  const sessionId = params.get('session_id');
  const isSuccess = status === 'success';
  const continueUrl = isSuccess && sessionId ? `/?session_id=${sessionId}` : '/';

  return (
    <div className="min-h-screen bg-white flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-xl bg-white border border-slate-200 shadow-lg rounded-xl p-8 sm:p-10 text-center space-y-6">
        <div className={`mx-auto w-16 h-16 rounded-xl flex items-center justify-center ${
          isSuccess ? 'bg-green-50 text-green-600' : 'bg-slate-100 text-slate-600'
        }`}>
          {isSuccess ? <CheckCircle2 size={32} /> : <X size={28} />}
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold text-slate-900 mb-2">
            {isSuccess ? 'Payment successful' : 'Payment canceled'}
          </h1>
          <p className="text-slate-600">
            {isSuccess
              ? 'Thanks for your purchase. We\'re unlocking 20 additional resumes for your account now.'
              : 'No charge was made. You can retry checkout any time from the app.'}
          </p>
        </div>
        <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 text-sm text-slate-600">
          {isSuccess
            ? 'If your access doesn’t update immediately, refresh the app or sign out and back in.'
            : 'Need help? Contact support and we can assist with checkout issues.'}
        </div>
        <button
          onClick={() => {
            window.location.href = continueUrl;
          }}
          className="w-full sm:w-auto px-6 py-3 bg-primary text-white font-semibold rounded-xl hover:bg-dark transition-colors cursor-pointer"
        >
          Return to app
        </button>
      </div>
    </div>
  );
}

// ============================================================================
// SETTINGS DRAWER COMPONENT
// ============================================================================

function SettingsDrawer({
  isOpen,
  onClose,
  masterResume,
  hasMasterResume,
  onEditMasterResume,
  onUploadResume,
  onDownloadResume,
  onCopyResume,
  formatMasterResume,
  isAnalyzing,
  copied,
  uploadSuccess,
  uploadError,
  // Data section props
  qaHistory,
  resumeLibrary,
  user,
  onExportData,
  onImportData,
  authToken,
  onResetAllData,
  isResettingData,
  resetDataError,
  // Re-optimization
  onOptimizeResume,
  isOptimizing,
  optimizeError,
  // LinkedIn
  onOpenLinkedIn
}) {
  const [activeTab, setActiveTab] = useState('resume');
  const [isLoadingPortal, setIsLoadingPortal] = useState(false);
  const [portalError, setPortalError] = useState('');

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40"
        onClick={onClose}
      />

      {/* Drawer */}
      <div className="fixed inset-y-0 right-0 w-full sm:w-[450px] bg-white shadow-lg z-50 drawer-enter overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h2 className="font-heading text-xl font-bold text-slate-900 flex items-center gap-2">
            <Settings size={20} aria-hidden="true" />
            Settings
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
            aria-label="Close settings"
          >
            <X size={20} aria-hidden="true" />
          </button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-slate-200" role="tablist" aria-label="Settings sections">
          <button
            onClick={() => setActiveTab('resume')}
            className={`flex-1 px-4 py-2.5 font-medium text-sm transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary ${
              activeTab === 'resume'
                ? 'text-primary border-b-2 border-primary bg-primary/5'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            role="tab"
            aria-selected={activeTab === 'resume'}
            aria-controls="resume-panel"
          >
            Master Resume
          </button>
          <button
            onClick={() => setActiveTab('data')}
            className={`flex-1 px-4 py-2.5 font-medium text-sm transition-colors cursor-pointer focus:outline-none focus:ring-2 focus:ring-inset focus:ring-primary ${
              activeTab === 'data'
                ? 'text-primary border-b-2 border-primary bg-primary/5'
                : 'text-slate-600 hover:text-slate-900'
            }`}
            role="tab"
            aria-selected={activeTab === 'data'}
            aria-controls="data-panel"
          >
            Data & Export
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-4">
          {activeTab === 'resume' && (
            <div className="space-y-4">
              {/* Actions */}
              <div className="grid grid-cols-2 gap-2">
                <label
                  className={`flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 text-white font-medium text-sm rounded-xl transition-colors focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2 ${
                    isAnalyzing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-slate-600'
                  }`}
                  role="button"
                  tabIndex={isAnalyzing ? -1 : 0}
                >
                  {isAnalyzing ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent" aria-hidden="true"></div>
                      <span>Uploading...</span>
                    </>
                  ) : (
                    <>
                      <Upload size={16} aria-hidden="true" />
                      <span>Upload New</span>
                    </>
                  )}
                  <input
                    type="file"
                    accept=".txt,.pdf,.docx,.doc"
                    onChange={onUploadResume}
                    disabled={isAnalyzing}
                    className="sr-only"
                    aria-label="Upload resume file"
                  />
                </label>
                <button
                  onClick={onEditMasterResume}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white font-medium text-sm rounded-xl transition-colors cursor-pointer hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  aria-label="Edit master resume"
                >
                  <Edit2 size={16} aria-hidden="true" />
                  Edit
                </button>
                <button
                  onClick={onCopyResume}
                  className="flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 font-medium text-sm rounded-xl transition-colors cursor-pointer hover:bg-slate-200 focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2"
                  aria-label={copied ? 'Resume copied to clipboard' : 'Copy resume to clipboard'}
                >
                  {copied ? <Check size={16} aria-hidden="true" /> : <Copy size={16} aria-hidden="true" />}
                  {copied ? 'Copied!' : 'Copy'}
                </button>
                <button
                  onClick={onDownloadResume}
                  disabled={!hasMasterResume}
                  className={`flex items-center justify-center gap-2 px-4 py-2 font-medium text-sm rounded-xl transition-colors focus:outline-none focus:ring-2 focus:ring-slate-400 focus:ring-offset-2 ${
                    hasMasterResume
                      ? 'bg-slate-900 text-white cursor-pointer hover:bg-slate-800'
                      : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                  }`}
                  aria-label="Download master resume"
                  aria-disabled={!hasMasterResume}
                >
                  <Download size={16} aria-hidden="true" />
                  Download
                </button>
              </div>

              {/* Upload Success Message */}
              {uploadSuccess && (
                <div className="flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl">
                  <Check size={20} className="text-green-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-green-800">Resume replaced successfully!</p>
                    <p className="text-sm text-green-700">Your new master resume is now active.</p>
                  </div>
                </div>
              )}

              {/* Upload Error Message */}
              {uploadError && (
                <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl">
                  <X size={20} className="text-red-600 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-red-800">Upload failed</p>
                    <p className="text-sm text-red-700">{uploadError}</p>
                  </div>
                </div>
              )}

              {/* Re-optimize Master Resume */}
              {hasMasterResume && qaHistory.length > 0 && (
                <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-primary/10 rounded-lg">
                      <Sparkles size={20} className="text-primary" />
                    </div>
                    <div className="flex-1">
                      <h4 className="font-heading font-semibold text-slate-900 mb-1">Enhance Your Master Resume</h4>
                      <p className="text-sm text-slate-600 mb-3">
                        Use your {qaHistory.length} Q&A session{qaHistory.length > 1 ? 's' : ''} to add metrics, achievements, and details you've shared during applications.
                      </p>
                      <button
                        onClick={onOptimizeResume}
                        disabled={isOptimizing}
                        className={`flex items-center gap-2 px-4 py-2 bg-primary text-white font-medium text-sm rounded-xl transition-colors ${
                          isOptimizing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-dark'
                        }`}
                      >
                        {isOptimizing ? (
                          <>
                            <Loader2 size={16} className="animate-spin" />
                            Optimizing...
                          </>
                        ) : (
                          <>
                            <TrendingUp size={16} />
                            Optimize Resume
                          </>
                        )}
                      </button>
                      <p className="text-xs text-slate-500 mt-2">Uses 1 credit</p>
                      {optimizeError && (
                        <p className="text-xs text-red-600 mt-2">{optimizeError}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {/* LinkedIn Profile Generator */}
              {hasMasterResume && (
                <div className="bg-[#0A66C2]/5 border border-[#0A66C2]/20 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <div className="p-2 bg-[#0A66C2]/10 rounded-lg">
                      <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5 text-[#0A66C2]">
                        <path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/>
                      </svg>
                    </div>
                    <div className="flex-1">
                      <h4 className="font-heading font-semibold text-slate-900 mb-1">LinkedIn Profile Generator</h4>
                      <p className="text-sm text-slate-600 mb-3">
                        Transform your resume into a LinkedIn-optimized profile with headline, about section, and more.
                      </p>
                      <button
                        onClick={onOpenLinkedIn}
                        className="flex items-center gap-2 px-4 py-2 bg-[#0A66C2] text-white font-medium text-sm rounded-xl transition-colors cursor-pointer hover:bg-[#004182]"
                      >
                        <Sparkles size={16} />
                        Generate LinkedIn Profile
                      </button>
                      <p className="text-xs text-slate-500 mt-2">Uses 1 credit</p>
                    </div>
                  </div>
                </div>
              )}

              {/* Preview */}
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-4 max-h-[400px] overflow-y-auto">
                <pre className="whitespace-pre-wrap font-mono text-xs text-slate-700 leading-relaxed">
                  {formatMasterResume()}
                </pre>
              </div>

              <p className="text-xs text-slate-500 text-center">
                Your master resume is the foundation for all tailored versions
              </p>
            </div>
          )}

          {activeTab === 'data' && (
            <div className="space-y-4">
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3" role="region" aria-label="Your data summary">
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-slate-900">{qaHistory.length}</div>
                  <div className="text-xs text-slate-500">Q&A Sessions</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-primary">{resumeLibrary.length}</div>
                  <div className="text-xs text-slate-500">Resumes</div>
                </div>
                <div className="bg-slate-50 rounded-xl p-3 text-center">
                  <div className="text-2xl font-bold text-slate-700">
                    {new Set(qaHistory.map(e => e.company).filter(Boolean)).size}
                  </div>
                  <div className="text-xs text-slate-500">Companies</div>
                </div>
              </div>

              {/* Export */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-heading font-semibold text-slate-900 mb-2">Export Profile</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Download all your data as JSON for backup.
                </p>
                <button
                  onClick={onExportData}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white font-medium text-sm rounded-xl transition-colors cursor-pointer hover:bg-primary/90 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  aria-label="Export all your data as JSON"
                >
                  <Download size={16} aria-hidden="true" />
                  Export Data (JSON)
                </button>
              </div>

              {/* Import */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-heading font-semibold text-slate-900 mb-2">Import Profile</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Restore from a previously exported file.
                </p>
                <label
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-700 text-white font-medium text-sm rounded-xl transition-colors cursor-pointer hover:bg-slate-600 focus-within:ring-2 focus-within:ring-primary focus-within:ring-offset-2"
                  role="button"
                  tabIndex={0}
                >
                  <Upload size={16} aria-hidden="true" />
                  <span>Import Data (JSON)</span>
                  <input
                    type="file"
                    accept=".json"
                    onChange={onImportData}
                    className="sr-only"
                    aria-label="Import data from JSON file"
                  />
                </label>
              </div>

              {/* Danger Zone */}
              <div className="bg-red-50 border border-red-200 rounded-xl p-4">
                <h3 className="font-heading font-semibold text-red-900 mb-2">Reset All Data</h3>
                <p className="text-sm text-red-700 mb-3">
                  Permanently deletes your master resume, resume history, applications, interviews, offers, and Q&A history.
                  Credits and billing stay unchanged.
                </p>
                <button
                  onClick={onResetAllData}
                  disabled={isResettingData}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-red-600 text-white font-medium text-sm rounded-xl transition-colors cursor-pointer hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
                  aria-label="Reset all account data"
                >
                  {isResettingData ? (
                    <>
                      <Loader2 size={16} className="animate-spin" />
                      Resetting...
                    </>
                  ) : (
                    <>
                      <Trash2 size={16} />
                      Reset All Data
                    </>
                  )}
                </button>
                {resetDataError && (
                  <p className="mt-2 text-sm text-red-700">{resetDataError}</p>
                )}
              </div>

              {/* Account */}
              <div className="bg-slate-50 rounded-xl p-4">
                <h3 className="font-heading font-semibold text-slate-900 mb-2">Account</h3>
                <p className="text-sm text-slate-600 mb-3">
                  Signed in as <span className="font-medium">{user?.email}</span>
                </p>
                <button
                  onClick={async () => {
                    if (isLoadingPortal) return;
                    setIsLoadingPortal(true);
                    try {
                      const response = await fetch(`${API_URL}/api/billing/portal`, {
                        method: 'POST',
                        headers: {
                          'Authorization': `Bearer ${authToken}`,
                          'Content-Type': 'application/json'
                        }
                      });

                      if (!response.ok) {
                        const errorData = await response.json();
                        setPortalError(errorData.error || 'Failed to open billing portal');
                        setIsLoadingPortal(false);
                        return;
                      }

                      const data = await response.json();

                      if (data.url) {
                        window.location.href = data.url;
                      } else {
                        setPortalError(data.error || 'No billing portal URL received');
                        setIsLoadingPortal(false);
                      }
                    } catch (error) {
                      console.error('Error opening billing portal:', error);
                      setPortalError('Failed to open billing portal. Please try again.');
                      setIsLoadingPortal(false);
                    }
                  }}
                  disabled={isLoadingPortal}
                  className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-primary text-white font-medium text-sm rounded-xl transition-colors cursor-pointer hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
                  aria-label="Manage billing and subscription"
                >
                  {isLoadingPortal ? (
                    <>
                      <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                      Opening...
                    </>
                  ) : (
                    <>
                      <Briefcase size={16} />
                      Manage Billing
                    </>
                  )}
                </button>
                {portalError && (
                  <p className="mt-2 text-sm text-red-600">{portalError}</p>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// ============================================================================
// LOCALSTORAGE PERSISTENCE LAYER
// ============================================================================

const STORAGE_KEYS = {
  USER_PROFILE: 'resmaker_userProfile',
  QA_HISTORY: 'resmaker_qaHistory',
  RESUME_LIBRARY: 'resmaker_resumeLibrary'
};

// Save user profile (master resume + metadata)
const saveUserProfile = (profile) => {
  localStorage.setItem(STORAGE_KEYS.USER_PROFILE, JSON.stringify(profile));
};

// Load user profile
const loadUserProfile = () => {
  const data = localStorage.getItem(STORAGE_KEYS.USER_PROFILE);
  return data ? JSON.parse(data) : null;
};

// Save Q&A history (builds knowledge over time)
const saveQAHistory = (qaEntry) => {
  const history = loadQAHistory();
  history.push({
    ...qaEntry,
    timestamp: new Date().toISOString(),
    id: Date.now()
  });
  localStorage.setItem(STORAGE_KEYS.QA_HISTORY, JSON.stringify(history));
};

// Load Q&A history
const loadQAHistory = () => {
  const data = localStorage.getItem(STORAGE_KEYS.QA_HISTORY);
  return data ? JSON.parse(data) : [];
};

// Save tailored resume to library
const saveToResumeLibrary = (resume) => {
  const library = loadResumeLibrary();
  library.push({
    ...resume,
    timestamp: new Date().toISOString(),
    id: Date.now(),
    status: resume.status || 'draft', // draft, applied, interview, rejected, offer
    appliedDate: resume.appliedDate || null
  });
  localStorage.setItem(STORAGE_KEYS.RESUME_LIBRARY, JSON.stringify(library));
};

// Update application status
const updateApplicationStatus = (id, status, appliedDate) => {
  const library = loadResumeLibrary();
  const index = library.findIndex(item => item.id === id);
  if (index !== -1) {
    library[index].status = status;
    if (appliedDate) {
      library[index].appliedDate = appliedDate;
    }
    localStorage.setItem(STORAGE_KEYS.RESUME_LIBRARY, JSON.stringify(library));
  }
  return library;
};

// Load resume library
const loadResumeLibrary = () => {
  const data = localStorage.getItem(STORAGE_KEYS.RESUME_LIBRARY);
  return data ? JSON.parse(data) : [];
};

// Export entire user data (now uses state from database)
const exportUserData = (qaHistory, resumeLibrary, masterResume, user) => {
  return {
    user: {
      email: user?.email || 'unknown',
      createdAt: new Date().toISOString()
    },
    masterResume: masterResume,
    qaHistory: qaHistory,
    resumeLibrary: resumeLibrary,
    exportDate: new Date().toISOString(),
    version: '2.0'
  };
};

// ============================================
// BUILD CANDIDATE PROFILE FROM Q&A HISTORY
// This extracts learnings about the candidate to personalize future tailoring
// ============================================
const buildCandidateProfile = (qaHistory) => {
  if (!qaHistory || qaHistory.length === 0) return null;

  // Extract all Q&A pairs
  const allQAs = qaHistory.flatMap(entry => {
    const questions = Array.isArray(entry.questions) ? entry.questions : [];
    const answers = Array.isArray(entry.answers) ? entry.answers : [];
    return questions.map((q, i) => ({
      question: q,
      answer: answers[i] || '',
      company: entry.company || 'Unknown'
    })).filter(qa => qa.answer && qa.answer.trim() !== '');
  });

  // Extract achievements with numbers (metrics)
  const achievements = allQAs
    .filter(qa => /\d+/.test(qa.answer)) // Has numbers
    .map(qa => qa.answer)
    .slice(0, 10); // Top 10 quantified achievements

  // Detect primary domain from questions asked
  const allText = allQAs.map(qa => `${qa.question} ${qa.answer}`).join(' ').toLowerCase();

  let primaryDomain = 'general';
  let secondaryDomain = null;

  // Engineering signals
  const engSignals = (allText.match(/engineer|code|software|system|architect|deploy|api|database|infrastructure|devops|backend|frontend|fullstack/g) || []).length;
  // Product signals
  const pmSignals = (allText.match(/product|roadmap|stakeholder|user research|requirements|prioriti|strategy|metrics|okr|kpi|launch/g) || []).length;
  // Management signals
  const mgmtSignals = (allText.match(/team|manage|led|direct report|hire|mentor|budget|headcount/g) || []).length;

  if (engSignals > pmSignals && engSignals > mgmtSignals) {
    primaryDomain = 'engineering';
    if (pmSignals > 3) secondaryDomain = 'product';
    if (mgmtSignals > 3) secondaryDomain = 'management';
  } else if (pmSignals > engSignals && pmSignals > mgmtSignals) {
    primaryDomain = 'product';
    if (engSignals > 3) secondaryDomain = 'engineering';
  } else if (mgmtSignals > 2) {
    primaryDomain = 'management';
  }

  // Detect career trajectory
  let careerTrajectory = 'growth'; // default
  if (primaryDomain !== secondaryDomain && secondaryDomain) {
    careerTrajectory = 'pivot'; // Has experience in multiple domains
  }

  // Extract key strengths from answers
  const strengths = [];
  if (allText.includes('scale') || allText.includes('million') || allText.includes('growth')) {
    strengths.push('scaling systems/teams');
  }
  if (allText.includes('lead') || allText.includes('manage') || allText.includes('team')) {
    strengths.push('leadership');
  }
  if (allText.includes('cross-functional') || allText.includes('stakeholder')) {
    strengths.push('cross-functional collaboration');
  }
  if (allText.includes('data') || allText.includes('metrics') || allText.includes('analytics')) {
    strengths.push('data-driven decision making');
  }

  // Get unique companies applied to
  const companiesApplied = [...new Set(qaHistory.map(e => e.company).filter(Boolean))];

  return {
    primaryDomain,
    secondaryDomain,
    careerTrajectory,
    strengths: strengths.length > 0 ? strengths : ['See resume for details'],
    achievements,
    companiesApplied,
    totalApplications: qaHistory.length,
    background: `${primaryDomain}${secondaryDomain ? ` with ${secondaryDomain} experience` : ''}`
  };
};

// Import user data (async - saves to database)
const importUserData = async (data, authToken, apiUrl) => {
  try {
    // Import master resume if present
    if (data.masterResume) {
      const masterResumeResponse = await fetch(`${apiUrl}/api/user/master-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          content: JSON.stringify(data.masterResume)
        })
      });

      if (!masterResumeResponse.ok) {
        const errorText = await masterResumeResponse.text();
        console.error('Master resume save failed:', errorText);
        throw new Error(`Failed to save master resume: ${errorText}`);
      }

    }

    // Sync Q&A history and resume library to database
    if (data.qaHistory || data.resumeLibrary) {
      const syncResponse = await fetch(`${apiUrl}/api/user/sync`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          qaHistory: data.qaHistory || [],
          resumeLibrary: data.resumeLibrary || []
        })
      });

      if (!syncResponse.ok) {
        const errorText = await syncResponse.text();
        console.error('Sync failed:', errorText);
        throw new Error(`Failed to sync data: ${errorText}`);
      }

    }

    return true;
  } catch (error) {
    console.error('Error importing user data:', error);
    throw error;
  }
};


// ============================================================================

// Empty template for new users
const EMPTY_RESUME_TEMPLATE = {
  name: "",
  contact: { phone: "", linkedin: "", email: "" },
  summary: "",
  competencies: "",
  experience: [],
  education: { degree: "", school: "" },
  skills: ""
};

// Function to parse AI-generated resume text into structured data
function parseResumeText(text) {
  const lines = text.split('\n');
  const parsed = {
    name: "",
    contact: { phone: "", linkedin: "", email: "" },
    summary: "",
    competencies: "",
    experience: [],
    education: { degree: "", school: "" },
    skills: ""
  };

  let currentSection = null;
  let currentJob = null;
  let summaryLines = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i].trim();

    if (!line) continue;

    // Detect name (first non-empty line or line starting with "Name:")
    if (line.startsWith('Name:')) {
      parsed.name = line.replace('Name:', '').trim();
      continue;
    }
    if (!parsed.name && line && !line.includes('|') && !line.includes('Contact:')) {
      parsed.name = line;
      continue;
    }

    // Detect contact info
    if (line.startsWith('Contact:')) {
      const contactStr = line.replace('Contact:', '').trim();
      const parts = contactStr.split('|').map(p => p.trim());
      parts.forEach(part => {
        if (part.includes('@')) parsed.contact.email = part;
        else if (part.toLowerCase().includes('linkedin')) parsed.contact.linkedin = part;
        else if (part && !parsed.contact.phone) parsed.contact.phone = part;
      });
      continue;
    }
    if (line.includes('|') && !currentSection && parsed.name && !parsed.contact.phone) {
      const parts = line.split('|').map(p => p.trim());
      parts.forEach(part => {
        if (part.includes('@')) parsed.contact.email = part;
        else if (part.toLowerCase().includes('linkedin')) parsed.contact.linkedin = part;
        else if (part && !parsed.contact.phone) parsed.contact.phone = part;
      });
      continue;
    }
    
    // Detect sections - be strict about section headers (must be primarily a header, not content with keywords)
    const upperLine = line.toUpperCase();
    const isSectionHeader = (keywords, line) => {
      // A section header should be short (under 60 chars) and primarily consist of the keyword
      if (line.length > 60) return false;
      // Check if the line is mostly the section name (allowing for some formatting)
      const cleanLine = line.replace(/[^A-Z\s]/gi, '').trim();
      return keywords.some(kw => cleanLine.toUpperCase().includes(kw.replace(/[^A-Z\s]/gi, '').trim()));
    };

    if (isSectionHeader(['EXECUTIVE SUMMARY', 'SUMMARY'], upperLine)) {
      currentSection = 'summary';
      continue;
    }
    if (isSectionHeader(['PROFESSIONAL EXPERIENCE', 'WORK EXPERIENCE', 'EXPERIENCE'], upperLine) && !upperLine.includes('YEARS')) {
      currentSection = 'experience';
      continue;
    }
    if (isSectionHeader(['EDUCATION'], upperLine)) {
      currentSection = 'education';
      continue;
    }
    // Skills section - must be a clear header, not a job title with "Technical" in it
    if (isSectionHeader(['TECHNICAL & METHODOLOGICAL SKILLS', 'TECHNICAL SKILLS', 'SKILLS'], upperLine) &&
        !upperLine.includes('|') && !line.startsWith('•') && !line.startsWith('-')) {
      currentSection = 'skills';
      continue;
    }

    // Detect Core Competencies as section header (AI sometimes outputs this as a header)
    if (isSectionHeader(['CORE COMPETENCIES', 'KEY COMPETENCIES', 'COMPETENCIES'], upperLine) &&
        !upperLine.includes('|') && line.length < 30) {
      // This is a header line, next line will have the actual competencies
      currentSection = 'competencies';
      continue;
    }

    // Parse content based on current section
    if (currentSection === 'competencies') {
      // This line contains the competencies list
      if (line.includes('|')) {
        parsed.competencies = line.trim();
        currentSection = null;
      }
      continue;
    }

    if (currentSection === 'summary') {
      // Check for Core Competencies line (case-insensitive, with or without colon)
      const lowerLine = line.toLowerCase();
      if (lowerLine.startsWith('core competencies:') || lowerLine.startsWith('core competencies ')) {
        // Extract competencies - remove prefix
        const colonIndex = line.indexOf(':');
        if (colonIndex > 0) {
          parsed.competencies = line.substring(colonIndex + 1).trim();
        } else {
          // No colon, find where the list starts (after "Core Competencies")
          const prefixMatch = line.match(/^core\s+competencies\s*/i);
          if (prefixMatch) {
            parsed.competencies = line.substring(prefixMatch[0].length).trim();
          }
        }
        currentSection = null;
      } else if (upperLine.includes('CORE COMPETENCIES') && line.includes('|')) {
        // Core Competencies embedded mid-line - extract and truncate summary
        const coreIndex = line.toUpperCase().indexOf('CORE COMPETENCIES');
        // Add everything before "CORE COMPETENCIES" to summary
        const beforeCore = line.substring(0, coreIndex).trim();
        if (beforeCore) {
          summaryLines.push(beforeCore);
        }
        // Extract competencies (everything after the prefix)
        const afterCore = line.substring(coreIndex);
        const colonIndex = afterCore.indexOf(':');
        if (colonIndex > 0) {
          parsed.competencies = afterCore.substring(colonIndex + 1).trim();
        } else {
          // Find where list starts (has |)
          const listStart = afterCore.search(/[A-Z][a-z]/);
          if (listStart > 0) {
            parsed.competencies = afterCore.substring(listStart).trim();
          }
        }
        currentSection = null;
      } else {
        summaryLines.push(line);
      }
    } else if (currentSection === 'experience') {
      // Check if it's a company line (has " – " or " - " but NOT a date pattern and NOT a bullet)
      // Company line format: "Company Name – Location" or "Company Name - Location"
      const hasDash = line.includes(' – ') || line.includes(' - ');
      const hasDatePattern = line.includes('|') && (line.match(/\b20\d{2}\b/) || line.includes('Present'));
      const isBullet = line.startsWith('•') || line.startsWith('-');

      if (hasDash && !hasDatePattern && !isBullet && !line.includes('|')) {
        // This is a company/location line - start a new job entry
        if (currentJob && currentJob.title) {
          parsed.experience.push(currentJob);
        }
        const dashIndex = line.includes(' – ') ? line.indexOf(' – ') : line.indexOf(' - ');
        const dashLength = line.includes(' – ') ? 3 : 3;
        currentJob = {
          company: line.substring(0, dashIndex).trim(),
          location: line.substring(dashIndex + dashLength).trim() || '',
          title: '',
          dates: '',
          achievements: []
        };
      } else if (line.includes('|') && (line.match(/\b20\d{2}\b/) || line.includes('Present'))) {
        // This is a title/dates line: "Title | Dates"
        const parts = line.split('|');
        if (currentJob) {
          currentJob.title = parts[0].trim();
          currentJob.dates = parts[1].trim();
        } else {
          // If no currentJob yet, create one (fallback)
          currentJob = {
            company: '',
            location: '',
            title: parts[0].trim(),
            dates: parts[1].trim(),
            achievements: []
          };
        }
      } else if (line.startsWith('•') || line.startsWith('-')) {
        // Achievement bullet
        if (currentJob) {
          const bulletText = line.substring(1).trim();
          if (bulletText) {
            currentJob.achievements.push(bulletText);
          }
        }
      } else if (currentJob) {
        // Unrecognized line in experience section — treat as achievement text
        currentJob.achievements.push(line);
      }
    } else if (currentSection === 'education') {
      // Parse education - handle multiple formats
      if (line.includes('|')) {
        // Format: "Degree | School" or "Degree | School | Year"
        const parts = line.split('|').map(p => p.trim());
        parsed.education.degree = parts[0] || "";
        parsed.education.school = parts[1] || "";
      } else if (line.includes(' – ') || line.includes(' - ')) {
        // Format: "School – Location" or "Degree - School"
        const dashIndex = line.includes(' – ') ? line.indexOf(' – ') : line.indexOf(' - ');
        const firstPart = line.substring(0, dashIndex).trim();
        const secondPart = line.substring(dashIndex + 3).trim();
        // If we already have a degree, this is likely school/location
        if (parsed.education.degree && !parsed.education.school) {
          parsed.education.school = firstPart + (secondPart ? ', ' + secondPart : '');
        } else {
          parsed.education.degree = firstPart;
          parsed.education.school = secondPart;
        }
      } else if (!parsed.education.degree) {
        // First line without separator - likely the degree
        parsed.education.degree = line;
      } else if (!parsed.education.school) {
        // Second line without separator - likely the school
        parsed.education.school = line;
      } else {
        // Additional info - append to school
        parsed.education.school += ', ' + line;
      }
    } else if (currentSection === 'skills') {
      // Only stop collecting skills if we detect a job-like line (not bullets - skills often use bullets)
      const isJobLine = line.includes('|') && (line.match(/\b20\d{2}\b/) || line.includes('Present'));
      const isCompanyLine = (line.includes(' – ') || line.includes(' - ')) && !line.includes('|');

      if (isJobLine || isCompanyLine) {
        currentSection = null;
      } else {
        // Strip bullet prefix if present, then add to skills
        const skillText = (line.startsWith('•') || line.startsWith('-')) ? line.substring(1).trim() : line;
        if (skillText) {
          parsed.skills += skillText + ' ';
        }
      }
    }
  }

  // Add last job (only if it has at least a title or company)
  if (currentJob && (currentJob.title || currentJob.company)) {
    parsed.experience.push(currentJob);
  }

  // Join summary and clean up any embedded Core Competencies
  let summary = summaryLines.join(' ').trim();

  // Remove "Core Competencies" section if embedded in summary (common AI formatting issue)
  // Matches: "Core Competencies Product Management | ..." or "CORE COMPETENCIES: ..." (case-insensitive)
  const upperSummary = summary.toUpperCase();
  const coreCompIndex = upperSummary.indexOf('CORE COMPETENCIES');
  if (coreCompIndex > 0) {
    // Only strip if it appears to be a list (contains | separator after it)
    const afterCore = summary.substring(coreCompIndex);
    if (afterCore.includes('|')) {
      summary = summary.substring(0, coreCompIndex).trim();
      // If we haven't extracted competencies yet, do it now
      if (!parsed.competencies) {
        const colonIndex = afterCore.indexOf(':');
        if (colonIndex > 0 && colonIndex < 20) {
          parsed.competencies = afterCore.substring(colonIndex + 1).trim();
        } else {
          // Find where the list starts
          const listMatch = afterCore.match(/[A-Z][a-z]/);
          if (listMatch) {
            parsed.competencies = afterCore.substring(listMatch.index).trim();
          }
        }
      }
    }
  }

  parsed.summary = summary;
  parsed.skills = parsed.skills.trim();

  return parsed;
}

// Function to generate .docx file from resume data
async function generateDocx(resumeData, masterResume) {
  const isMaster = resumeData === masterResume;
  const data = isMaster ? resumeData : parseResumeText(resumeData);
  
  const doc = new Document({
    styles: {
      default: {
        document: {
          run: { font: "Calibri", size: 22 }
        }
      },
      paragraphStyles: [
        {
          id: "Heading1",
          name: "Heading 1",
          basedOn: "Normal",
          next: "Normal",
          quickFormat: true,
          run: { size: 28, bold: true, font: "Calibri", color: "1F4788" },
          paragraph: { spacing: { before: 240, after: 120 }, outlineLevel: 0 }
        }
      ]
    },
    numbering: {
      config: [
        {
          reference: "bullets",
          levels: [
            {
              level: 0,
              format: LevelFormat.BULLET,
              text: "•",
              alignment: AlignmentType.LEFT,
              style: {
                paragraph: {
                  indent: { left: 720, hanging: 360 }
                }
              }
            }
          ]
        }
      ]
    },
    sections: [{
      properties: {
        page: {
          size: { width: 12240, height: 15840 },
          margin: { top: 720, right: 720, bottom: 720, left: 720 }
        }
      },
      children: [
        // Name
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 100 },
          children: [
            new TextRun({
              text: isMaster ? data.name : data.name || masterResume.name,
              bold: true,
              size: 32,
              font: "Calibri"
            })
          ]
        }),
        // Contact
        new Paragraph({
          alignment: AlignmentType.CENTER,
          spacing: { after: 240 },
          children: [
            new TextRun({
              text: (() => {
                // For master resume or if tailored resume has contact info
                const contact = data.contact || {};
                const hasContact = contact.phone || contact.linkedin || contact.email;
                if (isMaster || hasContact) {
                  return `${contact.phone || masterResume.contact.phone} | ${contact.linkedin || masterResume.contact.linkedin} | ${contact.email || masterResume.contact.email}`;
                }
                // Fall back to master resume contact
                return `${masterResume.contact.phone} | ${masterResume.contact.linkedin} | ${masterResume.contact.email}`;
              })(),
              size: 20,
              font: "Calibri"
            })
          ]
        }),
        // Executive Summary Header
        new Paragraph({
          heading: HeadingLevel.HEADING_1,
          children: [new TextRun("EXECUTIVE SUMMARY")]
        }),
        // Summary Text (clean any embedded Core Competencies)
        new Paragraph({
          spacing: { after: 200 },
          children: [
            new TextRun({
              text: (() => {
                let summary = data.summary || masterResume.summary;
                // Remove embedded Core Competencies if present
                const coreCompIndex = summary.indexOf('Core Competencies');
                if (coreCompIndex > 0 && summary.substring(coreCompIndex).includes('|')) {
                  summary = summary.substring(0, coreCompIndex).trim();
                }
                return summary;
              })(),
              size: 22
            })
          ]
        }),
        // Core Competencies
        new Paragraph({
          spacing: { after: 240 },
          children: [
            new TextRun({
              text: "Core Competencies: ",
              bold: true,
              size: 22
            }),
            new TextRun({
              text: data.competencies || masterResume.competencies,
              size: 22
            })
          ]
        }),
        // Professional Experience Header
        new Paragraph({
          heading: HeadingLevel.HEADING_1,
          children: [new TextRun("PROFESSIONAL EXPERIENCE")]
        }),
        // Experience entries
        ...(isMaster ? data.experience : (data.experience.length > 0 ? data.experience : masterResume.experience)).flatMap((exp, idx) => {
          const elements = [];

          // Skip entries that have no company AND no title (incomplete entries)
          if (!exp.company && !exp.title) {
            return elements;
          }

          // Company and location (only if company exists)
          if (exp.company) {
            const companyChildren = [
              new TextRun({
                text: exp.company,
                bold: true,
                size: 24
              })
            ];
            // Only add location if it exists and is not empty
            if (exp.location) {
              companyChildren.push(new TextRun({
                text: ` – ${exp.location}`,
                size: 22,
                italics: true
              }));
            }
            elements.push(new Paragraph({
              spacing: { after: 60 },
              children: companyChildren
            }));
          }

          // Title and dates (only if title exists)
          if (exp.title) {
            const titleChildren = [
              new TextRun({
                text: exp.title,
                bold: true,
                size: 22
              })
            ];
            // Only add dates if they exist
            if (exp.dates) {
              titleChildren.push(new TextRun({
                text: ` | ${exp.dates}`,
                size: 22
              }));
            }
            elements.push(new Paragraph({
              spacing: { after: 120 },
              children: titleChildren
            }));
          }
          
          // Achievements
          exp.achievements.forEach((achievement, achIdx) => {
            elements.push(new Paragraph({
              numbering: { reference: "bullets", level: 0 },
              spacing: { after: achIdx === exp.achievements.length - 1 ? 200 : 100 },
              children: [
                new TextRun({
                  text: achievement,
                  size: 22
                })
              ]
            }));
          });
          
          return elements;
        }),
        // Education Header
        new Paragraph({
          heading: HeadingLevel.HEADING_1,
          children: [new TextRun("EDUCATION")]
        }),
        // Education content
        new Paragraph({
          spacing: { after: 240 },
          children: [
            new TextRun({
              text: (() => {
                if (isMaster) {
                  return `${data.education.degree} | ${data.education.school}`;
                }
                // For parsed resumes, education is an object with degree and school
                const edu = data.education;
                if (edu && (edu.degree || edu.school)) {
                  return edu.school ? `${edu.degree} | ${edu.school}` : edu.degree;
                }
                // Fallback to master resume education
                return `${masterResume.education.degree} | ${masterResume.education.school}`;
              })(),
              size: 22
            })
          ]
        }),
        // Skills Header
        new Paragraph({
          heading: HeadingLevel.HEADING_1,
          children: [new TextRun("TECHNICAL & METHODOLOGICAL SKILLS")]
        }),
        // Skills content
        new Paragraph({
          children: [
            new TextRun({
              text: data.skills || masterResume.skills,
              size: 22
            })
          ]
        })
      ]
    }]
  });
  
  return await Packer.toBlob(doc);
}

function App() {
  const posthog = usePostHog();
  
  // Set window.posthog for backward compatibility with existing code
  useEffect(() => {
    if (posthog) {
      window.posthog = posthog;
    }
  }, [posthog]);

  // Auth state
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem('authToken'));
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);

  const [view, setView] = useState('dashboard'); // Dashboard is home - the command center
  const [showSettingsDrawer, setShowSettingsDrawer] = useState(false);

  // Cached job search state (persists across view changes)
  const [cachedJobSearch, setCachedJobSearch] = useState({
    jobs: [],
    searchStats: null,
    selectedRole: '',
    customRole: '',
    location: '',
    includeRemote: true,
  });
  const [jobDescription, setJobDescription] = useState('');
  const [tailoredResume, setTailoredResume] = useState(null);
  const [changesSummary, setChangesSummary] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [generationProgress, setGenerationProgress] = useState(0);
  const [generationStep, setGenerationStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [downloadDropdown, setDownloadDropdown] = useState(null); // 'master' | 'tailored' | null
  const [error, setError] = useState('');
  const [uploadedResume, setUploadedResume] = useState(null);
  const [useUploadedResume, setUseUploadedResume] = useState(false);

  // Questionnaire state
  const [questions, setQuestions] = useState([]);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState(1);
  const [showQuestions, setShowQuestions] = useState(false);
  const [isSuggestingAnswer, setIsSuggestingAnswer] = useState(false);
  const [suggestingAnswerIndex, setSuggestingAnswerIndex] = useState(null);

  // Persistence state
  const [qaHistory, setQaHistory] = useState([]);
  const [resumeLibrary, setResumeLibrary] = useState([]);
  const [uploadSuccess, setUploadSuccess] = useState(false);

  // Application tracking state
  const [extractedCompany, setExtractedCompany] = useState('');
  const [extractedRole, setExtractedRole] = useState('');
  const [extractedLocation, setExtractedLocation] = useState('');
  const [showJobDetailsPrompt, setShowJobDetailsPrompt] = useState(false);
  const [isExtractingCompany, setIsExtractingCompany] = useState(false);
  const [currentApplicationId, setCurrentApplicationId] = useState(null);

  // Applications page filter (set by Dashboard/OfferComparison navigation)
  const [filterStatus, setFilterStatus] = useState('all');

  // UI state
  const [showJobDescModal, setShowJobDescModal] = useState(false);
  const [modalJobDescription, setModalJobDescription] = useState('');
  const [validationError, setValidationError] = useState('');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isResettingData, setIsResettingData] = useState(false);
  const [resetDataError, setResetDataError] = useState('');

  // Billing / paywall state (credits-based)
  const [billingStatus, setBillingStatus] = useState('free');
  const [creditsUsed, setCreditsUsed] = useState(0);
  const [freeLimit, setFreeLimit] = useState(5);
  const [paidLimit, setPaidLimit] = useState(20);
  const [paidCredits, setPaidCredits] = useState(0); // Total purchased credits
  const [priceDisplay, setPriceDisplay] = useState('$4.99');
  const [showPaywall, setShowPaywall] = useState(false);
  const [isPaying, setIsPaying] = useState(false);
  const [paymentError, setPaymentError] = useState('');

  // Master resume state
  const [masterResume, setMasterResume] = useState(EMPTY_RESUME_TEMPLATE);
  const [hasMasterResume, setHasMasterResume] = useState(false);
  const [isLoadingMasterResume, setIsLoadingMasterResume] = useState(false);
  const [isEditingMasterResume, setIsEditingMasterResume] = useState(false);
  const [editedMasterResumeText, setEditedMasterResumeText] = useState('');
  const [isSavingMasterResume, setIsSavingMasterResume] = useState(false);

  // Resume history state
  const [resumeHistory, setResumeHistory] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [showResumeHistory, setShowResumeHistory] = useState(false);

  // Onboarding state
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [onboardingResumeText, setOnboardingResumeText] = useState('');
  const [onboardingUploading, setOnboardingUploading] = useState(false);

  // Wizard state for funnel flow
  const [wizardStep, setWizardStep] = useState(1); // 1: Job Details, 2: Questions, 3: Generate, 4: Review
  const [wizardProgress, setWizardProgress] = useState({
    jobDescriptionAdded: false,
    questionsAnswered: false,
    resumeGenerated: false
  });
  const [showSuccessCelebration, setShowSuccessCelebration] = useState(false);
  const [showAppliedCelebration, setShowAppliedCelebration] = useState(null); // { company, appliedCount }
  const [lastFailedAction, setLastFailedAction] = useState(null); // 'analyze' | 'generate' | null
  const [markedApplied, setMarkedApplied] = useState(false);
  const [hasDownloaded, setHasDownloaded] = useState(false);
  const [inlineResumeText, setInlineResumeText] = useState('');
  const [isParsingInlineResume, setIsParsingInlineResume] = useState(false);

  const billingReturnStatus = window.location.pathname === '/billing/success'
    ? 'success'
    : window.location.pathname === '/billing/cancel'
      ? 'cancel'
      : null;

  // Application questions state (for company-specific application questions)
  const [appQuestions, setAppQuestions] = useState([{ question: '', charLimit: 500, response: '' }]);
  const [isGeneratingAppResponse, setIsGeneratingAppResponse] = useState(false);
  const [generatingQuestionIndex, setGeneratingQuestionIndex] = useState(null);

  // Enhanced tailoring state
  const [jobAnalysis, setJobAnalysis] = useState(null);
  const [qualityMetrics, setQualityMetrics] = useState(null);
  const [isAnalyzingJob, setIsAnalyzingJob] = useState(false);
  const [isOptimizingResume, setIsOptimizingResume] = useState(false);
  const [optimizeError, setOptimizeError] = useState('');
  const [optimizeResultModal, setOptimizeResultModal] = useState({ show: false, enhancements: '' });
  const [reviewTab, setReviewTab] = useState('preview');

  // Ref for scrolling to sections
  const questionsRef = useRef(null);
  const generateRef = useRef(null);

  // Check authentication on mount
  useEffect(() => {
    const checkAuth = async () => {
      const token = localStorage.getItem('authToken');
      const savedUser = localStorage.getItem('user');

      if (token && savedUser) {
        try {
          // Verify token is still valid
          const response = await fetch(`${API_URL}/api/auth/verify`, {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });

          if (response.ok) {
            const parsedUser = JSON.parse(savedUser);
            setAuthToken(token);
            setUser(parsedUser);
            setIsAuthenticated(true);
            identifyUser(parsedUser.id, { email: parsedUser.email });
          } else {
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
          }
        } catch (error) {
          captureError('auth_check_failed', error);
          localStorage.removeItem('authToken');
          localStorage.removeItem('user');
        }
      }
      setIsCheckingAuth(false);
    };

    checkAuth();
  }, []);

  // Load user data from database when authenticated
  useEffect(() => {
    if (isAuthenticated && authToken) {
      loadUserData();
    }
  }, [isAuthenticated, authToken]);

  // Animate generation progress when isGenerating is true
  useEffect(() => {
    if (isGenerating) {
      setGenerationProgress(0);
      setGenerationStep(1);

      const interval = setInterval(() => {
        setGenerationProgress(prev => {
          // Slow down as we approach 95%
          const remaining = 95 - prev;
          const increment = Math.max(0.5, remaining * 0.03);
          const newProgress = Math.min(95, prev + increment);

          // Update step based on progress
          if (newProgress >= 66) {
            setGenerationStep(3);
          } else if (newProgress >= 33) {
            setGenerationStep(2);
          }

          return newProgress;
        });
      }, 500);

      return () => clearInterval(interval);
    } else {
      // Reset when done
      setGenerationProgress(0);
      setGenerationStep(1);
    }
  }, [isGenerating]);

  // Rotating progress messages for loading states
  const ANALYSIS_MESSAGES = [
    'Reading the job description...',
    'Extracting key requirements...',
    'Generating personalized questions...',
  ];
  const GENERATION_MESSAGES = [
    'Matching your experience to requirements...',
    'Optimizing for ATS keywords...',
    'Tailoring your achievements...',
    'Formatting your resume...',
    'Running quality checks...',
  ];
  const [progressMessage, setProgressMessage] = useState('');

  useEffect(() => {
    if (!isAnalyzing && !isGenerating) {
      setProgressMessage('');
      return;
    }
    const messages = isGenerating ? GENERATION_MESSAGES : ANALYSIS_MESSAGES;
    let idx = 0;
    setProgressMessage(messages[0]);
    const interval = setInterval(() => {
      idx = (idx + 1) % messages.length;
      setProgressMessage(messages[idx]);
    }, 2500);
    return () => clearInterval(interval);
  }, [isAnalyzing, isGenerating]);

  const refreshBillingStatus = async () => {
    if (!authToken) return;
    try {
      const res = await fetch(`${API_URL}/api/billing/status`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });
      if (res.ok) {
        const data = await res.json();
        setBillingStatus(data.billingStatus || 'free');
        setCreditsUsed(data.creditsUsed || 0);
        setFreeLimit(data.freeLimit || 5);
        if (typeof data.paidLimit === 'number') {
          setPaidLimit(data.paidLimit);
        }
        if (typeof data.paidCredits === 'number') {
          setPaidCredits(data.paidCredits);
        }
        if (data.price?.display) {
          setPriceDisplay(data.price.display);
        }
      }
    } catch (err) {
      console.error('Error loading billing status:', err);
    }
  };

  // Close download dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (downloadDropdown && !e.target.closest('.relative')) {
        setDownloadDropdown(null);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [downloadDropdown]);

  const loadUserData = async () => {
    try {
      setIsLoadingMasterResume(true);
      await refreshBillingStatus();

      // Load master resume first
      const masterResumeResponse = await fetch(`${API_URL}/api/user/master-resume`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (masterResumeResponse.ok) {
        const { resume } = await masterResumeResponse.json();
        console.log('[LoadUserData] Got resume from DB:', { hasResume: !!resume, hasContent: !!resume?.content, contentLength: resume?.content?.length, resumeId: resume?.id });
        if (resume && resume.content) {
          try {
            const parsedResume = JSON.parse(resume.content);
            console.log('[LoadUserData] Parsed resume:', { name: parsedResume.name, experienceCount: parsedResume.experience?.length });
            setMasterResume(parsedResume);
            setHasMasterResume(true);
          } catch (parseError) {
            console.error('[LoadUserData] Error parsing master resume:', parseError, 'Content preview:', resume.content?.substring(0, 100));
            setHasMasterResume(false);
            setShowOnboarding(true);
          }
        } else {
          console.log('[LoadUserData] No resume found');
          // No master resume found - show onboarding
          setHasMasterResume(false);
          setShowOnboarding(true);
        }
      } else {
        console.log('[LoadUserData] Response not ok:', masterResumeResponse.status);
        setHasMasterResume(false);
        setShowOnboarding(true);
      }

      setIsLoadingMasterResume(false);

      // Load applications
      const appsResponse = await fetch(`${API_URL}/api/user/applications`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (appsResponse.ok) {
        const { applications } = await appsResponse.json();
        // Map database fields to match existing format
        const mappedApps = applications.map(app => ({
          id: app.id,
          company: app.company,
          role: app.role,
          jobDescription: app.job_description,
          resume: app.tailored_resume,
          changesSummary: app.changes_summary,
          status: app.status === 'interviewing' ? 'interview' : app.status,
          appliedDate: app.applied_date,
          timestamp: app.created_at,
          archived: app.archived ?? false,
          notes: app.notes,
          qualityScore: app.quality_score,
          atsScore: app.ats_score,
          beforeQualityScore: app.before_quality_score,
          beforeAtsScore: app.before_ats_score,
          keywordsFound: app.keywords_found,
          strengths: app.strengths,
          interviewPrep: app.interview_prep,
          salaryRangeMin: app.salary_range_min ?? '',
          salaryRangeMax: app.salary_range_max ?? '',
          location: app.location || '',
          statusHistory: (app.application_status_history || [])
            .sort((a, b) => new Date(a.changed_at) - new Date(b.changed_at))
            .map(h => ({
              fromStatus: h.from_status === 'interviewing' ? 'interview' : h.from_status,
              toStatus: h.to_status === 'interviewing' ? 'interview' : h.to_status,
              source: h.source,
              changedAt: h.changed_at
            }))
        }));
        setResumeLibrary(mappedApps);
      }

      // Load Q&A history
      const qaResponse = await fetch(`${API_URL}/api/user/qa-history`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (qaResponse.ok) {
        const { history } = await qaResponse.json();
        // Map database fields
        const mappedHistory = history.map(entry => ({
          id: entry.id,
          company: entry.company,
          jobDescription: entry.job_description_snippet,
          questions: entry.questions,
          answers: entry.answers,
          timestamp: entry.created_at
        }));
        setQaHistory(mappedHistory);
      }
    } catch (error) {
      console.error('Error loading user data:', error);
      setIsLoadingMasterResume(false);
    }
  };

  useEffect(() => {
    if (!isAuthenticated || !authToken) return;

    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get('session_id');
    if (!sessionId) return;

    const confirmCheckout = async () => {
      try {
        const res = await fetch(`${API_URL}/api/billing/confirm`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({ sessionId })
        });
        const data = await res.json();
        if (!res.ok) {
          throw new Error(data.error || 'Failed to confirm payment');
        }
        await refreshBillingStatus();
        setShowPaywall(false);
      } catch (err) {
        setError(err.message || 'Failed to confirm payment');
      } finally {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    };

    confirmCheckout();
  }, [isAuthenticated, authToken]);

  const handleAuthSuccess = async (token, userData) => {
    setAuthToken(token);
    setUser(userData);
    setIsAuthenticated(true);
    await refreshBillingStatus();

    // Check if there's localStorage data to migrate
    const localQA = loadQAHistory();
    const localLibrary = loadResumeLibrary();

    if (localQA.length > 0 || localLibrary.length > 0) {
      try {
        await fetch(`${API_URL}/api/user/sync`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            qaHistory: localQA,
            resumeLibrary: localLibrary
          })
        });

        // Clear localStorage after successful sync
        localStorage.removeItem(STORAGE_KEYS.QA_HISTORY);
        localStorage.removeItem(STORAGE_KEYS.RESUME_LIBRARY);
      } catch (error) {
        console.error('Error syncing data:', error);
      }
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('authToken');
    localStorage.removeItem('user');
    setAuthToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setQaHistory([]);
    setResumeLibrary([]);
    setBillingStatus('free');
    setCreditsUsed(0);
    setFreeLimit(5);
    setPaidLimit(20);
    setIsResettingData(false);
    setResetDataError('');
    // Clear tailored resume to prevent data leakage between users
    setTailoredResume(null);
    setChangesSummary('');
    setView('dashboard');
  };

  // Billing return pages (Stripe success/cancel)
  if (billingReturnStatus) {
    return <BillingReturnPage status={billingReturnStatus} />;
  }

  // Show auth screen if not authenticated
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-primary border-t-transparent"></div>
      </div>
    );
  }

  // FAQ page route (public)
  if (!isAuthenticated && window.location.pathname === '/faq') {
    return <FAQPage />;
  }

  if (!isAuthenticated) {
    return <LandingPage onAuthSuccess={handleAuthSuccess} />;
  }

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['text/plain', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];

    if (!allowedTypes.includes(file.type)) {
      setValidationError('Please upload a .txt, .pdf, or .docx file');
      event.target.value = '';
      return;
    }

    setIsAnalyzing(true);
    setValidationError('');
    setUploadSuccess(false);

    try {
      // Create FormData to send the file
      const formData = new FormData();
      formData.append('resume', file);

      // Upload to backend
      const response = await fetch(`${API_URL}/api/upload-resume`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${authToken}`
        },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload resume');
      }

      const data = await response.json();
      console.log('[Upload] Backend response:', { hasResumeText: !!data.resumeText, resumeTextLength: data.resumeText?.length });

      // Parse the returned resume
      const parsedResume = parseResumeText(data.resumeText);
      console.log('[Upload] Parsed resume:', { name: parsedResume.name, experienceCount: parsedResume.experience?.length });

      // Save to database - only update UI on success
      const saved = await saveMasterResume(parsedResume);
      console.log('[Upload] Save result:', saved);

      if (!saved) {
        setValidationError('Failed to save resume to your account. Please try again.');
        return;
      }

      setValidationError('');
      setUploadSuccess(true);

      // Track analytics
      if (window.posthog) {
        posthog.capture('resume_uploaded', {
          file_type: file.type,
          file_size_kb: Math.round(file.size / 1024)
        });
      }

      // Hide success message after 5 seconds
      setTimeout(() => setUploadSuccess(false), 5000);
    } catch (error) {
      console.error('Error uploading resume:', error);
      setValidationError(error.message || 'Failed to upload resume');
    } finally {
      setIsAnalyzing(false);
      event.target.value = '';
    }
  };

  // Check if user has real resume content (not empty template)
  const hasMasterResumeContent = () => {
    if (useUploadedResume && uploadedResume) return true;
    return !!(masterResume.name && masterResume.name.trim() && masterResume.experience && masterResume.experience.length > 0);
  };

  const handleInlineResumePaste = async () => {
    if (!inlineResumeText.trim()) return;
    setIsParsingInlineResume(true);
    setError('');
    try {
      const response = await fetch(`${API_URL}/api/parse-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ resumeText: inlineResumeText })
      });
      if (!response.ok) throw new Error('Failed to parse resume');
      const data = await response.json();
      const parsedResume = parseResumeText(data.parsedResume);
      await saveMasterResume(parsedResume);
      setInlineResumeText('');
      if (window.posthog) {
        posthog.capture('resume_added_inline_tailor', { source: 'tailor_gate' });
      }
    } catch (err) {
      setError('Failed to parse your resume. Please try again.');
    } finally {
      setIsParsingInlineResume(false);
    }
  };

  // Inline file upload handler for tailor view gate
  const handleInlineFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;
    const allowedTypes = ['text/plain', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a .txt, .pdf, or .docx file');
      event.target.value = '';
      return;
    }
    setIsParsingInlineResume(true);
    setError('');
    try {
      const formData = new FormData();
      formData.append('resume', file);
      const response = await fetch(`${API_URL}/api/upload-resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload resume');
      }
      const data = await response.json();
      const parsedResume = parseResumeText(data.parsedResume);
      await saveMasterResume(parsedResume);
      if (window.posthog) {
        posthog.capture('resume_uploaded_inline_tailor', { source: 'tailor_gate', file_type: file.type });
      }
    } catch (err) {
      setError(err.message || 'Failed to upload resume');
    } finally {
      setIsParsingInlineResume(false);
      event.target.value = '';
    }
  };

  const analyzeJobDescription = async () => {
    if (!jobDescription.trim()) {
      setValidationError('Please paste a job description first');
      return;
    }

    // Early paywall check — don't waste user's time analyzing if they can't generate
    const totalCreditsAvailable = freeLimit + paidCredits;
    if (creditsUsed >= totalCreditsAvailable) {
      setShowPaywall(true);
      if (window.posthog) {
        posthog.capture('paywall_shown', {
          credits_used: creditsUsed,
          total_credits: totalCreditsAvailable,
          trigger: 'analyze_job_early'
        });
      }
      return;
    }

    setIsAnalyzing(true);
    setAnalysisStep(1);
    setIsExtractingCompany(true);
    setIsAnalyzingJob(true);
    setError('');
    setValidationError('');
    setLastFailedAction(null);
    setJobAnalysis(null);
    setQualityMetrics(null);

    try {
      // Analyze job description (includes company extraction)
      const analysisResponse = await fetch(`${API_URL}/api/analyze-job`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`
        },
        body: JSON.stringify({ jobDescription })
      });

      setIsExtractingCompany(false);
      setAnalysisStep(2);

      // Process job analysis for ATS optimization and company extraction
      let analysis = null;
      let companyFound = false;
      let roleFound = false;
      if (analysisResponse.ok) {
        const analysisData = await analysisResponse.json();
        analysis = analysisData.analysis || analysisData;
        console.log('[DEBUG] Job analysis received:', {
          hasAtsKeywords: !!analysis?.atsKeywords,
          atsKeywordsCount: analysis?.atsKeywords?.length || 0,
          hasMustHaveSkills: !!analysis?.mustHaveSkills,
          mustHaveSkillsCount: analysis?.mustHaveSkills?.length || 0,
          company: analysis?.company,
          role: analysis?.role
        });
        setJobAnalysis(analysis);
        // Extract company and role from analysis
        if (analysis?.company && analysis.company !== 'Unknown' && analysis.company !== 'Unknown Company') {
          setExtractedCompany(analysis.company);
          companyFound = true;
        } else {
          setExtractedCompany('');
        }
        if (analysis?.role && analysis.role !== 'Unknown' && analysis.role !== 'Unknown Role') {
          setExtractedRole(analysis.role);
          roleFound = true;
        } else {
          setExtractedRole('');
        }
        setExtractedLocation(analysis?.location || '');
        // Show prompt if either is missing
        if (!companyFound || !roleFound) {
          setShowJobDetailsPrompt(true);
        }
      } else {
        // Analysis API failed - log for debugging intermittent keyword issues
        console.warn('[DEBUG] Job analysis API failed:', {
          status: analysisResponse.status,
          statusText: analysisResponse.statusText
        });
        // Try to get error details
        try {
          const errorData = await analysisResponse.json();
          console.warn('[DEBUG] Analysis error details:', errorData);
        } catch (e) {
          console.warn('[DEBUG] Could not parse analysis error response');
        }
      }
      setIsAnalyzingJob(false);
      setAnalysisStep(3);

      // Get master resume text
      let masterResumeText;
      if (useUploadedResume && uploadedResume) {
        masterResumeText = uploadedResume;
      } else {
        masterResumeText = `Name: ${masterResume.name}
Contact: ${masterResume.contact.phone} | ${masterResume.contact.linkedin} | ${masterResume.contact.email}

EXECUTIVE SUMMARY
${masterResume.summary}

Core Competencies: ${masterResume.competencies}

PROFESSIONAL EXPERIENCE

${masterResume.experience.map(exp => `
${exp.company} - ${exp.location}
${exp.title} | ${exp.dates}
${exp.achievements.map(ach => `• ${ach}`).join('\n')}
`).join('\n')}

EDUCATION
${masterResume.education.degree} | ${masterResume.education.school}

TECHNICAL & METHODOLOGICAL SKILLS
${masterResume.skills}`;
      }

      // Build historical context from past Q&As
      let historicalContext = '';
      if (qaHistory.length > 0) {
        historicalContext = qaHistory.slice(-5).map(entry => {
          const questions = Array.isArray(entry.questions) ? entry.questions : [];
          const answers = Array.isArray(entry.answers) ? entry.answers : [];
          return questions.map((q, i) =>
            `Q: ${q}\nA: ${answers[i]}`
          ).join('\n');
        }).join('\n\n');
      }

      // Call the backend API to generate questions with job analysis for smarter gap detection
      const response = await fetch(`${API_URL}/api/generate-questions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`
        },
        body: JSON.stringify({
          jobDescription,
          masterResume: masterResumeText,
          historicalContext,
          jobAnalysis: analysis // Pass structured analysis for better questions
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const data = await response.json();
      const questionsArray = Array.isArray(data.questions) ? data.questions : [];

      // Debug logging for question generation
      console.log('[DEBUG] Questions response:', {
        questionsCount: questionsArray.length,
        noGapsFound: data.noGapsFound,
        jobAnalysisAvailable: !!analysis,
        atsKeywordsCount: analysis?.atsKeywords?.length || 0,
        mustHaveSkillsCount: analysis?.mustHaveSkills?.length || 0
      });

      setQuestions(questionsArray);
      setCurrentQuestionIndex(0); // Reset to first question
      setShowQuestions(true);
      setAnswers({});

      // Update wizard progress
      setWizardProgress(prev => ({ ...prev, jobDescriptionAdded: true }));
      setWizardStep(2);

      // Track analytics
      if (window.posthog) {
        posthog.capture('job_description_uploaded', {
          job_description_length: jobDescription.length,
          questions_generated: questionsArray.length,
          no_gaps_found: data.noGapsFound || false
        });
      }

      // If no questions were generated, show message and auto-trigger resume generation
      if (questionsArray.length === 0) {
        console.log('[DEBUG] No questions generated - auto-tailoring. noGapsFound:', data.noGapsFound);
        setTimeout(() => generateTailoredResume(), 100);
      } else {
        // Auto-scroll to questions section
        setTimeout(() => {
          if (questionsRef.current) {
            questionsRef.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }
        }, 100);
      }
    } catch (error) {
      console.error('Error analyzing job:', error);
      setError(`Failed to analyze job description: ${error.message}`);
      setLastFailedAction('analyze');
      if (window.posthog) {
        posthog.capture('analyze_error', { error: error.message });
      }
    } finally {
      setIsAnalyzing(false);
      setIsAnalyzingJob(false);
      setAnalysisStep(1);
    }
  };

  const handleAnswerChange = (index, value) => {
    setAnswers(prev => ({
      ...prev,
      [index]: value
    }));
  };

  const getResumeTextForSuggestions = () => {
    if (useUploadedResume && uploadedResume) {
      return uploadedResume;
    }
    return formatMasterResume();
  };

  const hasResumeContentForSuggestions = () => {
    if (useUploadedResume && uploadedResume) {
      return uploadedResume.trim().length > 0;
    }

    return Boolean(
      masterResume.summary ||
      masterResume.competencies ||
      masterResume.skills ||
      (masterResume.experience && masterResume.experience.length > 0)
    );
  };

  const suggestAnswerForQuestion = async (index) => {
    const questionText = questions[index]?.question;
    if (!questionText) {
      setError('No question available to answer.');
      return;
    }

    if (!jobDescription.trim()) {
      setValidationError('Please paste a job description first');
      return;
    }

    if (!hasResumeContentForSuggestions()) {
      setError('Please add your resume details to generate a suggestion.');
      return;
    }

    const resumeText = getResumeTextForSuggestions();

    setIsSuggestingAnswer(true);
    setSuggestingAnswerIndex(index);
    setError('');

    try {
      const response = await fetch(`${API_URL}/api/generate-app-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          question: questionText,
          charLimit: 600,
          jobDescription,
          tailoredResume: resumeText
        })
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to generate suggestion');
      }

      handleAnswerChange(index, data.response || '');

      if (window.posthog) {
        posthog.capture('question_answer_suggested', { question_index: index });
      }
    } catch (error) {
      console.error('Error generating suggestion:', error);
      setError(`Failed to generate suggestion: ${error.message}`);
    } finally {
      setIsSuggestingAnswer(false);
      setSuggestingAnswerIndex(null);
    }
  };


  const hasAnyAnswers = () => {
    return Object.values(answers).some(answer => answer && answer.trim() !== '');
  };

  // Reset the tailor form for a fresh start
  const resetTailorForm = () => {
    setJobDescription('');
    setQuestions([]);
    setAnswers({});
    setShowQuestions(false);
    setTailoredResume(null);
    setChangesSummary('');
    setCurrentApplicationId(null);
    setValidationError('');
    setWizardStep(1);
    setWizardProgress({
      jobDescriptionAdded: false,
      questionsAnswered: false,
      resumeGenerated: false
    });
    setAppQuestions([{ question: '', charLimit: 500, response: '' }]);
    setExtractedCompany('');
    setExtractedRole('');
    setExtractedLocation('');
    setShowJobDetailsPrompt(false);
    // Reset enhanced tailoring state
    setJobAnalysis(null);
    setQualityMetrics(null);
    setMarkedApplied(false);
    setHasDownloaded(false);
  };

  const createManualApplication = async ({ company, role, salaryRangeMin, salaryRangeMax, location, status, jobDescription, notes, rounds = [] }) => {
    const cleanCompany = (company || '').trim();
    if (!cleanCompany) {
      throw new Error('Company name is required.');
    }

    const cleanRole = (role || '').trim();
    const cleanNotes = (notes || '').trim();
    const requestedStatus = status || 'applied';
    const normalizedRounds = rounds
      .map((round) => ({
        roundType: round.roundType || 'phone_screen',
        scheduledDate: round.scheduledDate || null,
        interviewers: (round.interviewers || '').trim(),
        notes: (round.notes || '').trim(),
        status: round.status || 'scheduled'
      }))
      .filter((round) => round.roundType);

    if (isAuthenticated && authToken) {
      let applicationId = null;

      try {
        const createResponse = await fetch(`${API_URL}/api/user/applications`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            company: cleanCompany,
            role: cleanRole || null,
            jobDescription: (jobDescription || '').trim(),
            tailoredResume: '',
            changesSummary: '',
            status: requestedStatus,
            notes: cleanNotes,
            salaryRangeMin: salaryRangeMin || null,
            salaryRangeMax: salaryRangeMax || null,
            location: location || null
          })
        });

        const createData = await createResponse.json().catch(() => ({}));
        if (!createResponse.ok) {
          throw new Error(createData.error || 'Failed to create manual application.');
        }

        applicationId = createData.id;

        for (const round of normalizedRounds) {
          const roundResponse = await fetch(`${API_URL}/api/user/applications/${applicationId}/interviews`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${authToken}`
            },
            body: JSON.stringify(round)
          });

          if (!roundResponse.ok) {
            const roundError = await roundResponse.json().catch(() => ({}));
            throw new Error(roundError.error || 'Failed to save one or more interview rounds.');
          }
        }

        await loadUserData();
      } catch (error) {
        if (applicationId) {
          await loadUserData();
          throw new Error(`${error.message} The application was created and can be updated from its detail panel.`);
        }
        throw error;
      }
    } else {
      const timestamp = new Date().toISOString();
      const localId = Date.now();
      const effectiveStatus = normalizedRounds.length > 0 && ['draft', 'applied'].includes(requestedStatus)
        ? 'interview'
        : requestedStatus;

      const newApplication = {
        id: localId,
        company: cleanCompany,
        role: cleanRole || null,
        jobDescription: '',
        resume: '',
        changesSummary: '',
        status: effectiveStatus,
        appliedDate: effectiveStatus === 'applied' ? timestamp : null,
        timestamp,
        archived: false,
        notes: cleanNotes || null,
        qualityScore: null,
        atsScore: null,
        beforeQualityScore: null,
        beforeAtsScore: null,
        keywordsFound: [],
        strengths: [],
        interviewPrep: null,
        interviewRounds: normalizedRounds.map((round, index) => ({
          ...round,
          id: `local-round-${localId}-${index + 1}`
        }))
      };

      const updatedLibrary = [...loadResumeLibrary(), newApplication];
      localStorage.setItem(STORAGE_KEYS.RESUME_LIBRARY, JSON.stringify(updatedLibrary));
      setResumeLibrary(updatedLibrary);
    }

    if (window.posthog) {
      posthog.capture('manual_application_created', {
        has_notes: cleanNotes.length > 0,
        rounds_count: normalizedRounds.length,
        status: requestedStatus
      });
    }
  };

  const generateTailoredResume = async () => {
    if (!jobDescription.trim()) {
      setValidationError('Please paste a job description first');
      return;
    }

    const totalCredits = freeLimit + paidCredits;
    if (creditsUsed >= totalCredits) {
      setShowPaywall(true);
      if (window.posthog) {
        posthog.capture('paywall_shown', {
          credits_used: creditsUsed,
          total_credits: totalCredits,
          trigger: 'generate_resume'
        });
      }
      return;
    }

    setIsGenerating(true);
    setError('');
    setValidationError('');
    setLastFailedAction(null);

    try {
      // Use uploaded resume or default master resume
      let masterResumeText;

      if (useUploadedResume && uploadedResume) {
        masterResumeText = uploadedResume;
      } else {
        // Format the default master resume for the API - use the helper function
        masterResumeText = formatMasterResume();
      }

      // Format questionnaire answers (only include answered questions)
      let questionnaireAnswers = '';
      if (questions.length > 0 && hasAnyAnswers()) {
        questionnaireAnswers = questions
          .map((q, idx) => ({
            question: q.question,
            answer: answers[idx]
          }))
          .filter(qa => qa.answer && qa.answer.trim() !== '')
          .map(qa => `Q: ${qa.question}\nA: ${qa.answer}`)
          .join('\n\n');
      }

      // Build candidate profile from Q&A history for personalized tailoring
      // NOTE: Q&A history is now sent directly to server, not hacked into resume text
      const candidateProfile = qaHistory.length > 0 ? buildCandidateProfile(qaHistory) : null;

      // Debug logging for tailor request
      console.log('[DEBUG] Tailoring with jobAnalysis:', {
        hasJobAnalysis: !!jobAnalysis,
        atsKeywords: jobAnalysis?.atsKeywords,
        mustHaveSkills: jobAnalysis?.mustHaveSkills
      });

      // Call the enhanced backend API with job analysis for ATS optimization
      const response = await fetch(`${API_URL}/api/tailor-resume`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${authToken}`
        },
        body: JSON.stringify({
          jobDescription,
          masterResume: masterResumeText, // Clean resume, no hacks
          questionnaireAnswers: questionnaireAnswers || undefined,
          jobAnalysis, // Pass structured analysis for ATS keyword optimization
          candidateProfile, // NEW: Learned profile from Q&A history
          qaHistory: qaHistory.slice(-10) // Last 10 Q&A sessions for context
        })
      });

      if (response.status === 402) {
        const data = await response.json();
        setCreditsUsed(data.creditsUsed ?? creditsUsed);
        setFreeLimit(data.freeLimit ?? freeLimit);
        if (typeof data.paidLimit === 'number') {
          setPaidLimit(data.paidLimit);
        }
        if (typeof data.paidCredits === 'number') {
          setPaidCredits(data.paidCredits);
        }
        if (data.price?.display) setPriceDisplay(data.price.display);
        setShowPaywall(true);
        setIsGenerating(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || `API error: ${response.status}`);
      }

      const data = await response.json();

      if (typeof data.creditsUsed === 'number') {
        setCreditsUsed(data.creditsUsed);
      }
      if (data.freeLimit) {
        setFreeLimit(data.freeLimit);
      }
      if (data.billingStatus) {
        setBillingStatus(data.billingStatus);
      }

      // Store quality metrics if returned (from enhanced tailoring)
      if (data.qualityMetrics) {
        setQualityMetrics(data.qualityMetrics);
      }

      // Use extracted company name or fall back to regex
      const company = extractedCompany || (() => {
        const companyMatch = jobDescription.match(/(?:at|for|join)\s+([A-Z][A-Za-z0-9\s&]+)/i);
        return companyMatch ? companyMatch[1].trim() : 'Unknown Company';
      })();

      // Save Q&A to history for future knowledge building (only if there are answered questions)
      if (questions.length > 0 && hasAnyAnswers()) {
        const answeredQAs = questions
          .map((q, idx) => ({ question: q.question, answer: answers[idx] }))
          .filter(qa => qa.answer && qa.answer.trim() !== '');

        if (answeredQAs.length > 0) {
          // Immediately update local state so it's available for the next question generation
          const newQAEntry = {
            id: Date.now(),
            company,
            jobDescription: jobDescription.substring(0, 500),
            questions: answeredQAs.map(qa => qa.question),
            answers: answeredQAs.map(qa => qa.answer),
            timestamp: new Date().toISOString()
          };
          setQaHistory(prev => [...prev, newQAEntry]);

          // Also save to database
          try {
            await fetch(`${API_URL}/api/user/qa-history`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${authToken}`
              },
              body: JSON.stringify({
                company,
                jobDescriptionSnippet: jobDescription.substring(0, 500),
                questions: answeredQAs.map(qa => qa.question),
                answers: answeredQAs.map(qa => qa.answer)
              })
            });
          } catch (error) {
            console.error('Error saving Q&A history:', error);
          }
        }
      }

      // Extract role/position from job description (first few lines often contain the title)
      const extractRole = (jd) => {
        const lines = jd.split('\n').filter(line => line.trim().length > 0);
        // Look for common job title patterns in first 5 lines
        for (let i = 0; i < Math.min(5, lines.length); i++) {
          const line = lines[i].trim();
          // Common patterns: "Job Title:", "Position:", or just a standalone title
          if (line.match(/^(job\s+title|position|role|title):/i)) {
            return line.split(':')[1].trim();
          }
          // If line is short (< 60 chars) and doesn't contain common meta info, likely a title
          if (line.length < 60 && !line.match(/(location|salary|date|apply|company)/i)) {
            return line;
          }
        }
        return null;
      };

      const role = extractedRole || extractRole(jobDescription);

      // Save tailored resume to library (including quality metrics)
      try {
        const saveResponse = await fetch(`${API_URL}/api/user/applications`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify({
            company,
            role,
            location: extractedLocation || null,
            jobDescription: jobDescription,
            tailoredResume: data.tailoredResume,
            changesSummary: data.changesSummary,
            status: 'draft',
            qualityMetrics: data.qualityMetrics
          })
        });
        if (saveResponse.ok) {
          const savedApp = await saveResponse.json();
          setCurrentApplicationId(savedApp.id);
        }
      } catch (error) {
        console.error('Error saving application:', error);
      }

      // Reload data from database
      await loadUserData();

      setTailoredResume(data.tailoredResume);
      setChangesSummary(data.changesSummary || '');

      // Track analytics
      if (window.posthog) {
        posthog.capture('tailored_resume_created', {
          company: company,
          quality_score: data.qualityMetrics?.qualityScore || null,
          ats_score: data.qualityMetrics?.atsScore || null
        });
      }

      // Update wizard progress
      setWizardProgress(prev => ({ ...prev, resumeGenerated: true }));
      setWizardStep(4); // Move to Review step

      // Show success celebration - user controls when to dismiss
      setShowSuccessCelebration(true);
    } catch (error) {
      console.error('Error generating resume:', error);
      setError(`Failed to generate resume: ${error.message}`);
      setLastFailedAction('generate');
      if (window.posthog) {
        posthog.capture('generate_error', { error: error.message });
      }
    } finally {
      setIsGenerating(false);
    }
  };

  const handleStripeCheckout = async () => {
    setIsPaying(true);
    setPaymentError('');
    try {
      const checkoutRes = await fetch(`${API_URL}/api/billing/checkout`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        }
      });

      let checkoutData;
      try {
        checkoutData = await checkoutRes.json();
      } catch (jsonErr) {
        console.error('Failed to parse checkout response:', jsonErr);
        throw new Error('Server returned an invalid response. Please try again.');
      }

      if (!checkoutRes.ok) {
        throw new Error(checkoutData.error || 'Failed to start checkout');
      }

      console.log('Checkout response:', checkoutData);

      if (!checkoutData.checkoutUrl || typeof checkoutData.checkoutUrl !== 'string' || !checkoutData.checkoutUrl.startsWith('http')) {
        console.error('Invalid checkout URL:', checkoutData.checkoutUrl);
        throw new Error(`Invalid checkout URL received. Please contact support.`);
      }

      console.log('Redirecting to Stripe:', checkoutData.checkoutUrl);
      window.location.href = checkoutData.checkoutUrl;
    } catch (err) {
      console.error('Checkout error:', err);
      const errorMessage = err?.message || 'Payment failed. Please try again.';
      setPaymentError(errorMessage);
      // Don't close the modal - keep it open to show the error
    } finally {
      setIsPaying(false);
    }
  };

  // Generate AI response for application question
  const generateAppQuestionResponse = async (index) => {
    const question = appQuestions[index];
    if (!question.question.trim()) {
      setError('Please enter a question first');
      return;
    }

    setIsGeneratingAppResponse(true);
    setGeneratingQuestionIndex(index);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/api/generate-app-response`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          question: question.question,
          charLimit: question.charLimit,
          jobDescription: jobDescription,
          tailoredResume: tailoredResume
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to generate response');
      }

      const data = await response.json();

      // Update the question with the generated response
      const updatedQuestions = [...appQuestions];
      updatedQuestions[index] = { ...updatedQuestions[index], response: data.response };
      setAppQuestions(updatedQuestions);

      // Track analytics
      if (window.posthog) {
        posthog.capture('app_question_response_generated', { charLimit: question.charLimit });
      }
    } catch (error) {
      console.error('Error generating response:', error);
      setError(`Failed to generate response: ${error.message}`);
    } finally {
      setIsGeneratingAppResponse(false);
      setGeneratingQuestionIndex(null);
    }
  };

  // Add a new application question
  const addAppQuestion = () => {
    setAppQuestions([...appQuestions, { question: '', charLimit: 500, response: '' }]);
  };

  // Remove an application question
  const removeAppQuestion = (index) => {
    if (appQuestions.length > 1) {
      const updatedQuestions = appQuestions.filter((_, i) => i !== index);
      setAppQuestions(updatedQuestions);
    }
  };

  // Update an application question field
  const updateAppQuestion = (index, field, value) => {
    const updatedQuestions = [...appQuestions];
    updatedQuestions[index] = { ...updatedQuestions[index], [field]: value };
    setAppQuestions(updatedQuestions);
  };

  const copyToClipboard = async (text, source = 'unknown') => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      if (window.posthog) {
        posthog.capture('resume_copied', { source });
      }
    } catch (error) {
      console.error('Failed to copy:', error);
    }
  };

  const downloadResumeDocx = async (resumeData, defaultFilename, skipPrompt = false) => {
    try {
      let finalFilename = defaultFilename;

      // Try to prompt for filename, but use default if prompt is blocked
      if (!skipPrompt) {
        try {
          const userFilename = prompt(
            'Enter filename for your resume (without .docx extension):',
            defaultFilename.replace('.docx', '')
          );

          // If user cancels (clicks Cancel button), don't download
          if (userFilename === null) return;

          // Clean the filename and ensure it ends with .docx
          const cleanFilename = userFilename.trim() || defaultFilename.replace('.docx', '');
          finalFilename = cleanFilename.endsWith('.docx') ? cleanFilename : `${cleanFilename}.docx`;
        } catch (promptError) {
          // If prompt fails (blocked by browser), use default filename
          console.log('Prompt blocked, using default filename');
          finalFilename = defaultFilename;
        }
      }

      const blob = await generateDocx(resumeData, masterResume);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (window.posthog) {
        const source = defaultFilename.includes('Master') ? 'master_resume' : 'tailored_resume';
        posthog.capture('resume_downloaded', { format: 'docx', source });
      }
    } catch (error) {
      console.error('Error generating docx:', error);
      setError('Failed to generate Word document. Please try again.');
    }
  };

  const downloadResumeTxt = (resumeText, defaultFilename) => {
    try {
      const userFilename = prompt(
        'Enter filename for your resume (without .txt extension):',
        defaultFilename.replace('.txt', '')
      );

      if (userFilename === null) return;

      const cleanFilename = userFilename.trim() || defaultFilename.replace('.txt', '');
      const finalFilename = cleanFilename.endsWith('.txt') ? cleanFilename : `${cleanFilename}.txt`;

      // For master resume (object), format it; for tailored (string), use directly
      const textContent = typeof resumeText === 'object' ? formatMasterResume() : resumeText;

      const blob = new Blob([textContent], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = finalFilename;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      if (window.posthog) {
        const source = defaultFilename.includes('Master') ? 'master_resume' : 'tailored_resume';
        posthog.capture('resume_downloaded', { format: 'txt', source });
      }
    } catch (error) {
      console.error('Error generating txt:', error);
      setError('Failed to generate text file. Please try again.');
    }
  };

  // Simple markdown renderer for changes summary
  const renderMarkdown = (text) => {
    if (!text) return '';

    return text
      // Headers (## Header)
      .replace(/^## (.+)$/gm, '<h3 class="font-heading text-base font-semibold text-green-900 mt-4 mb-2">$1</h3>')
      .replace(/^# (.+)$/gm, '<h2 class="font-heading text-lg font-bold text-green-900 mt-4 mb-2">$1</h2>')
      // Bold (**text**)
      .replace(/\*\*(.+?)\*\*/g, '<strong class="font-semibold">$1</strong>')
      // Italic (*text*)
      .replace(/\*(.+?)\*/g, '<em>$1</em>')
      // Bullet points (- item or * item)
      .replace(/^[-*] (.+)$/gm, '<li class="ml-4">$1</li>')
      // Wrap lists
      .replace(/(<li[^>]*>.*<\/li>\s*)+/gs, '<ul class="list-disc list-inside space-y-1 my-2">$&</ul>')
      // Line breaks
      .replace(/\n\n/g, '<br/><br/>')
      .replace(/\n/g, '<br/>');
  };

  const formatMasterResume = () => {
    const sections = [];

    // Header with contact info (filter out undefined/empty values)
    const contactParts = [
      masterResume.contact?.phone,
      masterResume.contact?.linkedin,
      masterResume.contact?.email
    ].filter(Boolean);

    sections.push(`${masterResume.name || 'Your Name'}`);
    if (contactParts.length > 0) {
      sections.push(contactParts.join(' | '));
    }
    sections.push('');

    // Executive Summary (clean any embedded Core Competencies)
    if (masterResume.summary) {
      sections.push('EXECUTIVE SUMMARY');
      let summary = masterResume.summary;
      const coreCompIndex = summary.indexOf('Core Competencies');
      if (coreCompIndex > 0 && summary.substring(coreCompIndex).includes('|')) {
        summary = summary.substring(0, coreCompIndex).trim();
      }
      sections.push(summary);
      sections.push('');
    }

    // Core Competencies
    if (masterResume.competencies) {
      sections.push(`Core Competencies: ${masterResume.competencies}`);
      sections.push('');
    }

    // Professional Experience
    if (masterResume.experience && masterResume.experience.length > 0) {
      sections.push('PROFESSIONAL EXPERIENCE');
      sections.push('');
      masterResume.experience.forEach(exp => {
        const companyLocation = [exp.company, exp.location].filter(Boolean).join(' - ');
        if (companyLocation) sections.push(companyLocation);

        const titleDates = [exp.title, exp.dates].filter(Boolean).join(' | ');
        if (titleDates) sections.push(titleDates);

        if (exp.achievements && exp.achievements.length > 0) {
          exp.achievements.forEach(ach => {
            if (ach) sections.push(`• ${ach}`);
          });
        }
        sections.push('');
      });
    }

    // Education
    if (masterResume.education?.degree || masterResume.education?.school) {
      sections.push('EDUCATION');
      const eduParts = [masterResume.education.degree, masterResume.education.school].filter(Boolean);
      if (eduParts.length > 0) {
        sections.push(eduParts.join(' | '));
      }
      sections.push('');
    }

    // Skills
    if (masterResume.skills) {
      sections.push('TECHNICAL & METHODOLOGICAL SKILLS');
      sections.push(masterResume.skills);
    }

    return sections.join('\n');
  };

  // Function to save master resume to database
  const saveMasterResume = async (resumeData) => {
    console.log('[SaveMasterResume] Starting save with data:', { name: resumeData.name, experienceCount: resumeData.experience?.length });
    try {
      const response = await fetch(`${API_URL}/api/user/master-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          content: JSON.stringify(resumeData)
        })
      });

      console.log('[SaveMasterResume] Response status:', response.status, response.ok);

      if (response.ok) {
        console.log('[SaveMasterResume] Success - updating state');
        setMasterResume(resumeData);
        setHasMasterResume(true);
        // If coming from onboarding, send directly to tailor view (skip dashboard)
        if (showOnboarding) {
          setView('tailor');
        }
        setShowOnboarding(false);
        if (window.posthog) {
          posthog.capture('master_resume_saved', {
            has_experience: resumeData.experience?.length > 0,
            experience_count: resumeData.experience?.length || 0,
            has_skills: !!resumeData.skills
          });
        }
        return true;
      } else {
        // Get actual error from server
        const errorData = await response.json().catch(() => ({}));
        console.error('[SaveMasterResume] Server error:', response.status, errorData);
        throw new Error(errorData.error || `Server error: ${response.status}`);
      }
    } catch (error) {
      console.error('[SaveMasterResume] Error:', error);
      setError(error.message || 'Failed to save your resume. Please try again.');
      return false;
    }
  };

  // Function to fetch resume history
  const fetchResumeHistory = async () => {
    setIsLoadingHistory(true);
    try {
      const response = await fetch(`${API_URL}/api/user/resume-history`, {
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        const data = await response.json();
        setResumeHistory(data.resumes || []);
      } else {
        throw new Error('Failed to fetch resume history');
      }
    } catch (error) {
      console.error('Error fetching resume history:', error);
      setError('Failed to load resume history');
    } finally {
      setIsLoadingHistory(false);
    }
  };

  // Function to switch to a different resume version
  const switchToResume = async (resumeId) => {
    try {
      const response = await fetch(`${API_URL}/api/user/resume-history/set-active`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ resumeId })
      });

      if (response.ok) {
        // Reload master resume and history
        await loadUserData();
        await fetchResumeHistory();
      } else {
        throw new Error('Failed to switch resume');
      }
    } catch (error) {
      console.error('Error switching resume:', error);
      setError('Failed to activate resume');
    }
  };

  // Function to delete a resume from history
  const deleteResumeFromHistory = async (resumeId) => {
    if (!window.confirm('Are you sure you want to delete this resume version? This cannot be undone.')) {
      return;
    }

    try {
      const response = await fetch(`${API_URL}/api/user/resume-history/${resumeId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${authToken}`
        }
      });

      if (response.ok) {
        // Reload master resume and history
        await loadUserData();
        await fetchResumeHistory();
      } else {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to delete resume');
      }
    } catch (error) {
      console.error('Error deleting resume:', error);
      setError(error.message || 'Failed to delete resume');
    }
  };

  const handleResetAllData = async () => {
    if (!authToken || isResettingData) return;

    const confirmed = window.confirm(
      'This will permanently delete all your saved data except credits. Continue?'
    );
    if (!confirmed) return;

    const typedConfirmation = window.prompt('Type RESET to confirm:');
    if (typedConfirmation !== 'RESET') return;

    setIsResettingData(true);
    setResetDataError('');

    try {
      const response = await fetch(`${API_URL}/api/user/reset-all`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({ confirmReset: true })
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || 'Failed to reset user data');
      }

      // Wipe loaded user data in UI and restart onboarding.
      resetTailorForm();
      setMasterResume(EMPTY_RESUME_TEMPLATE);
      setHasMasterResume(false);
      setResumeHistory([]);
      setShowResumeHistory(false);
      setQaHistory([]);
      setResumeLibrary([]);
      setCurrentQuestionIndex(0);
      setUploadedResume(null);
      setUseUploadedResume(false);
      setUploadSuccess(false);
      setCachedJobSearch({
        jobs: [],
        searchStats: null,
        selectedRole: '',
        customRole: '',
        location: '',
        includeRemote: true
      });
      setFilterStatus('all');
      setDownloadDropdown(null);
      setValidationError('');
      setError('');
      setView('dashboard');
      setShowSettingsDrawer(false);
      setShowOnboarding(true);

      if (typeof data.creditsUsed === 'number') {
        setCreditsUsed(data.creditsUsed);
      }
      if (typeof data.paidCredits === 'number') {
        setPaidCredits(data.paidCredits);
      }

      await refreshBillingStatus();

      if (window.posthog) {
        posthog.capture('user_data_reset_all', {
          credits_used: data.creditsUsed,
          paid_credits: data.paidCredits
        });
      }
    } catch (error) {
      console.error('Error resetting user data:', error);
      setResetDataError(error.message || 'Failed to reset user data');
    } finally {
      setIsResettingData(false);
    }
  };

  // Function to enter edit mode for master resume
  const handleEditMasterResume = () => {
    setEditedMasterResumeText(formatMasterResume());
    setIsEditingMasterResume(true);
    setShowSettingsDrawer(false);
    setView('master');
  };

  // Function to cancel editing
  const handleCancelEdit = () => {
    setIsEditingMasterResume(false);
    setEditedMasterResumeText('');
  };

  // Function to optimize master resume using Q&A history
  const handleOptimizeResume = async () => {
    if (qaHistory.length === 0) {
      setOptimizeError('No Q&A history available. Apply to some jobs first to build context.');
      return;
    }

    // Check if user has credits before making API call
    const totalCredits = freeLimit + paidCredits;
    if (creditsUsed >= totalCredits) {
      setShowPaywall(true);
      return;
    }

    setIsOptimizingResume(true);
    setOptimizeError('');

    try {
      const response = await fetch(`${API_URL}/api/optimize-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          masterResume: formatMasterResume(),
          qaHistory
        })
      });

      // Handle paywall response
      if (response.status === 402) {
        const data = await response.json();
        setCreditsUsed(data.creditsUsed ?? creditsUsed);
        setFreeLimit(data.freeLimit ?? freeLimit);
        if (typeof data.paidCredits === 'number') {
          setPaidCredits(data.paidCredits);
        }
        setShowPaywall(true);
        setIsOptimizingResume(false);
        return;
      }

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to optimize resume');
      }

      const data = await response.json();

      // Update credits
      if (typeof data.creditsUsed === 'number') {
        setCreditsUsed(data.creditsUsed);
      }

      // Parse the optimized resume and update
      const parsedResume = parseResumeText(data.optimizedResume);
      setMasterResume(parsedResume);
      await saveMasterResume(parsedResume);

      // Show success modal with enhancements
      setOptimizeResultModal({
        show: true,
        enhancements: data.enhancements || 'Your resume has been enhanced with details from your Q&A history.'
      });

      if (window.posthog) {
        posthog.capture('resume_optimized', {
          qa_sessions_used: qaHistory.length
        });
      }
    } catch (error) {
      console.error('Error optimizing resume:', error);
      setOptimizeError(error.message || 'Failed to optimize resume. Please try again.');
    } finally {
      setIsOptimizingResume(false);
    }
  };

  // Function to save edited master resume text
  const handleSaveEditedMasterResume = async () => {
    setIsSavingMasterResume(true);
    setError('');

    try {
      // Parse directly — no Claude round-trip needed since the text follows formatMasterResume's format
      const parsedResume = parseResumeText(editedMasterResumeText);

      // Validate: warn if critical sections appear empty
      const warnings = [];
      if (!parsedResume.name) warnings.push('name');
      if (!parsedResume.experience.length) warnings.push('experience');
      if (!parsedResume.summary) warnings.push('summary');

      if (warnings.length === 3) {
        setError('Could not parse your resume. Please keep the section headers (EXECUTIVE SUMMARY, PROFESSIONAL EXPERIENCE, etc.) intact.');
        return;
      }

      // Save the parsed resume
      const saved = await saveMasterResume(parsedResume);

      if (saved) {
        setIsEditingMasterResume(false);
        setEditedMasterResumeText('');
        // Show warning if some sections were empty
        if (warnings.length > 0) {
          setError(`Note: The following sections could not be detected and may be empty: ${warnings.join(', ')}. Make sure section headers match the expected format.`);
        }
      }
    } catch (error) {
      console.error('Error saving edited resume:', error);
      setError('Failed to save your edited resume. Please check the format and try again.');
    } finally {
      setIsSavingMasterResume(false);
    }
  };

  // Function to parse resume text into structured format
  const parseOnboardingResume = async () => {
    try {
      setIsGenerating(true);
      setError('');

      const response = await fetch(`${API_URL}/api/parse-resume`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authToken}`
        },
        body: JSON.stringify({
          resumeText: onboardingResumeText
        })
      });

      if (!response.ok) {
        throw new Error('Failed to parse resume');
      }

      const data = await response.json();
      console.log('[ParseOnboarding] Parse response:', { hasParsedResume: !!data.parsedResume });

      const parsedResume = parseResumeText(data.parsedResume);
      console.log('[ParseOnboarding] Parsed resume:', { name: parsedResume?.name, experienceCount: parsedResume?.experience?.length });

      const saved = await saveMasterResume(parsedResume);
      console.log('[ParseOnboarding] Save result:', saved);

      if (!saved) {
        throw new Error('Failed to save resume. Please try again.');
      }
    } catch (error) {
      console.error('[ParseOnboarding] Error:', error);
      setError(error.message || 'Failed to parse your resume. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Function to handle file upload during onboarding
  const handleOnboardingUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    const allowedTypes = ['text/plain', 'application/pdf', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'application/msword'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please upload a .txt, .pdf, or .docx file');
      event.target.value = '';
      return;
    }

    setOnboardingUploading(true);
    setError('');

    try {
      const formData = new FormData();
      formData.append('resume', file);

      const response = await fetch(`${API_URL}/api/upload-resume`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${authToken}` },
        body: formData
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to upload resume');
      }

      const data = await response.json();
      // Server returns "resumeText" field (not "parsedResume")
      const resumeText = data.resumeText || data.parsedResume;
      console.log('[OnboardingUpload] Parse response:', { hasResumeText: !!resumeText, preview: resumeText?.substring(0, 100) });

      if (!resumeText) {
        throw new Error('No resume text returned from server');
      }

      const parsedResume = parseResumeText(resumeText);
      console.log('[OnboardingUpload] Parsed resume:', { name: parsedResume?.name, experienceCount: parsedResume?.experience?.length });

      const saved = await saveMasterResume(parsedResume);
      console.log('[OnboardingUpload] Save result:', saved);

      if (!saved) {
        throw new Error('Failed to save resume. Please try again.');
      }

      if (window.posthog) {
        posthog.capture('onboarding_resume_uploaded', { file_type: file.type });
      }
    } catch (error) {
      console.error('[OnboardingUpload] Error:', error);
      setError(error.message || 'Failed to upload resume. Try pasting it instead.');
    } finally {
      setOnboardingUploading(false);
      event.target.value = '';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 font-body">
      {/* Onboarding Modal - Simplified single screen */}
      {showOnboarding && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4 overflow-y-auto">
          <div className="bg-white max-w-lg w-full p-6 sm:p-8 shadow-lg rounded-2xl relative my-4">
            {/* Logout button */}
            <button
              onClick={handleLogout}
              className="absolute top-4 right-4 p-2 text-slate-400 hover:text-slate-600 transition-colors cursor-pointer"
              title="Logout"
            >
              <LogOut size={18} />
            </button>

            {/* Single focused screen */}
            <div className="text-center mb-6">
              <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-primary/10 flex items-center justify-center">
                <Upload size={24} className="text-primary" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-slate-900 mb-1">
                Upload your resume
              </h2>
              <p className="text-sm text-slate-500">
                We'll help you tailor it for each job you apply to
              </p>
            </div>

            {/* Error display */}
            {error && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl">
                <p className="text-red-700 text-sm">{error}</p>
              </div>
            )}

            {/* Upload Area - Primary action */}
            <div
              className={`relative border-2 border-dashed rounded-xl p-8 mb-4 transition-all text-center ${
                onboardingUploading ? 'border-primary bg-primary/5' : 'border-slate-300 hover:border-primary hover:bg-slate-50'
              }`}
            >
              <input
                type="file"
                accept=".pdf,.doc,.docx,.txt"
                onChange={handleOnboardingUpload}
                className="absolute inset-0 opacity-0 cursor-pointer"
                disabled={onboardingUploading}
              />
              {onboardingUploading ? (
                <div className="flex flex-col items-center gap-2">
                  <Loader2 size={24} className="animate-spin text-primary" />
                  <span className="text-sm text-primary font-medium">Processing...</span>
                </div>
              ) : (
                <div className="flex flex-col items-center gap-2">
                  <Upload size={24} className="text-slate-400" />
                  <span className="text-sm font-medium text-slate-700">
                    Drop your resume here or click to browse
                  </span>
                  <span className="text-xs text-slate-400">PDF, DOC, DOCX, or TXT</span>
                </div>
              )}
            </div>

            {/* Divider */}
            <div className="flex items-center gap-3 mb-4">
              <div className="flex-1 h-px bg-slate-200" />
              <span className="text-xs text-slate-400">or paste text</span>
              <div className="flex-1 h-px bg-slate-200" />
            </div>

            {/* Paste option - Secondary */}
            <textarea
              value={onboardingResumeText}
              onChange={(e) => setOnboardingResumeText(e.target.value)}
              placeholder="Paste your resume text here..."
              className="w-full h-24 p-3 border border-slate-200 rounded-xl text-sm resize-none focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary mb-4"
            />

            {onboardingResumeText.trim() && (
              <button
                onClick={parseOnboardingResume}
                disabled={isGenerating}
                className="w-full px-6 py-3 bg-primary text-white font-medium rounded-xl hover:bg-dark transition-colors cursor-pointer flex items-center justify-center gap-2"
              >
                {isGenerating ? (
                  <><Loader2 size={18} className="animate-spin" /> Processing...</>
                ) : (
                  <>Continue <ArrowRight size={18} /></>
                )}
              </button>
            )}

            {/* What happens next */}
            <p className="text-xs text-slate-400 text-center mt-4">
              Next: Paste a job description and we'll tailor your resume
            </p>
          </div>
        </div>
      )}

      {/* Modern Floating Navigation */}
      {isAuthenticated && !showOnboarding && (
        <Navigation
          view={view}
          onNavigate={(newView) => {
            if (newView === 'tailor') {
              resetTailorForm();
            }
            // Reset filter when navigating away from library
            if (newView !== 'library') {
              setFilterStatus('all');
            }
            setView(newView);
          }}
          onLogout={handleLogout}
          onOpenSettings={() => {
            setResetDataError('');
            setShowSettingsDrawer(true);
          }}
          userName={user?.email || 'User'}
          applicationCount={resumeLibrary.length}
          showUpgrade={true}
          onUpgrade={() => setShowPaywall(true)}
          creditsRemaining={Math.max(freeLimit + paidCredits - creditsUsed, 0)}
        />
      )}

      {/* Settings Drawer */}
      <SettingsDrawer
        isOpen={showSettingsDrawer}
        onClose={() => {
          setShowSettingsDrawer(false);
          setResetDataError('');
        }}
        masterResume={masterResume}
        onEditMasterResume={handleEditMasterResume}
        onUploadResume={handleFileUpload}
        onDownloadResume={() => downloadResumeDocx(masterResume, 'Master_Resume.docx', true)}
        onCopyResume={() => copyToClipboard(formatMasterResume(), 'master_resume')}
        formatMasterResume={formatMasterResume}
        isAnalyzing={isAnalyzing}
        copied={copied}
        uploadSuccess={uploadSuccess}
        uploadError={validationError}
        hasMasterResume={hasMasterResume}
        onOptimizeResume={handleOptimizeResume}
        isOptimizing={isOptimizingResume}
        optimizeError={optimizeError}
        qaHistory={qaHistory}
        resumeLibrary={resumeLibrary}
        user={user}
        onExportData={() => {
          const data = exportUserData(qaHistory, resumeLibrary, masterResume, user);
          const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
          const url = URL.createObjectURL(blob);
          const a = document.createElement('a');
          a.href = url;
          a.download = `resmaker-backup-${new Date().toISOString().split('T')[0]}.json`;
          document.body.appendChild(a);
          a.click();
          document.body.removeChild(a);
          URL.revokeObjectURL(url);
          if (window.posthog) posthog.capture('data_exported', { application_count: resumeLibrary.length });
        }}
        onImportData={async (e) => {
          const file = e.target.files[0];
          if (file) {
            const reader = new FileReader();
            reader.onload = async (event) => {
              try {
                const data = JSON.parse(event.target.result);
                await importUserData(data, authToken, API_URL);
                alert('Profile imported successfully! Page will reload.');
                window.location.reload();
              } catch (error) {
                console.error('Import error:', error);
                alert(`Failed to import: ${error.message}`);
                e.target.value = '';
              }
            };
            reader.readAsText(file);
          }
        }}
        authToken={authToken}
        onResetAllData={handleResetAllData}
        isResettingData={isResettingData}
        resetDataError={resetDataError}
        onOpenLinkedIn={() => {
          setShowSettingsDrawer(false);
          setView('linkedin');
        }}
      />

      <div className="max-w-7xl mx-auto px-4 py-6 pb-24 sm:pb-6">

        {/* Error Message with Retry */}
        {error && (
          <div className="bg-red-50 border-l-4 border-error px-6 py-4 mb-6 rounded-xl flex items-center justify-between gap-4">
            <p className="text-error font-medium text-sm m-0">{error}</p>
            {lastFailedAction && (
              <button
                onClick={() => {
                  setError('');
                  if (lastFailedAction === 'analyze') analyzeJobDescription();
                  else if (lastFailedAction === 'generate') generateTailoredResume();
                }}
                className="flex-shrink-0 flex items-center gap-1.5 px-4 py-2 bg-red-600 text-white text-sm font-semibold rounded-xl hover:bg-red-700 transition-colors cursor-pointer"
              >
                <Sparkles size={14} />
                Try Again
              </button>
            )}
          </div>
        )}

        {/* Content Area */}
        <main className="bg-white border border-slate-200 shadow-xl rounded-xl p-4 sm:p-6 md:p-8">
          {/* Dashboard view */}
          {view === 'dashboard' && (
            <DashboardView
              resumeLibrary={resumeLibrary}
              authToken={authToken}
              onNavigate={(newView, filter) => {
                if (newView === 'tailor') {
                  resetTailorForm();
                }
                setView(newView);
                if (filter) {
                  setFilterStatus(filter);
                }
              }}
              onViewApplication={(application) => {
                setTailoredResume(application.resume);
                setChangesSummary(application.changesSummary || '');
                if (application.qualityScore || application.atsScore) {
                  setQualityMetrics({
                    qualityScore: application.qualityScore,
                    atsScore: application.atsScore,
                    beforeQualityScore: application.beforeQualityScore,
                    beforeAtsScore: application.beforeAtsScore,
                    keywordsFound: application.keywordsFound || [],
                    strengths: application.strengths || []
                  });
                } else {
                  setQualityMetrics(null);
                }
                setView('tailored');
              }}
            />
          )}

          {view === 'master' && (
            <div>
              {uploadSuccess && (
                <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3">
                  <Check size={20} className="text-green-600" />
                  <span className="text-green-800 font-medium">Resume uploaded and saved successfully!</span>
                </div>
              )}
              <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center gap-4 mb-6">
                <h2 className="font-heading text-2xl font-semibold text-slate-900 m-0">Master Resume</h2>
                <div className="flex flex-wrap gap-2 sm:gap-3">
                  {!isEditingMasterResume ? (
                    <>
                      <label
                        className={`flex items-center gap-2 px-4 py-2 bg-secondary text-white text-sm font-medium transition-colors duration-200 rounded-xl ${
                          isAnalyzing ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:bg-cyan-600'
                        }`}
                      >
                        {isAnalyzing ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload size={18} />
                            Upload New
                          </>
                        )}
                        <input
                          type="file"
                          accept=".txt,.pdf,.docx,.doc"
                          onChange={handleFileUpload}
                          disabled={isAnalyzing}
                          className="hidden"
                        />
                      </label>
                      <button
                        onClick={handleEditMasterResume}
                        className="flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium transition-colors duration-200 cursor-pointer hover:bg-dark rounded-xl"
                      >
                        <Edit2 size={18} />
                        Edit
                      </button>
                      <button
                        onClick={() => copyToClipboard(formatMasterResume(), 'master_resume')}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium transition-colors duration-200 cursor-pointer hover:bg-slate-200 rounded-xl border border-slate-200"
                      >
                        {copied ? <Check size={18} /> : <Copy size={18} />}
                        {copied ? 'Copied!' : 'Copy'}
                      </button>
                      <div className="relative">
                        <button
                          onClick={() => setDownloadDropdown(downloadDropdown === 'master' ? null : 'master')}
                          className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium transition-colors cursor-pointer hover:bg-slate-200 rounded-xl"
                        >
                          <Download size={18} />
                          Download
                          <ChevronDown size={16} className={`transition-transform ${downloadDropdown === 'master' ? 'rotate-180' : ''}`} />
                        </button>
                        {downloadDropdown === 'master' && (
                          <div className="absolute right-0 mt-2 w-40 bg-white rounded-xl shadow-lg border border-slate-200 py-1 z-50">
                            <button
                              onClick={() => { downloadResumeDocx(masterResume, 'Master_Resume.docx'); setDownloadDropdown(null); }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2 cursor-pointer"
                            >
                              <FileText size={16} />
                              .docx (Word)
                            </button>
                            <button
                              onClick={() => { downloadResumeTxt(masterResume, 'Master_Resume.txt'); setDownloadDropdown(null); }}
                              className="w-full text-left px-4 py-2 text-sm text-slate-700 hover:bg-slate-100 flex items-center gap-2 cursor-pointer"
                            >
                              <FileText size={16} />
                              .txt (Plain Text)
                            </button>
                          </div>
                        )}
                      </div>
                    </>
                  ) : (
                    <>
                      <button
                        onClick={handleCancelEdit}
                        disabled={isSavingMasterResume}
                        className="flex items-center gap-2 px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium transition-colors duration-200 cursor-pointer hover:bg-slate-200 rounded-xl border border-slate-200 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <X size={18} />
                        Cancel
                      </button>
                      <button
                        onClick={handleSaveEditedMasterResume}
                        disabled={isSavingMasterResume}
                        className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium transition-colors duration-200 cursor-pointer hover:bg-green-700 rounded-xl disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        <Save size={18} />
                        {isSavingMasterResume ? 'Saving...' : 'Save Changes'}
                      </button>
                    </>
                  )}
                </div>
              </div>

              {!isEditingMasterResume ? (
                <>
                  <pre className="whitespace-pre-wrap font-mono text-sm text-slate-700 bg-slate-50 p-6 border border-slate-200 rounded-xl mb-6">
                    {formatMasterResume()}
                  </pre>

                  {/* Resume History Section */}
                  <div className="mt-8">
                    <div className="flex justify-between items-center mb-4">
                      <h3 className="font-heading text-xl font-semibold text-slate-900">Resume History</h3>
                      <button
                        onClick={() => {
                          setShowResumeHistory(!showResumeHistory);
                          if (!showResumeHistory && resumeHistory.length === 0) {
                            fetchResumeHistory();
                          }
                        }}
                        className="flex items-center gap-2 px-3 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors"
                      >
                        <History size={16} />
                        {showResumeHistory ? 'Hide History' : 'Show History'}
                      </button>
                    </div>

                    {showResumeHistory && (
                      <div className="border border-slate-200 rounded-xl overflow-hidden">
                        {isLoadingHistory ? (
                          <div className="p-6 text-center text-slate-600">
                            <div className="animate-spin inline-block w-6 h-6 border-2 border-primary border-t-transparent rounded-full mb-2"></div>
                            <p className="text-sm">Loading history...</p>
                          </div>
                        ) : resumeHistory.length === 0 ? (
                          <div className="p-6 text-center text-slate-600">
                            <p className="text-sm">No resume history yet. Upload a new resume to see versions here.</p>
                          </div>
                        ) : (
                          <div className="divide-y divide-slate-200">
                            {resumeHistory.map((resume, index) => (
                              <div
                                key={resume.id}
                                className={`p-4 hover:bg-slate-50 transition-colors ${
                                  resume.is_active ? 'bg-primary/10' : ''
                                }`}
                              >
                                <div className="flex justify-between items-start gap-4">
                                  <div className="flex-1">
                                    <div className="flex items-center gap-2 mb-1">
                                      <span className="text-sm font-medium text-slate-900">
                                        Version {resumeHistory.length - index}
                                      </span>
                                      {resume.is_active && (
                                        <span className="px-2 py-0.5 text-xs font-medium bg-primary text-white rounded-full">
                                          Active
                                        </span>
                                      )}
                                    </div>
                                    <p className="text-xs text-slate-500 mb-2">
                                      Uploaded: {new Date(resume.created_at).toLocaleDateString()} at{' '}
                                      {new Date(resume.created_at).toLocaleTimeString()}
                                    </p>
                                    <p className="text-xs text-slate-600 font-mono line-clamp-2">
                                      {resume.preview}...
                                    </p>
                                  </div>
                                  <div className="flex gap-2">
                                    {!resume.is_active && (
                                      <>
                                        <button
                                          onClick={() => switchToResume(resume.id)}
                                          className="px-3 py-1.5 text-xs font-medium bg-secondary hover:bg-cyan-600 text-white rounded-xl transition-colors cursor-pointer"
                                        >
                                          Use This
                                        </button>
                                        <button
                                          onClick={() => deleteResumeFromHistory(resume.id)}
                                          className="px-3 py-1.5 text-xs font-medium bg-red-600 hover:bg-red-700 text-white rounded-xl transition-colors cursor-pointer"
                                        >
                                          Delete
                                        </button>
                                      </>
                                    )}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                </>
              ) : (
                <div>
                  <div className="mb-4 p-4 bg-primary/10 border border-primary/20 rounded-xl">
                    <p className="text-slate-900 font-medium mb-2">Edit Mode</p>
                    <p className="text-slate-700 text-sm">
                      Make changes to your resume below. Keep the section headers (like EXECUTIVE SUMMARY, PROFESSIONAL EXPERIENCE) as they are for best results.
                    </p>
                  </div>
                  <textarea
                    value={editedMasterResumeText}
                    onChange={(e) => setEditedMasterResumeText(e.target.value)}
                    className="w-full h-96 font-mono text-sm text-slate-700 bg-white p-6 border border-slate-300 rounded-xl focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary resize-y"
                    placeholder="Edit your resume here..."
                  />
                </div>
              )}
            </div>
          )}

          {view === 'jobs' && (
            <div>
              <JobSearch
                authToken={authToken}
                masterResume={masterResume}
                candidateProfile={buildCandidateProfile(qaHistory)}
                cachedState={cachedJobSearch}
                onStateChange={setCachedJobSearch}
                resumeLibrary={resumeLibrary}
                onTailorJob={(job) => {
                  // Pre-fill job description with job info and prompt user to paste full description
                  const template = `Company: ${job.company}
Position: ${job.title}
Location: ${job.location}${job.department && job.department !== 'Unknown' ? `\nDepartment: ${job.department}` : ''}
Job URL: ${job.url}

---
PASTE THE FULL JOB DESCRIPTION BELOW:

`;
                  setJobDescription(template);
                  setView('tailor');
                }}
              />
            </div>
          )}

          {view === 'tailor' && (
            <div>
              {/* Header with encouragement */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-3">
                  <button
                    onClick={() => setView('library')}
                    className="p-2 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer text-slate-500"
                  >
                    <ChevronLeft size={20} />
                  </button>
                  <div>
                    <h2 className="font-heading text-xl font-bold text-slate-900">Let's land this one</h2>
                    <p className="text-sm text-slate-500">Tailored resumes get 3x more interviews</p>
                  </div>
                </div>
                {(() => {
                  const totalCredits = freeLimit + paidCredits;
                  const remaining = Math.max(totalCredits - creditsUsed, 0);
                  if (remaining <= 0) {
                    return (
                      <button
                        onClick={() => setShowPaywall(true)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-primary text-white text-xs font-medium rounded-lg hover:bg-dark transition-colors cursor-pointer"
                      >
                        <span className="w-1.5 h-1.5 bg-white rounded-full"></span>
                        Add Credits
                      </button>
                    );
                  }
                  return (
                    <div className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-slate-600">
                      <span className={`w-1.5 h-1.5 rounded-full ${remaining <= 2 ? 'bg-amber-400' : 'bg-green-500'}`}></span>
                      {remaining} credit{remaining !== 1 ? 's' : ''}
                    </div>
                  );
                })()}
              </div>

              {/* Resume Gate */}
              {!hasMasterResumeContent() ? (
                <div className="space-y-6">
                  <div className="bg-white rounded-xl border border-slate-200 p-6 sm:p-10 text-center hover:shadow-lg transition-shadow">
                    <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-primary/20 to-purple-100 flex items-center justify-center mx-auto mb-5">
                      <FileText size={28} className="text-primary" />
                    </div>
                    <h3 className="font-heading text-2xl font-bold text-slate-900 mb-3">
                      Show us what you've got
                    </h3>
                    <p className="text-slate-500 max-w-md mx-auto mb-8">
                      Upload your resume and we'll work our magic. We won't change who you are — just how you present it.
                    </p>

                    {error && (
                      <div className="bg-red-50 border border-red-200 rounded-xl p-3 mb-4 max-w-lg mx-auto">
                        <p className="text-red-700 text-sm">{error}</p>
                      </div>
                    )}

                    <div className="max-w-lg mx-auto space-y-5">
                      {/* Option 1: Upload file — fastest path */}
                      <label className={`w-full flex items-center justify-center gap-3 px-6 py-3 bg-primary text-white font-medium rounded-xl transition-colors ${
                        isParsingInlineResume ? 'opacity-50 cursor-not-allowed' : 'hover:bg-primary/90 cursor-pointer'
                      }`}>
                        <Upload size={18} />
                        Upload Resume File
                        <input
                          type="file"
                          accept=".txt,.pdf,.docx,.doc"
                          onChange={handleInlineFileUpload}
                          disabled={isParsingInlineResume}
                          className="hidden"
                        />
                      </label>
                      <p className="text-xs text-slate-400 text-center">
                        Supports .pdf, .docx, and .txt files
                      </p>

                      {/* Divider */}
                      <div className="flex items-center gap-4">
                        <div className="flex-1 h-px bg-slate-200"></div>
                        <span className="text-xs text-slate-400 font-medium">OR PASTE TEXT</span>
                        <div className="flex-1 h-px bg-slate-200"></div>
                      </div>

                      {/* Option 2: Paste text */}
                      <textarea
                        value={inlineResumeText}
                        onChange={(e) => setInlineResumeText(e.target.value)}
                        placeholder="Paste your complete resume here..."
                        className="w-full h-36 p-4 border border-slate-300 rounded-xl text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary font-mono"
                      />
                      {inlineResumeText.trim() && (
                        <button
                          onClick={handleInlineResumePaste}
                          disabled={isParsingInlineResume}
                          className={`w-full flex items-center justify-center gap-2 px-6 py-3 text-white font-semibold rounded-xl transition-all ${
                            isParsingInlineResume
                              ? 'bg-slate-400 cursor-not-allowed'
                              : 'bg-primary hover:bg-dark cursor-pointer'
                          }`}
                        >
                          {isParsingInlineResume ? (
                            <>
                              <Loader2 size={18} className="animate-spin" />
                              Processing...
                            </>
                          ) : (
                            <>
                              <Sparkles size={18} />
                              Save & Continue
                            </>
                          )}
                        </button>
                      )}
                      {isParsingInlineResume && (
                        <div className="flex items-center justify-center gap-2 text-sm text-primary">
                          <Loader2 size={16} className="animate-spin" />
                          Processing your resume...
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
              <>
              {/* Wizard Progress Bar */}
              <WizardProgressBar currentStep={wizardStep} progress={wizardProgress} />

              <div className="space-y-6">

                {/* Step 1: Job Description */}
                <div className="card hover:shadow-lg transition-shadow duration-300">
                  <div className="flex items-start justify-between mb-5">
                    <div>
                      <h3 className="heading-section mb-1">
                        Paste the job description
                      </h3>
                      <p className="text-secondary">We'll decode what they're really looking for</p>
                    </div>
                    <div className="hidden sm:flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 rounded-full text-xs text-slate-600 font-medium">
                      <History size={12} />
                      <span>Auto-saved</span>
                    </div>
                  </div>
                  <div>
                    <textarea
                      value={jobDescription}
                      onChange={(e) => {
                        setJobDescription(e.target.value);
                        setValidationError('');
                        if (showQuestions) {
                          setShowQuestions(false);
                          setQuestions([]);
                          setAnswers({});
                        }
                      }}
                      placeholder="Copy the full job posting and paste it here. The more detail, the better we can tailor your resume."
                      className={`input h-48 resize-none ${
                        validationError ? 'input-error' : ''
                      }`}
                    />
                    {validationError && (
                      <p className="text-xs text-red-600 font-medium mt-2 flex items-center gap-1">
                        <X size={12} /> {validationError}
                      </p>
                    )}
                  </div>
                  {!showQuestions && (
                    <div className="mt-6">
                      <button
                        onClick={analyzeJobDescription}
                        disabled={isAnalyzing || !jobDescription.trim()}
                        className={`btn-primary btn-full btn-with-icon btn-lg group ${
                          isAnalyzing ? 'btn-loading' : ''
                        } ${!jobDescription.trim() ? 'btn-disabled' : ''}`}
                      >
                        {isAnalyzing ? (
                          <>
                            <Loader2 size={20} className="animate-spin" />
                            <span>{progressMessage || 'Reading between the lines...'}</span>
                          </>
                        ) : (
                          <>
                            <Sparkles size={20} className="group-hover:rotate-12 transition-transform" />
                            <span>Analyze This Role</span>
                          </>
                        )}
                      </button>
                      {!isAnalyzing && (
                        <p className="text-center text-xs text-slate-400 mt-3">
                          We'll find what they're really looking for
                        </p>
                      )}
                    </div>
                  )}
                </div>

                {/* Job Details Prompt - When we couldn't detect company/role */}
                {showJobDetailsPrompt && (
                  <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 space-y-3">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-amber-100 flex items-center justify-center flex-shrink-0">
                        <Briefcase size={16} className="text-amber-600" />
                      </div>
                      <div className="flex-1">
                        <h4 className="font-medium text-slate-900 text-sm">Quick question — where are you applying?</h4>
                        <p className="text-xs text-slate-500 mt-0.5">We couldn't detect these from the job description</p>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Company</label>
                        <input
                          type="text"
                          value={extractedCompany}
                          onChange={(e) => setExtractedCompany(e.target.value)}
                          placeholder="e.g., Stripe"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                      <div>
                        <label className="block text-xs font-medium text-slate-600 mb-1">Role</label>
                        <input
                          type="text"
                          value={extractedRole}
                          onChange={(e) => setExtractedRole(e.target.value)}
                          placeholder="e.g., Senior Engineer"
                          className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-medium text-slate-600 mb-1">Location <span className="text-slate-400 font-normal">(optional)</span></label>
                      <input
                        type="text"
                        value={extractedLocation}
                        onChange={(e) => setExtractedLocation(e.target.value)}
                        placeholder="e.g., San Francisco, CA or Remote"
                        className="w-full px-3 py-2 text-sm border border-slate-200 rounded-lg focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                      />
                    </div>
                    <button
                      onClick={() => setShowJobDetailsPrompt(false)}
                      disabled={!extractedCompany.trim() || !extractedRole.trim()}
                      className={`w-full py-2 text-sm font-medium rounded-lg transition-colors ${
                        extractedCompany.trim() && extractedRole.trim()
                          ? 'bg-primary text-white hover:bg-dark cursor-pointer'
                          : 'bg-slate-200 text-slate-400 cursor-not-allowed'
                      }`}
                    >
                      Continue
                    </button>
                  </div>
                )}

                {/* Company Intel Card - Shows after job is analyzed */}
                {showQuestions && extractedCompany && (
                  <CompanyIntelCard company={extractedCompany} jobDescription={jobDescription} />
                )}

                {/* Step 2: Generate + Optional Details */}
                {showQuestions && questions.length > 0 && !showJobDetailsPrompt && (
                  <div ref={questionsRef} className="space-y-4">

                    {/* Skill Gap Questions - Conversational Style */}
                    {!isGenerating && questions.length > 0 && (
                      <div className="card-subtle overflow-hidden animate-fadeIn">
                        {/* Header with progress */}
                        <div className="px-5 py-4 flex items-center justify-between border-b border-slate-200/50">
                          <div>
                            <div className="flex items-center gap-2">
                              <h4 className="font-heading font-semibold text-slate-800">
                                {Object.keys(answers).filter(k => answers[k]?.trim()).length === 0
                                  ? 'Quick check-in'
                                  : Object.keys(answers).filter(k => answers[k]?.trim()).length === questions.length
                                    ? 'All set!'
                                    : 'Making progress'}
                              </h4>
                              {Object.keys(answers).filter(k => answers[k]?.trim()).length === 0 && (
                                <span className="px-2.5 py-1 bg-green-100 text-green-700 text-[11px] font-semibold rounded-full">
                                  +{Math.min(questions.length * 4, 15)}% boost
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              {Object.keys(answers).filter(k => answers[k]?.trim()).length === 0
                                ? `${questions.length} quick question${questions.length > 1 ? 's' : ''} to strengthen your resume`
                                : `${Object.keys(answers).filter(k => answers[k]?.trim()).length}/${questions.length} answered`}
                            </p>
                          </div>
                          {/* Progress dots */}
                          <div className="flex items-center gap-1.5">
                            {questions.map((_, idx) => (
                              <button
                                key={idx}
                                onClick={() => setCurrentQuestionIndex(idx)}
                                className={`w-2.5 h-2.5 rounded-full transition-all duration-200 cursor-pointer ${
                                  idx === currentQuestionIndex
                                    ? 'bg-primary w-6'
                                    : answers[idx]?.trim()
                                      ? 'bg-green-500'
                                      : 'bg-slate-300 hover:bg-slate-400'
                                }`}
                              />
                            ))}
                          </div>
                        </div>

                        {/* Question Card */}
                        <div className="p-5">
                          <p className="text-sm font-medium text-slate-700 mb-3 leading-relaxed">
                            {questions[currentQuestionIndex]?.question}
                          </p>
                          <textarea
                            id={`question-answer-${currentQuestionIndex}`}
                            value={answers[currentQuestionIndex] || ''}
                            onChange={(e) => handleAnswerChange(currentQuestionIndex, e.target.value)}
                            placeholder="A specific example with numbers makes the biggest impact..."
                            className="input min-h-[80px] resize-none"
                          />

                          {/* Actions */}
                          <div className="flex items-center justify-between mt-4">
                            <div>
                              {answers[currentQuestionIndex]?.trim() ? (
                                <span className="inline-flex items-center gap-1.5 text-green-600 text-sm font-medium">
                                  <Check size={14} />
                                  Saved automatically
                                </span>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => suggestAnswerForQuestion(currentQuestionIndex)}
                                  disabled={isSuggestingAnswer}
                                  className="btn-text text-sm"
                                >
                                  {isSuggestingAnswer && suggestingAnswerIndex === currentQuestionIndex
                                    ? 'Thinking...'
                                    : '✨ Help me answer this'}
                                </button>
                              )}
                            </div>
                            <div className="flex items-center gap-2">
                              {currentQuestionIndex > 0 && (
                                <button
                                  onClick={() => setCurrentQuestionIndex(prev => prev - 1)}
                                  className="btn-ghost btn-sm"
                                >
                                  <ChevronLeft size={16} />
                                </button>
                              )}
                              {currentQuestionIndex < questions.length - 1 ? (
                                <button
                                  onClick={() => setCurrentQuestionIndex(prev => prev + 1)}
                                  className="btn-secondary btn-sm btn-with-icon"
                                >
                                  {answers[currentQuestionIndex]?.trim() ? 'Next' : 'Skip for now'}
                                  <ChevronRight size={14} />
                                </button>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-3 py-1.5 bg-green-50 border border-green-200 text-green-700 text-sm font-medium rounded-lg">
                                  <Check size={14} />
                                  Done
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Primary Action - Generate Resume */}
                    <div className="card animate-fadeIn">
                      {/* Success indicator */}
                      <div className="flex items-center gap-3 mb-4 pb-4 border-b border-slate-100">
                        <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center">
                          <Check size={16} className="text-green-600" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-slate-800">We found what matters</p>
                          <p className="text-xs text-slate-500">Your resume will speak their language</p>
                        </div>
                      </div>

                      {/* CTA */}
                      <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                        {isGenerating ? 'Creating your tailored resume...' : 'Ready to make it yours'}
                      </h3>

                      {isGenerating ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-3 p-4 bg-primary/5 rounded-xl">
                            <Loader2 size={24} className="animate-spin text-primary" />
                            <div>
                              <p className="text-sm font-medium text-slate-800">
                                {progressMessage || 'Tailoring your experience...'}
                              </p>
                              <p className="text-xs text-slate-500 mt-0.5">This usually takes 10-15 seconds</p>
                            </div>
                          </div>
                        </div>
                      ) : (
                        <>
                          <p className="text-sm text-slate-500 mb-5">
                            We'll highlight your relevant experience and weave in their keywords naturally.
                          </p>
                          <button
                            onClick={generateTailoredResume}
                            className="btn-primary btn-full btn-with-icon btn-lg group"
                          >
                            <Sparkles size={20} className="group-hover:rotate-12 transition-transform" />
                            <span>Tailor My Resume</span>
                          </button>
                          <p className="text-center text-xs text-slate-400 mt-3">
                            Takes about 15 seconds
                          </p>
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Generating state when auto-triggered (no questions) */}
                {showQuestions && questions.length === 0 && isGenerating && (
                  <div className="card text-center py-10 animate-fadeIn">
                    {/* Great match badge */}
                    <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-50 text-green-700 rounded-full text-sm font-medium mb-6">
                      <CheckCircle size={16} />
                      <span>Great match — no gaps to fill!</span>
                    </div>

                    {/* Animated loader */}
                    <div className="relative w-16 h-16 mx-auto mb-6">
                      <div className="absolute inset-0 rounded-full border-4 border-slate-100"></div>
                      <div className="absolute inset-0 rounded-full border-4 border-primary border-t-transparent animate-spin"></div>
                      <Sparkles size={24} className="absolute inset-0 m-auto text-primary" />
                    </div>

                    <h3 className="font-heading text-lg font-semibold text-slate-900 mb-2">
                      {progressMessage || 'Creating your tailored resume...'}
                    </h3>
                    <p className="text-sm text-slate-500 max-w-sm mx-auto">
                      We're optimizing the format and weaving in the right keywords.
                    </p>
                  </div>
                )}
              </div>
              </>
              )}
            </div>
          )}

          {view === 'tailored' && tailoredResume && (
            <div className="space-y-5">
              {/* Success Header - Celebratory! */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
                <div className="flex items-start gap-4 mb-6">
                  <div className="w-12 h-12 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                    <Sparkles size={22} className="text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h2 className="font-heading text-xl font-bold text-slate-900 mb-1">
                      Looking good! Your resume is ready.
                    </h2>
                    <p className="text-sm text-slate-600">
                      {extractedCompany
                        ? `Tailored for ${extractedCompany} with ${qualityMetrics?.keywordsFound?.length || 0} matching keywords`
                        : 'Your experience now speaks directly to what they\'re looking for'}
                    </p>
                  </div>
                </div>

                {/* Download Options - Primary Action */}
                <div className="grid grid-cols-3 gap-3 mb-5">
                  <button
                    onClick={() => downloadResumeDocx(tailoredResume, 'Tailored_Resume.docx')}
                    className="flex flex-col items-center gap-2 p-4 bg-primary/5 hover:bg-primary/10 border border-primary/20 hover:border-primary/40 rounded-xl transition-all cursor-pointer"
                  >
                    <FileText size={20} className="text-primary" />
                    <div className="text-center">
                      <span className="text-sm font-medium text-slate-900 block">.docx</span>
                      <span className="text-xs text-slate-500">Editable</span>
                    </div>
                  </button>
                  <button
                    onClick={() => {
                      const blob = new Blob([tailoredResume], { type: 'text/plain' });
                      const url = URL.createObjectURL(blob);
                      const a = document.createElement('a');
                      a.href = url;
                      a.download = `Tailored_Resume_${extractedCompany || 'Job'}.txt`;
                      document.body.appendChild(a);
                      a.click();
                      document.body.removeChild(a);
                      URL.revokeObjectURL(url);
                    }}
                    className="flex flex-col items-center gap-2 p-4 bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 rounded-xl transition-all cursor-pointer"
                  >
                    <FileText size={20} className="text-slate-600" />
                    <div className="text-center">
                      <span className="text-sm font-medium text-slate-900 block">.txt</span>
                      <span className="text-xs text-slate-500">ATS-safe</span>
                    </div>
                  </button>
                  <button
                    onClick={() => copyToClipboard(tailoredResume, 'tailored_resume')}
                    className="flex flex-col items-center gap-2 p-4 bg-slate-50 hover:bg-slate-100 border border-slate-200 hover:border-slate-300 rounded-xl transition-all cursor-pointer"
                  >
                    {copied ? <Check size={20} className="text-green-600" /> : <Copy size={20} className="text-slate-600" />}
                    <div className="text-center">
                      <span className="text-sm font-medium text-slate-900 block">{copied ? 'Copied!' : 'Copy'}</span>
                      <span className="text-xs text-slate-500">Clipboard</span>
                    </div>
                  </button>
                </div>

                {/* Keywords if available */}
                {qualityMetrics?.keywordsFound?.length > 0 && (
                  <div className="pt-4 border-t border-slate-100">
                    <p className="text-xs font-medium text-slate-500 mb-2">Keywords matched</p>
                    <div className="flex flex-wrap gap-1.5">
                      {qualityMetrics.keywordsFound.slice(0, 8).map((keyword, i) => (
                        <span key={i} className="px-2 py-1 bg-slate-100 text-slate-600 text-xs rounded-md">{keyword}</span>
                      ))}
                      {qualityMetrics.keywordsFound.length > 8 && (
                        <span className="px-2 py-1 bg-slate-100 text-slate-500 text-xs rounded-md">+{qualityMetrics.keywordsFound.length - 8}</span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Next Steps Card */}
              <div className="bg-white rounded-xl border border-slate-200 p-5 sm:p-6">
                <h3 className="text-sm font-semibold text-slate-900 mb-4">What happens next</h3>
                <div className="space-y-3">
                  {/* Mark as applied */}
                  {!markedApplied ? (
                    <div className="flex items-center justify-between p-3 bg-primary/5 border border-primary/20 rounded-lg">
                      <div>
                        <p className="text-sm font-medium text-slate-900">Submitted your application?</p>
                        <p className="text-xs text-slate-500">We'll help you prep when you get an interview</p>
                      </div>
                      <button
                        onClick={async () => {
                          if (!currentApplicationId) return;
                          try {
                            await fetch(`${API_URL}/api/user/applications/${currentApplicationId}`, {
                              method: 'PATCH',
                              headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                              body: JSON.stringify({ status: 'applied', appliedDate: new Date().toISOString() })
                            });
                            await loadUserData();
                            setMarkedApplied(true);
                            // Show celebration
                            const appliedCount = resumeLibrary.filter(a => a.status === 'applied').length + 1;
                            setShowAppliedCelebration({
                              company: extractedCompany || 'this role',
                              appliedCount
                            });
                            if (window.posthog) posthog.capture('marked_applied', { source: 'results_page' });
                          } catch (error) {
                            console.error('Error:', error);
                          }
                        }}
                        className="flex items-center gap-1.5 px-4 py-2 bg-primary hover:bg-dark text-white text-sm font-medium rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <Check size={14} />
                        I Applied
                      </button>
                    </div>
                  ) : (
                    <div className="flex items-start gap-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                      <div className="w-6 h-6 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0 mt-0.5">
                        <Check size={14} className="text-white" />
                      </div>
                      <div className="min-w-0">
                        <p className="text-sm font-medium text-green-800">Application tracked.</p>
                        <p className="text-xs text-green-700 mt-0.5">When you land an interview, go to <span className="font-semibold">My Applications</span> and update your status — we'll prompt you to prep.</p>
                      </div>
                    </div>
                  )}

                  {/* Saved for reference */}
                  <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center">
                      <History size={14} className="text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Job details saved</p>
                      <p className="text-xs text-slate-500">Come back to review the posting anytime</p>
                    </div>
                  </div>

                  {/* Interview prep teaser */}
                  <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-lg">
                    <div className="w-6 h-6 rounded-full bg-slate-200 flex items-center justify-center">
                      <MessageSquare size={14} className="text-slate-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-slate-700">Interview prep on deck</p>
                      <p className="text-xs text-slate-500">When you land an interview, we've got you covered</p>
                    </div>
                  </div>
                </div>

                {/* Navigation */}
                <div className="flex gap-3 mt-5 pt-4 border-t border-slate-100">
                  <button
                    onClick={() => {
                      setJobDescription('');
                      setTailoredResume(null);
                      setShowQuestions(false);
                      setQuestions([]);
                      setAnswers({});
                      setMarkedApplied(false);
                      setHasDownloaded(false);
                      setView('tailor');
                    }}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-primary hover:bg-primary/5 rounded-lg transition-colors cursor-pointer"
                  >
                    <Plus size={16} />
                    New Application
                  </button>
                  <button
                    onClick={() => setView('library')}
                    className="flex-1 flex items-center justify-center gap-2 py-2.5 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-lg transition-colors cursor-pointer"
                  >
                    View Applications
                    <ArrowRight size={16} />
                  </button>
                </div>
              </div>

              {/* Tabbed Content Area */}
              <div className="bg-white rounded-2xl border border-slate-200 overflow-hidden">
                {/* Tab Navigation */}
                <div className="flex border-b border-slate-200 overflow-x-auto">
                  {[
                    { id: 'preview', label: 'Preview', icon: FileText },
                    { id: 'diff', label: 'Before/After', icon: ArrowLeftRight },
                    { id: 'changes', label: 'Changes', icon: Sparkles },
                    { id: 'questions', label: 'Ask Claude', icon: MessageSquare },
                  ].map((tab) => (
                    <button
                      key={tab.id}
                      onClick={() => setReviewTab(tab.id)}
                      className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium whitespace-nowrap transition-colors cursor-pointer border-b-2 -mb-px ${
                        reviewTab === tab.id
                          ? 'text-primary border-primary bg-primary/5'
                          : 'text-slate-500 border-transparent hover:text-slate-700 hover:bg-slate-50'
                      }`}
                    >
                      <tab.icon size={16} />
                      {tab.label}
                    </button>
                  ))}
                </div>

                {/* Tab Content */}
                <div className="p-5">
                  {/* Preview Tab */}
                  {reviewTab === 'preview' && (
                    <div className="space-y-4">
                      {qualityMetrics?.keywordsFound?.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 pb-4 border-b border-slate-100">
                          <span className="text-xs font-medium text-slate-500 mr-2">Keywords matched:</span>
                          {qualityMetrics.keywordsFound.slice(0, 8).map((keyword, i) => (
                            <span key={i} className="px-2 py-0.5 bg-secondary/10 text-secondary text-xs rounded-full font-medium">{keyword}</span>
                          ))}
                          {qualityMetrics.keywordsFound.length > 8 && (
                            <span className="px-2 py-0.5 bg-slate-100 text-slate-500 text-xs rounded-full">+{qualityMetrics.keywordsFound.length - 8}</span>
                          )}
                        </div>
                      )}
                      <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed max-h-[500px] overflow-y-auto">
                        {tailoredResume}
                      </pre>
                    </div>
                  )}

                  {/* Diff Tab - Before/After Comparison */}
                  {reviewTab === 'diff' && (
                    <ResumeDiff
                      originalResume={formatMasterResume()}
                      tailoredResume={tailoredResume}
                    />
                  )}

                  {/* Changes Tab */}
                  {reviewTab === 'changes' && (
                    <div className="space-y-4">
                      {/* Score Cards with Benchmarks */}
                      {qualityMetrics && (
                        <div className="space-y-4 pb-4 border-b border-slate-100">
                          {/* ATS Score with Context */}
                          <div className={`rounded-xl p-5 transition-all ${
                            qualityMetrics.atsScore >= 85
                              ? 'bg-gradient-to-br from-green-50 to-emerald-50 border border-green-200'
                              : qualityMetrics.atsScore >= 75
                                ? 'bg-gradient-to-br from-primary/5 to-purple-50 border border-primary/20'
                                : 'bg-slate-50 border border-slate-200'
                          }`}>
                            <div className="flex items-center justify-between mb-2">
                              <span className="text-sm font-semibold text-slate-700">Match Score</span>
                              {qualityMetrics.beforeAtsScore && qualityMetrics.beforeAtsScore < qualityMetrics.atsScore && (
                                <span className="flex items-center gap-1 text-xs font-bold text-green-600 bg-green-100 px-2.5 py-1 rounded-full">
                                  <TrendingUp size={12} />
                                  +{qualityMetrics.atsScore - qualityMetrics.beforeAtsScore}
                                </span>
                              )}
                            </div>

                            {/* Big score display - animated */}
                            <div className="flex items-baseline gap-2 mb-4">
                              <span className={`font-heading text-5xl font-bold tracking-tight ${
                                qualityMetrics.atsScore >= 85 ? 'text-green-600' :
                                qualityMetrics.atsScore >= 75 ? 'text-primary' : 'text-slate-700'
                              }`}>
                                <AnimatedScore value={qualityMetrics.atsScore} duration={1200} />
                              </span>
                              <span className="text-slate-400 text-lg font-medium">/ 100</span>
                            </div>

                            {/* Progress bar - thicker, more satisfying */}
                            <div className="relative h-3 bg-white/60 rounded-full mb-3 overflow-hidden shadow-inner">
                              <div
                                className={`absolute h-full rounded-full transition-all duration-700 ease-out ${
                                  qualityMetrics.atsScore >= 85 ? 'bg-gradient-to-r from-green-500 to-emerald-400' :
                                  qualityMetrics.atsScore >= 75 ? 'bg-gradient-to-r from-primary to-purple-400' :
                                  'bg-slate-400'
                                }`}
                                style={{ width: `${qualityMetrics.atsScore}%` }}
                              />
                              {/* Benchmark markers */}
                              <div className="absolute top-0 w-0.5 h-full bg-slate-300" style={{ left: '68%' }} />
                              <div className="absolute top-0 w-0.5 h-full bg-green-400" style={{ left: '85%' }} />
                            </div>

                            {/* Benchmark labels */}
                            <div className="flex items-center justify-between text-xs mb-3">
                              <span className="text-slate-400">0</span>
                              <div className="flex items-center gap-4">
                                <span className="text-slate-500">Avg: 68</span>
                                <span className="text-green-600 font-medium">Top 10%: 85+</span>
                              </div>
                              <span className="text-slate-400">100</span>
                            </div>

                            {/* Encouragement message */}
                            <div className={`text-sm font-medium p-3 rounded-lg ${
                              qualityMetrics.atsScore >= 85 ? 'bg-green-100 text-green-700' :
                              qualityMetrics.atsScore >= 75 ? 'bg-primary/10 text-primary' :
                              qualityMetrics.atsScore >= 68 ? 'bg-slate-100 text-slate-600' : 'bg-amber-50 text-amber-700'
                            }`}>
                              {qualityMetrics.atsScore >= 85 ? '🎯 Excellent match! You\'re in the top 10%' :
                               qualityMetrics.atsScore >= 75 ? '💪 Strong match — looking good!' :
                               qualityMetrics.atsScore >= 68 ? '👍 Above average — solid foundation' :
                               '💡 Tip: Try adding more keywords from the job posting'}
                            </div>
                          </div>

                          {/* Quality Score - Simplified */}
                          <div className="bg-slate-50 rounded-xl p-4">
                            <div className="flex items-center justify-between">
                              <span className="text-sm font-medium text-slate-600">Content Quality</span>
                              <span className="font-heading text-xl font-bold text-slate-700">
                                {qualityMetrics.qualityScore}/100
                              </span>
                            </div>
                            <p className="text-xs text-slate-500 mt-1">
                              Measures clarity, impact, and professional tone
                            </p>
                          </div>
                        </div>
                      )}

                      {/* What Changed */}
                      {changesSummary ? (
                        <div>
                          <div className="flex items-center gap-2 mb-3">
                            <Sparkles size={16} className="text-primary" />
                            <span className="font-medium text-slate-900">What We Optimized</span>
                          </div>
                          <div className="text-sm text-slate-700 space-y-2 bg-slate-50 rounded-xl p-4" dangerouslySetInnerHTML={{ __html: renderMarkdown(changesSummary) }} />
                        </div>
                      ) : (
                        <p className="text-sm text-slate-500 text-center py-8">No changes summary available</p>
                      )}

                      {/* Strengths */}
                      {qualityMetrics?.strengths?.length > 0 && (
                        <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 mt-4">
                          <div className="flex items-center gap-2 mb-2">
                            <Check size={14} className="text-slate-600" />
                            <span className="text-sm font-medium text-slate-900">Resume Strengths</span>
                          </div>
                          <div className="text-sm text-slate-600">{qualityMetrics.strengths.join(' • ')}</div>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Ask Claude Tab */}
                  {reviewTab === 'questions' && (
                    <div className="space-y-4">
                      <p className="text-sm text-slate-600 pb-3 border-b border-slate-100">
                        Paste application questions to generate tailored responses.
                        <span className="inline-flex items-center gap-1 ml-2 text-xs text-slate-400 font-medium">Uses 1 credit</span>
                      </p>
                      {appQuestions.map((q, index) => (
                        <div key={index} className="bg-slate-50 rounded-xl p-4">
                          <div className="flex gap-3 mb-3">
                            <textarea
                              value={q.question}
                              onChange={(e) => updateAppQuestion(index, 'question', e.target.value)}
                              placeholder="Paste the application question..."
                              className="flex-1 px-3 py-2 bg-white border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-primary focus:ring-1 focus:ring-primary"
                              rows={2}
                            />
                            {appQuestions.length > 1 && (
                              <button onClick={() => removeAppQuestion(index)} className="p-2 text-slate-400 hover:text-red-500 cursor-pointer">
                                <Trash2 size={16} />
                              </button>
                            )}
                          </div>
                          <div className="flex items-center gap-3">
                            <div className="flex items-center gap-2 text-xs text-slate-500">
                              <span>Limit:</span>
                              <input
                                type="number"
                                value={q.charLimit}
                                onChange={(e) => updateAppQuestion(index, 'charLimit', parseInt(e.target.value) || 500)}
                                className="w-16 px-2 py-1 border border-slate-200 rounded text-xs"
                              />
                            </div>
                            <button
                              onClick={() => generateAppQuestionResponse(index)}
                              disabled={isGeneratingAppResponse || !q.question.trim()}
                              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-medium transition-colors cursor-pointer ${
                                isGeneratingAppResponse || !q.question.trim()
                                  ? 'bg-slate-200 text-slate-400 cursor-not-allowed'
                                  : 'bg-primary text-white hover:bg-dark'
                              }`}
                            >
                              {isGeneratingAppResponse && generatingQuestionIndex === index ? (
                                <><Loader2 size={14} className="animate-spin" /> Generating...</>
                              ) : (
                                <><Sparkles size={14} /> Generate</>
                              )}
                            </button>
                          </div>
                          {q.response && (
                            <div className="mt-3 pt-3 border-t border-slate-200">
                              <div className="flex items-center justify-between mb-2">
                                <span className="text-xs font-medium text-slate-600">Response</span>
                                <div className="flex items-center gap-2">
                                  <span className={`text-xs ${q.response.length > q.charLimit ? 'text-red-600' : 'text-slate-400'}`}>
                                    {q.response.length}/{q.charLimit}
                                  </span>
                                  <button onClick={() => copyToClipboard(q.response, 'qa')} className="text-xs text-primary cursor-pointer">Copy</button>
                                </div>
                              </div>
                              <textarea
                                value={q.response}
                                onChange={(e) => updateAppQuestion(index, 'response', e.target.value)}
                                className={`w-full px-3 py-2 bg-white border rounded-lg text-sm resize-none focus:outline-none ${
                                  q.response.length > q.charLimit ? 'border-red-300 bg-red-50' : 'border-slate-200'
                                }`}
                                rows={4}
                              />
                            </div>
                          )}
                        </div>
                      ))}
                      <button
                        onClick={() => addAppQuestion()}
                        className="w-full py-2.5 border-2 border-dashed border-slate-200 rounded-xl text-sm text-slate-500 hover:border-primary hover:text-primary transition-colors cursor-pointer"
                      >
                        + Add question
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Start New - Subtle at bottom */}
              <button
                onClick={() => { resetTailorForm(); setView('tailor'); }}
                className="w-full flex items-center justify-center gap-2 py-3 text-primary hover:text-dark font-medium transition-colors cursor-pointer"
              >
                <Sparkles size={16} />
                Tailor Another Resume
              </button>
            </div>
          )}

          {/* Library View - Modern */}
          {view === 'library' && (
            <LibraryView
              resumeLibrary={resumeLibrary}
              initialFilter={filterStatus}
              onStatusChange={async (id, status, appliedDate) => {
                // Update React state immediately for instant UI feedback
                const app = resumeLibrary.find(item => item.id === id);
                const prevStatus = app?.status;
                const historyEntry = prevStatus && prevStatus !== status ? {
                  fromStatus: prevStatus,
                  toStatus: status,
                  source: 'user',
                  changedAt: new Date().toISOString()
                } : null;
                const updatedLibrary = resumeLibrary.map(item =>
                  item.id === id
                    ? {
                        ...item,
                        status,
                        ...(appliedDate && { appliedDate }),
                        statusHistory: historyEntry
                          ? [...(item.statusHistory || []), historyEntry]
                          : (item.statusHistory || [])
                      }
                    : item
                );
                setResumeLibrary(updatedLibrary);

                // Show celebration when marking as applied
                if (status === 'applied' && prevStatus !== 'applied') {
                  const appliedCount = updatedLibrary.filter(a => a.status === 'applied').length;
                  setShowAppliedCelebration({
                    company: app?.company || 'this role',
                    appliedCount
                  });
                }

                // Track analytics
                if (window.posthog) {
                  posthog.capture('application_status_updated', {
                    from_status: prevStatus,
                    to_status: status
                  });
                }

                // Save to database if authenticated
                if (isAuthenticated && authToken) {
                  try {
                    const response = await fetch(`${API_URL}/api/user/applications/${id}`, {
                      method: 'PATCH',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                      },
                      body: JSON.stringify({ status, appliedDate })
                    });
                    const result = await response.json();
                    if (!response.ok) {
                      console.error('Failed to update application:', result);
                      // Reload data to get the correct state
                      await loadUserData();
                    }
                  } catch (error) {
                    console.error('Failed to update application:', error);
                    // Reload data to get the correct state
                    await loadUserData();
                  }
                } else {
                  // Save to localStorage if not authenticated
                  localStorage.setItem(STORAGE_KEYS.RESUME_LIBRARY, JSON.stringify(updatedLibrary));
                }
              }}
              onViewResume={(application) => {
                setTailoredResume(application.resume);
                setChangesSummary(application.changesSummary || '');
                // Restore quality metrics for this application
                if (application.qualityScore || application.atsScore) {
                  setQualityMetrics({
                    qualityScore: application.qualityScore,
                    atsScore: application.atsScore,
                    beforeQualityScore: application.beforeQualityScore,
                    beforeAtsScore: application.beforeAtsScore,
                    keywordsFound: application.keywordsFound || [],
                    strengths: application.strengths || []
                  });
                } else {
                  setQualityMetrics(null);
                }
                setView('tailored');
              }}
              onViewJobDescription={(jobDesc) => {
                // Handled internally by LibraryView with modal
              }}
              authToken={authToken}
              onAddInterview={(applicationId) => {
                // Update local state to reflect interview status
                const updatedLibrary = resumeLibrary.map(item =>
                  item.id === applicationId && (item.status === 'draft' || item.status === 'applied')
                    ? { ...item, status: 'interview' }
                    : item
                );
                setResumeLibrary(updatedLibrary);
              }}
              onUpdateApplication={async (id, updates) => {
                // Update React state immediately for instant UI feedback
                const updatedLibrary = resumeLibrary.map(item =>
                  item.id === id ? { ...item, ...updates } : item
                );
                setResumeLibrary(updatedLibrary);

                // Save to database if authenticated
                if (isAuthenticated && authToken) {
                  try {
                    await fetch(`${API_URL}/api/user/applications/${id}`, {
                      method: 'PATCH',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                      },
                      body: JSON.stringify(updates)
                    });
                  } catch (error) {
                    console.error('Failed to update application:', error);
                  }
                } else {
                  // Save to localStorage if not authenticated
                  localStorage.setItem(STORAGE_KEYS.RESUME_LIBRARY, JSON.stringify(updatedLibrary));
                }
              }}
              onStartNewApplication={() => {
                if (window.posthog) posthog.capture('new_application_started', { type: 'tailor' });
                resetTailorForm();
                setFilterStatus('all');
                setView('tailor');
              }}
              onCreateManualApplication={createManualApplication}
              onArchive={async (id, archived) => {
                // Update React state immediately for instant UI feedback
                const updatedLibrary = resumeLibrary.map(item =>
                  item.id === id ? { ...item, archived } : item
                );
                setResumeLibrary(updatedLibrary);

                // Track analytics
                if (window.posthog) {
                  posthog.capture('application_archived', {
                    archived,
                    application_id: id
                  });
                }

                // Save to database if authenticated
                if (isAuthenticated && authToken) {
                  try {
                    await fetch(`${API_URL}/api/user/applications/${id}`, {
                      method: 'PATCH',
                      headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${authToken}`
                      },
                      body: JSON.stringify({ archived })
                    });
                  } catch (error) {
                    console.error('Failed to archive application:', error);
                  }
                } else {
                  // Save to localStorage if not authenticated
                  localStorage.setItem(STORAGE_KEYS.RESUME_LIBRARY, JSON.stringify(updatedLibrary));
                }
              }}
              onDownloadDocx={downloadResumeDocx}
            />
          )}

          {/* Offer Comparison View */}
          {view === 'offers' && (
            <OfferComparisonView
              authToken={authToken}
              onNavigate={(newView, filter) => {
                if (newView === 'tailor') {
                  resetTailorForm();
                }
                setView(newView);
                if (filter) {
                  setFilterStatus(filter);
                }
              }}
            />
          )}

          {/* LinkedIn Profile View */}
          {view === 'linkedin' && (
            <LinkedInProfileView
              masterResume={masterResume}
              hasMasterResume={hasMasterResume}
              formatMasterResume={formatMasterResume}
              authToken={authToken}
              onNavigate={(newView) => setView(newView)}
              onShowPaywall={() => setShowPaywall(true)}
            />
          )}

        </main>

        {/* Footer with Privacy Policy Link */}
        <footer className="mt-6 mb-4 sm:mb-0 text-center text-xs text-slate-500">
          <p>
            Your data is stored securely.{' '}
            <button
              onClick={() => setShowPrivacyModal(true)}
              className="text-primary hover:text-primary/80 underline cursor-pointer font-medium"
            >
              Privacy Policy
            </button>
          </p>
        </footer>
      </div>

      {/* Privacy Policy Modal */}
      {showPrivacyModal && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-4xl w-full max-h-[90vh] flex flex-col rounded-2xl shadow-lg overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200 bg-slate-50">
              <h3 className="font-heading text-xl font-semibold text-slate-900">Privacy Policy</h3>
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="w-10 h-10 flex items-center justify-center text-slate-400 hover:text-slate-600 hover:bg-slate-200 rounded-lg transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              <PrivacyPolicy />
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end">
              <button
                onClick={() => setShowPrivacyModal(false)}
                className="px-6 py-3 bg-primary hover:bg-dark text-white font-semibold rounded-xl transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Cookie Consent Banner */}
      <CookieConsent onOpenPrivacyPolicy={() => setShowPrivacyModal(true)} />

      {/* AI Analyzing Overlay - Shows during job description analysis */}
      {isAnalyzing && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-md w-full p-8 rounded-2xl shadow-lg text-center">
            {/* Animated Search Icon */}
            <div className="mb-6 relative">
              <div className="w-16 h-16 mx-auto rounded-xl bg-slate-100 flex items-center justify-center">
                <Eye size={28} className="text-slate-600 animate-pulse" />
              </div>
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>

            <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">
              Analyzing Job Description
            </h2>
            <p className="text-slate-600 mb-6 text-sm">
              Finding the key requirements and preparing personalized questions to strengthen your resume.
            </p>

            {/* Progress Steps */}
            <div className="space-y-3 text-left bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${analysisStep >= 2 ? 'bg-primary' : 'bg-primary animate-pulse'}`}>
                  {analysisStep >= 2 ? <Check size={14} className="text-white" /> : <Loader2 size={14} className="text-white animate-spin" />}
                </div>
                <span className={`text-sm ${analysisStep >= 2 ? 'text-slate-700' : 'text-slate-900 font-medium'}`}>Extracting job requirements</span>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${analysisStep >= 3 ? 'bg-primary' : analysisStep === 2 ? 'bg-primary animate-pulse' : 'bg-slate-200'}`}>
                  {analysisStep >= 3 ? <Check size={14} className="text-white" /> : analysisStep === 2 ? <Loader2 size={14} className="text-white animate-spin" /> : <span className="text-xs text-slate-500">2</span>}
                </div>
                <span className={`text-sm ${analysisStep >= 3 ? 'text-slate-700' : analysisStep === 2 ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>Identifying skill gaps</span>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${analysisStep === 3 ? 'bg-primary animate-pulse' : 'bg-slate-200'}`}>
                  {analysisStep === 3 ? <Loader2 size={14} className="text-white animate-spin" /> : <span className="text-xs text-slate-500">3</span>}
                </div>
                <span className={`text-sm ${analysisStep === 3 ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>Generating targeted questions</span>
              </div>
            </div>

            <p className="text-xs text-slate-500 mt-4">
              This usually takes 10-20 seconds
            </p>
          </div>
        </div>
      )}

      {/* AI Working Overlay - Shows during resume generation */}
      {isGenerating && !showSuccessCelebration && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-md w-full p-8 rounded-2xl shadow-lg text-center">
            {/* Animated AI Icon */}
            <div className="mb-6 relative">
              <div className="w-16 h-16 mx-auto rounded-xl bg-slate-100 flex items-center justify-center animate-pulse">
                <Sparkles size={28} className="text-slate-600" />
              </div>
              <div className="absolute -bottom-1 left-1/2 -translate-x-1/2 flex gap-1">
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>

            <h2 className="font-heading text-xl font-bold text-slate-900 mb-2">
              Tailoring Your Resume
            </h2>
            <p className="text-slate-600 mb-6 text-sm">
              Our AI is analyzing the job description and customizing your resume for the best match.
            </p>

            {/* Progress Steps */}
            <div className="space-y-3 text-left bg-slate-50 rounded-xl p-4">
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationStep >= 2 ? 'bg-primary' : 'bg-primary animate-pulse'}`}>
                  {generationStep >= 2 ? <Check size={14} className="text-white" /> : <Loader2 size={14} className="text-white animate-spin" />}
                </div>
                <span className={`text-sm ${generationStep >= 2 ? 'text-slate-700' : 'text-slate-900 font-medium'}`}>Analyzing job requirements</span>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationStep >= 3 ? 'bg-primary' : generationStep === 2 ? 'bg-primary animate-pulse' : 'bg-slate-200'}`}>
                  {generationStep >= 3 ? <Check size={14} className="text-white" /> : generationStep === 2 ? <Loader2 size={14} className="text-white animate-spin" /> : <span className="text-xs text-slate-500">2</span>}
                </div>
                <span className={`text-sm ${generationStep >= 3 ? 'text-slate-700' : generationStep === 2 ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>Matching your skills & experience</span>
              </div>
              <div className="flex items-center gap-3">
                <div className={`w-6 h-6 rounded-full flex items-center justify-center ${generationStep === 3 ? 'bg-primary animate-pulse' : 'bg-slate-200'}`}>
                  {generationStep === 3 ? <Loader2 size={14} className="text-white animate-spin" /> : <span className="text-xs text-slate-500">3</span>}
                </div>
                <span className={`text-sm ${generationStep === 3 ? 'text-slate-900 font-medium' : 'text-slate-400'}`}>Generating tailored content</span>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mt-4">
              <div className="flex items-center justify-between mb-1.5">
                <span className="text-xs text-slate-500">Progress</span>
                <span className="text-xs font-medium text-primary">{Math.round(generationProgress)}%</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div
                  className="h-full bg-slate-900 rounded-full transition-all duration-500 ease-out"
                  style={{ width: `${generationProgress}%` }}
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Success Screen - Conversion Focused */}
      {showSuccessCelebration && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-lg w-full rounded-2xl shadow-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-slate-50 px-6 py-5 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center">
                  <CheckCircle2 size={24} className="text-white" />
                </div>
                <div>
                  <h2 className="font-heading text-xl font-bold text-slate-900">
                    Resume Ready!
                  </h2>
                  <p className="text-sm text-slate-500">
                    {extractedCompany ? `Tailored for ${extractedCompany}` : 'Your tailored resume is ready'}
                  </p>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 space-y-3">
              {/* Primary CTA - swaps after download */}
              {!hasDownloaded ? (
                <button
                  onClick={() => {
                    downloadResumeDocx(tailoredResume, `Tailored_Resume_${extractedCompany || 'Job'}.docx`);
                    setHasDownloaded(true);
                    if (window.posthog) posthog.capture('success_modal_download', { company: extractedCompany });
                  }}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-xl hover:bg-dark transition-colors cursor-pointer"
                >
                  <Download size={18} />
                  Download Resume
                </button>
              ) : (
                <button
                  onClick={async () => {
                    if (currentApplicationId) {
                      try {
                        await fetch(`${API_URL}/api/user/applications/${currentApplicationId}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                          body: JSON.stringify({ status: 'applied', appliedDate: new Date().toISOString() })
                        });
                        await loadUserData();
                        setMarkedApplied(true);
                        if (window.posthog) posthog.capture('marked_applied', { source: 'success_modal' });
                      } catch (err) { console.error(err); }
                    }
                    setShowSuccessCelebration(false);
                    setView('tailored');
                  }}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-primary text-white font-medium rounded-xl hover:bg-dark transition-colors cursor-pointer"
                >
                  <Check size={18} />
                  I Applied - Track It
                </button>
              )}

              {/* Answer Application Questions */}
              <button
                onClick={() => {
                  setShowSuccessCelebration(false);
                  setView('tailored');
                  setReviewTab('questions');
                  if (window.posthog) posthog.capture('success_modal_answer_questions', { company: extractedCompany });
                }}
                className="w-full flex items-center justify-center gap-2 px-6 py-3 bg-slate-100 text-slate-700 font-medium rounded-xl hover:bg-slate-200 transition-colors cursor-pointer"
              >
                <MessageSquare size={18} />
                Answer Application Questions
              </button>

              {/* Secondary action - opposite of primary */}
              {hasDownloaded ? (
                <button
                  onClick={() => {
                    downloadResumeDocx(tailoredResume, `Tailored_Resume_${extractedCompany || 'Job'}.docx`);
                    if (window.posthog) posthog.capture('success_modal_download_again', { company: extractedCompany });
                  }}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  <Download size={18} />
                  Download Again
                </button>
              ) : (
                <button
                  onClick={async () => {
                    if (currentApplicationId) {
                      try {
                        await fetch(`${API_URL}/api/user/applications/${currentApplicationId}`, {
                          method: 'PATCH',
                          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
                          body: JSON.stringify({ status: 'applied', appliedDate: new Date().toISOString() })
                        });
                        await loadUserData();
                        setMarkedApplied(true);
                        if (window.posthog) posthog.capture('marked_applied', { source: 'success_modal' });
                      } catch (err) { console.error(err); }
                    }
                    setShowSuccessCelebration(false);
                    setView('tailored');
                  }}
                  className="w-full flex items-center justify-center gap-2 px-6 py-3 border border-slate-200 text-slate-700 font-medium rounded-xl hover:bg-slate-50 transition-colors cursor-pointer"
                >
                  <Check size={18} />
                  I Applied - Track It
                </button>
              )}

              {/* Tertiary actions row */}
              <div className="flex items-center justify-center gap-4 pt-2">
                <button
                  onClick={() => {
                    setShowSuccessCelebration(false);
                    setView('tailored');
                    setReviewTab('changes');
                  }}
                  className="text-sm text-slate-500 hover:text-slate-700 cursor-pointer"
                >
                  See what changed
                </button>
                <span className="text-slate-300">·</span>
                <button
                  onClick={() => {
                    setShowSuccessCelebration(false);
                    setView('dashboard');
                  }}
                  className="text-sm text-slate-500 hover:text-slate-700 cursor-pointer"
                >
                  Go to Dashboard
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Applied Celebration Modal */}
      {showAppliedCelebration && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={() => setShowAppliedCelebration(null)}>
          <div className="bg-white max-w-md w-full rounded-xl shadow-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Header */}
            <div className="bg-slate-50 px-6 py-6 text-center border-b border-slate-200">
              <div className="w-14 h-14 rounded-full bg-green-500 mx-auto flex items-center justify-center mb-3">
                <CheckCircle2 size={24} className="text-white" />
              </div>
              <h2 className="font-heading text-xl font-semibold text-slate-900">
                Application Sent!
              </h2>
              <p className="text-sm text-slate-500 mt-1">
                {showAppliedCelebration.company} - nice work!
              </p>
            </div>

            {/* Stats */}
            <div className="px-6 py-4 border-b border-slate-100">
              <div className="flex items-center justify-center gap-2 text-slate-700">
                <span className="w-2 h-2 rounded-full bg-green-500" aria-hidden="true" />
                <span className="text-sm font-medium">{showAppliedCelebration.appliedCount} application{showAppliedCelebration.appliedCount !== 1 ? 's' : ''} submitted</span>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 space-y-3">
              {/* Primary: Go to Dashboard - the natural end of the flow */}
              <button
                onClick={() => {
                  setShowAppliedCelebration(null);
                  setView('dashboard');
                  if (window.posthog) posthog.capture('applied_celebration_view_dashboard');
                }}
                className="btn-primary btn-full btn-with-icon"
              >
                <Briefcase size={18} aria-hidden="true" />
                View Dashboard
              </button>

              {/* Secondary: Add notes while fresh */}
              <button
                onClick={() => {
                  setShowAppliedCelebration(null);
                  setView('library');
                  setFilterStatus('applied');
                  if (window.posthog) posthog.capture('applied_celebration_add_notes');
                }}
                className="btn-secondary btn-full btn-with-icon"
              >
                <MessageSquare size={18} aria-hidden="true" />
                Add Notes
              </button>

              {/* Tertiary: Tailor another */}
              <button
                onClick={() => {
                  setShowAppliedCelebration(null);
                  setView('tailor');
                  setWizardStep(1);
                  setJobDescription('');
                  setTailoredResume('');
                  setExtractedCompany('');
                  setExtractedLocation('');
                  setAnswers({});
                  if (window.posthog) posthog.capture('applied_celebration_tailor_another');
                }}
                className="w-full text-sm text-slate-500 hover:text-slate-700 font-medium py-2 cursor-pointer transition-colors"
              >
                Tailor another →
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Job Description Modal */}
      {showJobDescModal && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-4xl w-full max-h-[80vh] flex flex-col rounded-2xl shadow-lg overflow-hidden">
            <div className="flex justify-between items-center px-6 py-4 border-b border-slate-200">
              <h3 className="font-heading text-xl font-semibold text-slate-900">Job Description</h3>
              <button
                onClick={() => setShowJobDescModal(false)}
                className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-xl transition-colors cursor-pointer"
              >
                <X size={20} />
              </button>
            </div>
            <div className="overflow-y-auto p-6 flex-1">
              <pre className="whitespace-pre-wrap font-sans text-sm text-slate-700 leading-relaxed">
                {modalJobDescription}
              </pre>
            </div>
            <div className="px-6 py-4 border-t border-slate-200 bg-slate-50 flex justify-end">
              <button
                onClick={() => setShowJobDescModal(false)}
                className="px-6 py-2 bg-primary text-white font-medium text-sm rounded-xl hover:bg-dark transition-colors cursor-pointer"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Resume Optimization Success Modal */}
      {optimizeResultModal.show && (
        <div className="fixed inset-0 bg-slate-900/80 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white max-w-lg w-full rounded-2xl shadow-lg overflow-hidden">
            <div className="p-6 text-center border-b border-slate-100">
              <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-green-500 flex items-center justify-center">
                <Check size={32} className="text-white" />
              </div>
              <h3 className="font-heading text-xl font-semibold text-slate-900 mb-2">Resume Enhanced!</h3>
              <p className="text-slate-600">Your master resume has been optimized with insights from your application history.</p>
            </div>
            <div className="p-6 bg-slate-50">
              <h4 className="font-medium text-slate-900 mb-3">Enhancements Made:</h4>
              <div className="text-sm text-slate-700 space-y-1 max-h-48 overflow-y-auto">
                {optimizeResultModal.enhancements.split('\n').map((line, i) => (
                  <p key={i} className={line.startsWith('•') || line.startsWith('-') ? 'pl-2' : ''}>
                    {line}
                  </p>
                ))}
              </div>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end">
              <button
                onClick={() => setOptimizeResultModal({ show: false, enhancements: '' })}
                className="px-6 py-2.5 bg-primary hover:bg-dark text-white font-semibold rounded-xl transition-colors cursor-pointer"
              >
                Got it!
              </button>
            </div>
          </div>
        </div>
      )}

      <PaywallModal
        open={showPaywall}
        onClose={() => {
          setShowPaywall(false);
          setPaymentError('');
          if (window.posthog) {
            posthog.capture('paywall_dismissed');
          }
        }}
        onPay={handleStripeCheckout}
        isPaying={isPaying}
        creditsUsed={creditsUsed}
        freeLimit={freeLimit}
        paidCredits={paidCredits}
        paidLimit={paidLimit}
        priceDisplay={priceDisplay}
        error={paymentError}
      />

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes scale-in {
          from {
            transform: scale(0.8);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }
        .animate-scale-in {
          animation: scale-in 0.3s ease-out;
        }
      `}</style>
    </div>
  );
}

export default App;
